import type {
  StepUpChallenge,
  StepUpResult,
  TotpEnrolmentResult,
  TotpSetup,
  TwoFactorStatus,
} from '@/types/twoFactor'
import type {
  AgencyBrandingResponse,
  AgencyBrandingUpdate,
} from '@/types/agencyBranding'
import type {
  AgencyComparablesFilters,
  AgencyComparablesResponse,
  BulkPriceActiveAdjustment,
  BulkPricePreview,
  BulkPriceStrategy,
  AgencyPricingPortfolio,
  AgentPriceReport,
  AgentPricingPortfolio,
  PricingAnalysis,
  PricingDecision,
  PricingRecalculationJob,
  PricingTrendSnapshot,
  SubjectAnalysis,
  SubjectAnalysisRequest,
} from '@/types/marketPricing'
import type { InboxNotificationsResponse } from '@/types/inboxNotifications'
import type {
  AutoresponseTemplate,
  AutoresponseTemplateInput,
  AutoresponseTemplatePatch,
  CalendarEvent,
  CalendarEventInput,
  CalendarEventListParams,
  CalendarEventPatch,
  CalendarSettings,
  CalendarSettingsInput,
  CalendarTeamEventsResponse,
} from '@/api/calendar'
import type { AgencySettingsOverview } from '@/types/agencySettings'
import type { NotificationEventRow, NotificationPreferenceRow } from '@/types/subscriptionNotifications'
import type { Territory } from '@/types/territory'
import type {
  OwnershipTransferMutationResult,
  OwnershipTransferOtpResult,
  OwnershipTransferStateResponse,
} from '@/types/ownershipTransfer'
import type {
  AgencyPublicProfileSettingsResponse,
  AgencyPublicProfileUpdate,
} from '@/types/agencyPublicProfile'

export interface AgencyDeletionImpact {
  active_members: number
  listings_count: number
  credits_balance_usd: number
  past_due_invoices: boolean
}

export interface AgencyDeletionStateResponse {
  agency: { id: string; name: string }
  impact: AgencyDeletionImpact
  blocks: { active_members: boolean; past_due: boolean }
  deletion: { id: string; status: string; scheduled_for: string; reason: string } | null
  can_schedule: boolean
}

export interface AgencyDeletionScheduleResponse {
  id: string
  status: string
  scheduled_for: string
  impact: AgencyDeletionImpact
}

/** H5 — audit log entry as returned by /api/audit/log. */
export interface AuditLogEntry {
  id: string
  agent_id: string | null
  agency_id: string | null
  type: string
  action: string | null
  entity_type: string | null
  entity_id: string | null
  ip: string | null
  user_agent: string | null
  metadata: unknown
  created_at: string
}

export interface AuditLogSearchResponse {
  entries: AuditLogEntry[]
  pagination: { limit: number; offset: number; total: number }
}

export interface AuditLogSearchFilters {
  agency_id?: string
  actor_id?: string
  type?: string
  entity_type?: string
  entity_id?: string
  from?: string
  to?: string
  q?: string
  limit?: number
  offset?: number
}

// PA-AUD-002 — Audit-log retention policy.
export interface AuditRetentionPolicy {
  financial_actions_days: number
  pa_actions_days: number
  tenant_actions_days: number
  system_events_days: number
  export_before_purge: boolean
  updated_by: string | null
  updated_at: string | null
}

export interface AuditRetentionConstraints {
  financial_floor_days: number
  min_category_days: number
  max_category_days: number
}

export interface AuditRetentionPolicyResponse {
  policy: AuditRetentionPolicy
  constraints: AuditRetentionConstraints
}

export interface AuditRetentionPolicyInput {
  financial_actions_days: number
  pa_actions_days: number
  tenant_actions_days: number
  system_events_days: number
  export_before_purge: boolean
}

export interface AgencyWalletAllocationSlice {
  key: string
  label: string
  credits: number
  kind: 'agent' | 'pool'
}

export interface AgencyWalletTransaction {
  id: string
  type: string
  amount: number
  description?: string | null
  created_at: string
}

export interface AgencyWalletOverview {
  agency_id: string
  balance: {
    credits_remaining: number
    credits_reserved: number
    currency: string
    hard_block: boolean
  }
  kpis: {
    wallet_balance: number
    mtd_spend: number
    burn_rate_daily: number
    days_until_exhausted: number | null
    allocated_to_agents: number
    total_available: number
  }
  allocation: {
    slices: AgencyWalletAllocationSlice[]
    allocated_total: number
    unallocated_pool: number
  }
  transactions: AgencyWalletTransaction[]
  settings: {
    agency_id: string
    low_balance_alert_threshold: number
    updated_by: string | null
    updated_at: string | null
    is_default?: boolean
  }
  alerts: {
    is_low_balance: boolean
    threshold: number
  }
  permissions: {
    can_manage_settings: boolean
    can_top_up: boolean
    can_allocate: boolean
  }
}

export interface AgencyWalletSettings {
  agency_id: string
  low_balance_alert_threshold: number
  updated_by: string | null
  updated_at: string | null
  is_default?: boolean
}

export type WhatsAppIntakeAnalyticsRange = '7d' | '30d' | '90d'

export interface AgencyWhiteLabelAnalyticsKpis {
  visitors: number
  inquiries: number
  conversions: number
  conversion_rate: number
  bazaar_referral_share: number
  pageviews: number
}

export interface AgencyWhiteLabelAnalyticsRow {
  key: string
  value: number
}

export interface AgencyWhiteLabelAnalyticsListing {
  property_id: string
  title: string
  views: number
}

export interface AgencyWhiteLabelAnalyticsTrendPoint {
  date: string
  pageviews: number
  inquiries: number
  visitors: number
}

export interface AgencyWhiteLabelAnalyticsResponse {
  agency_id: string
  start_date: string
  end_date: string
  kpis: AgencyWhiteLabelAnalyticsKpis
  top_pages: AgencyWhiteLabelAnalyticsRow[]
  traffic_sources: AgencyWhiteLabelAnalyticsRow[]
  devices: AgencyWhiteLabelAnalyticsRow[]
  top_listings: AgencyWhiteLabelAnalyticsListing[]
  trend: AgencyWhiteLabelAnalyticsTrendPoint[]
  total_events: number
}

export interface WhatsAppIntakeAnalytics {
  range: {
    key: WhatsAppIntakeAnalyticsRange
    days: number
    from: string
    to: string
  }
  summary: {
    total_drafts: number
    approved: number
    approval_rate: number
    avg_approval_minutes: number | null
    ai_cost_estimate_usd: number
  }
  activity: Array<{
    date: string
    drafts: number
    approved: number
  }>
  field_accuracy: Array<{
    field: string
    label: string
    accuracy: number
    sample_size: number
    corrected_count: number
  }>
  field_accuracy_basis: 'accepted_without_correction'
  quota: {
    used?: number
    max?: number
    remaining?: number
    allowed?: boolean
  } | null
  total_drafts: number
  published: number
  discarded: number
  awaiting_approval: number
  approval_rate: number
}

export type PersonalChannelPlatform =
  | 'facebook'
  | 'instagram'
  | 'linkedin'
  | 'tiktok'
  | 'x'
  | 'whatsapp'

export interface PersonalChannelConnection {
  id: string
  platform: PersonalChannelPlatform
  account_name: string
  handle: string | null
  is_primary: boolean
  status: string
  health: string | null
  created_at: string | null
  updated_at: string | null
}

export interface PersonalChannelConnectionInput {
  platform: PersonalChannelPlatform
  account_name: string
  handle: string
  is_primary?: boolean
}

export type CanonicalPropertyState = 'primary' | 'secondary' | 'disputed'

export interface CanonicalPrimaryDispute {
  id: string
  canonical_id: string
  listing_id: string
  mandate_type: 'exclusive'
  mandate_reference: string
  evidence_notes: string | null
  status: 'pending' | 'approved' | 'rejected' | 'withdrawn'
  created_at: string
  updated_at: string
}

export interface CanonicalSiblingListing {
  id: string
  title: string
  price: number | null
  currency: string
  status: string
  agency: { id: string; name: string } | null
  agent: { id: string; name: string }
  listed_at: string | null
  updated_at: string | null
  is_primary: boolean
  is_mine: boolean
}

export interface CanonicalPropertyView {
  canonical: {
    id: string
    location: string | null
    city: string | null
    neighborhood: string | null
    primary_listing_id: string
    sibling_count: number
  }
  state: CanonicalPropertyState
  can_contest: boolean
  dispute: CanonicalPrimaryDispute | null
  listings: CanonicalSiblingListing[]
}

export interface CanonicalPrimaryDisputeInput {
  mandate_type: 'exclusive'
  mandate_reference: string
  evidence_notes: string
}

/** Issue 192b — self-serve data-export job shape. */
export type ScheduledPublicationStatus =
  | 'pending'
  | 'processing'
  | 'published'
  | 'cancelled'
  | 'failed'

export interface ScheduledPublication {
  id: string
  property_id: string
  agent_id: string
  portals: Array<string | { code: string; country_code?: string }>
  message: string | null
  scheduled_at: string
  timezone: string | null
  recurrence: 'none' | 'weekly'
  status: ScheduledPublicationStatus
  job_id: string | null
  last_error: string | null
  last_fired_at: string | null
  created_at: string
  updated_at: string
}

export interface ScheduledPublicationInput {
  portals: Array<string | { code: string; country_code?: string }>
  message?: string | null
  scheduled_at: string
  timezone?: string | null
  recurrence?: 'none' | 'weekly'
}

/** AGT-INB-004 — a teammate a conversation can be assigned to. */
export interface AssignableAgent {
  id: string
  name: string | null
  email: string | null
  role: string | null
  is_self: boolean
}

export type PropertyDisposition = 'agency_retains' | 'agent_retains' | 'archive'
export type PropertyDispositionStatus =
  | 'pending'
  | 'agreed'
  | 'disputed'
  | 'completed'
  | 'cancelled'

export interface PropertyDispositionResponse {
  case: {
    id: string
    property_id: string
    proposed_disposition: PropertyDisposition
    agency_proposed_disposition: PropertyDisposition | null
    agent_proposed_disposition: PropertyDisposition | null
    agency_notes: string | null
    agent_notes: string | null
    status: PropertyDispositionStatus
    initiated_by: string
    resolved_by: string | null
    resolution_notes: string | null
    created_at: string
    updated_at: string
    resolved_at: string | null
  }
  property: {
    id: string
    title: string
    reference: string | null
    status: string
    price: number | null
    price_unit: string | null
    city: string | null
    neighborhood: string | null
    photo: string | null
  }
  parties: { agency: string; agent: string }
  viewer_role: 'agent' | 'agency'
  can_resolve: boolean
}

export type ReviewFlagReason =
  | 'spam'
  | 'abusive'
  | 'privacy'
  | 'conflict'
  | 'false_claim'
  | 'other'

export interface ManagedAgentReview {
  id: string
  rating: number
  title: string | null
  comment: string
  reviewer: { name: string }
  verified_transaction: boolean
  status: string
  response: string | null
  responded_at: string | null
  flag: {
    status: 'none' | 'pending' | 'resolved' | 'dismissed'
    reason: ReviewFlagReason | null
    details: string | null
    flagged_at: string | null
  }
  created_at: string
  updated_at: string
}

/** SHR-ERR-002 — who/what a permission-denied "Request access" targets. */
export type AccessRequestScope = 'platform' | 'agency' | 'resource'

/** SHR-ERR-002 — a recorded request for access to a restricted area. */
export interface AccessRequest {
  id: string
  requester_id: string
  requester_name: string | null
  scope: AccessRequestScope
  agency_id: string | null
  resource_type: string | null
  resource_id: string | null
  area_label: string | null
  reason: string | null
  status: 'open' | 'granted' | 'denied' | 'dismissed'
  created_at: string
  updated_at: string
}

/** SHR-ERR-005 — maintenance / degraded severity (also the overall status). */
export type PlatformStatusLevel = 'info' | 'degraded' | 'maintenance'

/** SHR-ERR-005 — one platform-wide status notice shown in the global banner. */
export interface PlatformStatusNotice {
  id: string
  status: PlatformStatusLevel
  title: string
  body: string | null
  learn_more_url: string | null
  active: boolean
  starts_at: string | null
  ends_at: string | null
  created_at: string
  updated_at: string
}

export interface ManagedAgentReviewsResponse {
  summary: {
    total: number
    average: number
    distribution: Record<1 | 2 | 3 | 4 | 5, number>
    awaiting_response: number
    flagged: number
  }
  reviews: ManagedAgentReview[]
}
/** AGT-WLA-004 — WhatsApp intake agent preferences. */
export type WhatsAppIntakeNotificationCadence = 'immediately' | 'hourly' | 'daily'

export interface WhatsAppIntakeAgentSettings {
  intake_enabled: boolean
  notification_cadence: WhatsAppIntakeNotificationCadence
  auto_approve_high_confidence: boolean
  ai_provider_preference: string
  default_template_variant: string
  auto_publish_social: boolean
  ai_providers_allowed: string[]
  thumbnail_variants_allowed: string[]
}

export interface WhatsAppIntakeAgentSettingsPatch {
  whatsapp_intake_enabled?: boolean
  whatsapp_intake_notification_cadence?: WhatsAppIntakeNotificationCadence
  whatsapp_intake_auto_approve_high_confidence?: boolean
  whatsapp_listings_ai_provider?: string
  whatsapp_listings_template_variant?: string
  whatsapp_listings_auto_publish_social?: boolean
}

/** PA-ARE-002 — platform-admin area profile row. */
export interface AdminArea {
  id: string
  name: string
  name_ar?: string | null
  slug: string
  level: string
  parent_id?: string | null
  center_latitude: number
  center_longitude: number
  boundary_geojson?: string | null
  proximity_radii_json?: string | null
  summary?: string | null
  summary_ar?: string | null
  lifestyle_profile?: string | null
  investment_outlook?: string | null
  activity_score?: number | null
  activity_trend?: string | null
  family_profile_skew?: string | null
  estimated_population_density?: string | null
  status: string
  published_at?: string | null
  last_google_signals_refresh_at?: string | null
  created_at?: string
  updated_at?: string
}

/** AGT-TSK-003 — appointment reminder policy. */
export type ReminderPolicyChannel = 'email' | 'whatsapp' | 'inapp'
export type ReminderAppointmentType = 'viewing' | 'call' | 'booking' | 'meeting'

export interface ReminderPolicyRule {
  offset_minutes: number
  channels: ReminderPolicyChannel[]
  message_template?: string
  active?: boolean
}

export interface ReminderPolicy {
  id: string
  name: string
  owner_type: 'agent' | 'agency'
  owner_id: string
  appointment_type: ReminderAppointmentType
  rules: ReminderPolicyRule[]
  is_default?: boolean
  created_at?: string
  updated_at?: string
}

export interface AdminAreaSource {
  id: string
  area_id: string
  source_type_id: string
  name?: string | null
  handle?: string | null
  url?: string | null
  api_endpoint?: string | null
  feed_url?: string | null
  is_monitored?: boolean
  last_fetched_at?: string | null
}

export interface AdminSourceType {
  id: string
  name: string
  slug: string
  archetype: string
  input_method: string
  is_active: boolean
}

export interface AdminAreaDetailResponse {
  area: AdminArea
  sources: AdminAreaSource[]
  google_budget: {
    monthly_spend_usd: number
    budget_usd_monthly: number
    quota_exceeded: boolean
  }
}

export interface ReminderPolicyInput {
  name: string
  owner_type?: 'agent' | 'agency'
  owner_id?: string
  appointment_type: ReminderAppointmentType
  rules: ReminderPolicyRule[]
  is_default?: boolean
}

/** SHR-ERR-002 — payload for POST /api/access-requests. */
export interface AccessRequestInput {
  scope?: AccessRequestScope
  agency_id?: string | null
  resource_type?: string | null
  resource_id?: string | null
  area_label?: string | null
  reason?: string | null
}

/** SHR-ERR-002 — receipt returned when filing an access request. */
export interface AccessRequestResult {
  request: AccessRequest
  /** True when an identical open request already existed (idempotent re-file). */
  already_requested: boolean
}

/** SHR-INT-002 — one lead-source row in the source-performance breakdown. */
export interface SourcePerformanceRow {
  source: string
  conversations: number
  inquiries: number
  deals: number
  won: number
  won_value: number
  lead_share: number
}

/** SHR-INT-002 — lead-source performance (GET /api/analytics/source-performance). */
export interface SourcePerformanceResponse {
  generated_at: string
  scope: { agent_id: string | null; agency_id: string | null; start_date: string | null; end_date: string | null }
  totals: { conversations: number; inquiries: number; deals: number; won: number; won_value: number }
  sources: SourcePerformanceRow[]
  /** The Bazaar row, always present (zeros when there's no syndication yet). */
  bazaar: SourcePerformanceRow
}

/** SHR-ERR-005 — response of the public GET /api/status poll. */
export interface PlatformStatusResponse {
  /** Highest severity among the live notices, or `ok` when there are none. */
  status: 'ok' | PlatformStatusLevel
  notices: PlatformStatusNotice[]
}

/** SHR-ERR-005 — platform-admin create/patch payload for a status notice. */
export interface PlatformStatusNoticeInput {
  status?: PlatformStatusLevel
  title?: string
  body?: string | null
  learn_more_url?: string | null
  active?: boolean
  starts_at?: string | null
  ends_at?: string | null
}

export interface DataExportRecord {
  id: string
  status: 'pending' | 'running' | 'complete' | 'failed'
  bytes: number | null
  sha256: string | null
  error: string | null
  requested_at: string
  started_at: string | null
  completed_at: string | null
  expires_at: string
}

/** Issue 189 — WebAuthn / passkey credential shape returned by /credentials. */
export interface PasskeyCredential {
  id: string
  credential_id: string
  name: string
  device_type: string | null
  backup_eligible: boolean
  backup_state: boolean
  transports: string[]
  last_used_at: string | null
  created_at: string
  revoked_at: string | null
}

/** Issue 190 — per-agency 2FA policy shape (matches backend agency_mfa_policy row + is_default flag). */
/** H1 conditional rule union — extend as new kinds land in mfa-policy-conditional.js. */
export type AgencyMfaConditionalRule =
  | { kind: 'unusual_ip' }
  | { kind: 'new_device' }
  | { kind: 'impossible_geo_hop' }

/** AGN-ROU-002 — lead routing rule condition row. */
export interface RoutingRuleCondition {
  field: 'source' | 'area' | 'property_type' | 'language' | 'time_of_day'
  op: 'eq' | 'contains' | 'gte' | 'lte'
  value: string
}

/** AGN-ROU-002 — lead routing rule filters block. */
export interface RoutingRuleFilters {
  match: 'all' | 'any'
  conditions: RoutingRuleCondition[]
}

/** AGN-ROU-002 — lead routing rule target assignment. */
export interface RoutingRuleTarget {
  strategy: 'round_robin' | 'first_response' | 'least_loaded' | 'weighted' | 'manual'
  assign_to_agent_id: string | null
  round_robin_group_id: string | null
  language: string | null
}

/** AGN-ROU-002 — agency lead routing rule (tenant_lead_routing_policies). */
export interface AgencyRoutingRule {
  id: string
  agency_id: string | null
  name: string
  priority: number
  trigger: 'inquiry' | 'comment' | 'whatsapp_message'
  enabled: boolean
  strategy: RoutingRuleTarget['strategy']
  relationship_priority: boolean
  filters: RoutingRuleFilters
  target: RoutingRuleTarget
  eligible_members: Record<string, unknown>
  strategy_config: Record<string, unknown>
  claim_timeout_seconds: number | null
  response_timeout_seconds: number | null
  max_attempts: number
  cooldown_seconds: number
  created_at: string
  updated_at: string
}

export interface AgencyRoutingRulesListResponse {
  rules: AgencyRoutingRule[]
}

/** AGN-TPL-001 — agency-scoped message template row. */
export interface AgencyMessageTemplate {
  id: string
  agency_id: string
  name: string
  channel: 'whatsapp' | 'sms' | 'email'
  category: 'greeting' | 'follow_up' | 'viewing' | 'offer' | 'general'
  subject: string | null
  body: string
  variables: string[]
  language: string
  approval_status: 'draft' | 'pending' | 'approved' | 'rejected'
  usage_count: number
  agents_using_count: number
  is_default: boolean
  created_by: string | null
  created_at: string
  updated_at: string
}

export interface AgencyMessageTemplatesListResponse {
  templates: AgencyMessageTemplate[]
}

export interface AgencyMessageTemplateRenderResult {
  body: string
  subject: string | null
  missing_variables: string[]
}

/** AGN-WLB-003 — white-label copy fields block. */
export interface AgencySiteCopyFields {
  header: { tagline: string }
  about: { paragraph: string; mission: string }
  featured_listings: {
    filter: 'all' | 'by_area' | 'by_property_type' | 'by_price_range'
    area?: string
    property_type?: string
    price_min?: number | null
    price_max?: number | null
    sort: 'newest' | 'most_viewed' | 'manual'
  }
  team: { intro: string }
  contact: { phone: string; email: string; address: string; hours: string }
  footer: { disclaimer: string }
}

/** AGN-WLB-003 — agency site configuration row. */
export interface AgencySiteConfig {
  id: string
  agency_id: string
  template_id: string | null
  logo_url: string | null
  favicon_url: string | null
  primary_color: string | null
  accent_color: string | null
  font_pair: string | null
  copy_fields: AgencySiteCopyFields
  custom_domain: string | null
  ssl_status: string
  published_at: string | null
  created_at: string
  updated_at: string
}

export interface AgencyWhiteLabelCopyResponse {
  config: AgencySiteConfig
  agency_name: string
}

/** Issue 190 + H1 — per-agency 2FA policy shape (matches backend agency_mfa_policy row + is_default flag). */
export interface AgencyMfaPolicy {
  agency_id: string
  required: boolean
  grace_days: number
  allowed_factors: string[]
  // H1 fields (migration 371) — default to empty/false on non-H1 rows.
  scoped_roles: string[]
  bypass_user_ids: string[]
  conditional_rules: AgencyMfaConditionalRule[]
  enforce_on_next_login: boolean
  updated_by: string | null
  updated_at: string | null
  created_at: string | null
  is_default: boolean
}

/** Wave 2E — ContactPolicy rules + row. */
export interface ContactPolicyRules {
  frequency_caps?: Array<{
    channel: 'email' | 'sms' | 'whatsapp'
    purpose?: 'marketing' | 'transactional' | 'nurture'
    max_sends: number
    window_hours: number
  }>
  quiet_hours?: {
    timezone?: string
    windows: Array<{ days?: number[]; start: string; end: string }>
  } | null
  do_not_contact_windows?: Array<{
    start?: string
    end?: string
    channels?: string[]
    reason?: string
  }>
  campaign_priority?: Array<Record<string, unknown>>
  negotiation_suppression?: boolean
}

export interface ContactPolicy {
  id: string
  agency_id?: string | null
  agent_id?: string | null
  scope: 'agency' | 'agent'
  name: string
  rules: ContactPolicyRules
  created_at?: string
  updated_at?: string
  data?: Record<string, unknown>
}

export interface JourneyNodeRunRow {
  id: string
  journey_run_id: string
  node_id: string
  node_type: string
  input?: Record<string, unknown>
  result?: Record<string, unknown>
  execution_id?: string | null
  occurred_at?: string
}

export interface JourneyTransitionRow {
  id: string
  journey_run_id: string
  from_node?: string | null
  to_node?: string | null
  reason?: Record<string, unknown>
  occurred_at?: string
}

export interface JourneyRunDetail {
  id: string
  contact_id: string
  status: string
  state?: Record<string, unknown>
  node_runs?: JourneyNodeRunRow[]
  transitions?: JourneyTransitionRow[]
}

/** AGN-REP-001 — agency reports home preview card. */
export interface AgencyReportsHomeCard {
  id: string
  report_id: string
  title: string
  href: string
  kpi_label: string
  kpi_value: number
  secondary_label?: string | null
  secondary_value?: number | null
  trend: number[]
}

/** AGN-REP-001 — agency reports home summary payload. */
export interface AgencyReportsHomeResponse {
  generated_at: string
  agency_id: string
  window_days: number
  cards: AgencyReportsHomeCard[]
  totals: {
    listings: number
    inquiries: number
    opportunities: number
    agents: number
    campaigns: number
  }
}

/** AGN-REP-002 — agency listings performance report row. */
export interface AgencyListingsPerformanceRow {
  id: string
  title: string
  city: string | null
  neighborhood: string | null
  property_type: string | null
  status: string
  agent_id: string | null
  agent_name: string | null
  views: number
  clicks: number
  saves: number
  inquiries: number
  viewings: number
  conversions: number
  conversion_rate: number | null
  engagement: number
}

/** AGN-REP-002 — agency listings performance report payload. */
export interface AgencyListingsPerformanceResponse {
  generated_at: string
  agency_id: string
  filters: {
    start_date: string | null
    end_date: string | null
    agent_id: string | null
    area: string | null
    property_type: string | null
  }
  overview: {
    listings: number
    active_listings: number
    total_views: number
    total_saves: number
    total_inquiries: number
    total_viewings: number
    total_conversions: number
    conversion_rate: number | null
  }
  rows: AgencyListingsPerformanceRow[]
  by_channel: Array<{ label: string; value: number }>
  by_device: Array<{ label: string; value: number }>
  top_listings: Array<{ id: string; title: string; views: number; inquiries: number }>
  filter_options: {
    agents: Array<{ id: string; name: string }>
    areas: string[]
    property_types: string[]
  }
}

/** AGN-REP-004 — agent leaderboard ranking metric. */
export type AgencyLeaderboardMetric = 'revenue' | 'closings' | 'response_time' | 'conversion_rate'

/** AGN-REP-004 — agent leaderboard row. */
export interface AgencyAgentLeaderboardRow {
  rank: number
  medal: 'gold' | 'silver' | 'bronze' | null
  agent_id: string
  agent_name: string
  role: string | null
  revenue: number
  closings: number
  conversion_rate: number | null
  median_response_minutes: number | null
  inquiries: number
  active_listings: number
  response_samples: number
  trend: 'up' | 'down' | 'flat'
  metric_value: number | null
  previous_metric_value: number | null
  member_href: string
}

/** AGN-REP-004 — agent leaderboard payload. */
export interface AgencyAgentLeaderboardResponse {
  generated_at: string
  agency_id: string
  metric: AgencyLeaderboardMetric
  available_metrics: AgencyLeaderboardMetric[]
  filters: { start_date: string; end_date: string }
  leaderboard: AgencyAgentLeaderboardRow[]
  summary: {
    agents_ranked: number
    total_revenue: number
    total_closings: number
  }
}

/** AGN-REP-006 — agency campaign performance row. */
export interface AgencyCampaignPerformanceRow {
  id: string
  name: string
  status: string
  trigger: string
  channel: string
  channels: string[]
  agent_id: string | null
  agent_name: string | null
  enrollments_total: number
  enrollments_active: number
  enrollments_completed: number
  completion_rate: number | null
  messages_sent: number
  messages_delivered: number
  messages_failed: number
  delivery_rate: number | null
  steps_count: number
  href: string
  created_at: string | null
}

/** AGN-REP-006 — agency campaign performance payload. */
export interface AgencyCampaignPerformanceResponse {
  generated_at: string
  agency_id: string
  filters: {
    start_date: string | null
    end_date: string | null
    channel: string | null
    agent_id: string | null
  }
  overview: {
    campaigns: number
    active_campaigns: number
    total_enrollments: number
    completed_enrollments: number
    messages_sent: number
    messages_delivered: number
    completion_rate: number | null
  }
  rows: AgencyCampaignPerformanceRow[]
  by_channel: Array<{
    channel: string
    messages_sent: number
    messages_delivered: number
    campaigns: number
    delivery_rate: number | null
  }>
  filter_options: {
    channels: string[]
    agents: Array<{ id: string; name: string }>
  }
}

/** Wave 2C — attribution model vocabulary. */
export type AttributionModel = 'last' | 'first' | 'linear' | 'position' | 'data_driven'

export interface AgencyAttributionFunnelMetrics {
  leads: number
  qualified: number
  viewings: number
  offers: number
  reservations: number
  transactions: number
  commissions: number
  gtv_micros: number
  commission_micros: number
  marketing_cost_micros: number
  roas: number | null
  roi: number | null
  currency: string | null
}

export interface AgencyAttributionPerformanceResponse {
  generated_at: string
  agency_id: string | null
  agent_id: string | null
  model: AttributionModel
  configured: boolean
  code: string | null
  overview: AgencyAttributionFunnelMetrics
  by_campaign: Array<AgencyAttributionFunnelMetrics & { campaign_id: string }>
  by_execution: Array<
    AgencyAttributionFunnelMetrics & { execution_id: string; campaign_id: string | null }
  >
  conversions: Array<{
    id: string
    contact_id: string | null
    from_stage: string | null
    to_stage: string
    occurred_at: string
    value_micros: number | null
    currency: string | null
  }>
}

export interface AgencyAttributionChainResponse {
  conversion: {
    id: string
    contact_id: string | null
    from_stage: string | null
    to_stage: string
    occurred_at: string
    value_micros: number | null
    currency: string | null
  }
  model: AttributionModel
  configured: boolean
  code: string | null
  touchpoint_execution_ids: string[]
  executions: Array<{
    id: string
    kind: string | null
    status: string | null
    campaign_id: string | null
    subject_type?: string | null
    subject_id?: string | null
  }>
  credits: Array<{
    id: string
    conversion_id: string
    execution_id: string
    model: string
    credit_weight: number
  }>
  credits_by_model: Record<string, Array<{
    id: string
    conversion_id: string
    execution_id: string
    model: string
    credit_weight: number
  }>>
}

/** Wave 2D — Experimentation (A/B/n + holdouts). */
export type ExperimentDimension =
  | 'creative'
  | 'copy'
  | 'cta'
  | 'channel'
  | 'timing'
  | 'journey_path'
export type ExperimentAllocation = 'even' | 'bandit'
export type ExperimentStatus = 'draft' | 'running' | 'concluded'

export interface ExperimentVariant {
  key: string
  label?: string
  weight?: number
  next?: string | null
  creative_variant_id?: string | null
  journey_path?: string | null
  is_control?: boolean
  payload?: Record<string, unknown>
}

export interface Experiment {
  id: string
  campaign_id: string | null
  dimension: ExperimentDimension
  variants: ExperimentVariant[]
  allocation: ExperimentAllocation
  holdout_pct: number
  goal_event: string
  status: ExperimentStatus
  result: Record<string, unknown>
  agency_id: string | null
  agent_id: string | null
  data: Record<string, unknown>
  created_at: string
  updated_at: string
}

export interface ExperimentAssignment {
  id: string
  experiment_id: string
  contact_id: string
  variant: string
  assigned_at: string
  assignment_reason: string
  model_version: string
  agency_id: string | null
  agent_id: string | null
  data: Record<string, unknown>
}

export interface ExperimentVariantResult {
  variant: string
  assigned: number
  conversions: number
  conversion_rate: number | null
  is_control: boolean
  vs_control: {
    rate_a: number | null
    rate_b: number | null
    lift: number | null
    z: number | null
    p_value: number | null
    significant_at_95: boolean
    confidence: number | null
    reason: string
    ci_95?: [number, number]
  } | null
}

export interface ExperimentResults {
  computed_at: string
  method: string
  goal_event: string
  to_stage: string
  control_variant: string | null
  assignment_count: number
  matched_conversions: number
  variants: ExperimentVariantResult[]
  sequential: { code: string }
  bandit: { code: string }
  winner_variant?: string | null
  promotion?: { promoted: boolean; variant?: string; reason?: string }
  concluded_at?: string
}

/** Wave 2B — publishing control plane / content calendar. */
export type ExecutionKind = 'message' | 'social_post' | 'paid_ad' | 'portal_submit' | 'seo_page'
export type ExecutionStatus =
  | 'draft'
  | 'scheduled'
  | 'in_review'
  | 'queued'
  | 'processing'
  | 'published'
  | 'failed'
  | 'cancelled'

export interface CalendarExecution {
  id: string
  kind: ExecutionKind | string
  status: ExecutionStatus | string
  scheduled_at: string | null
  recurrence: string | null
  campaign_id: string | null
  agent_id: string | null
  agency_id: string | null
  channel_connection_id: string | null
  creative_id: string | null
  audience_id: string | null
  subject_type: string | null
  subject_id: string | null
  provider_ref: string | null
  published_at: string | null
  completed_at: string | null
  created_at?: string
  updated_at?: string
  reschedulable: boolean
  data: Record<string, unknown>
}

export interface PublishingCalendarResponse {
  executions: CalendarExecution[]
  total: number
  truncated: boolean
  filters: Record<string, string | null | undefined>
  meta: {
    unsupported_filters: string[]
    reschedulable_statuses: string[]
  }
}

export interface PublishingValidationIssue {
  code: string
  severity: 'blocker' | 'warning'
  message: string
  meta?: Record<string, unknown>
}

export interface PublishingValidationResponse {
  execution_id: string
  ok: boolean
  blockers: PublishingValidationIssue[]
  warnings: PublishingValidationIssue[]
}

export interface PublishingPreviewResponse {
  execution: Pick<
    CalendarExecution,
    | 'id'
    | 'kind'
    | 'status'
    | 'scheduled_at'
    | 'campaign_id'
    | 'subject_type'
    | 'subject_id'
    | 'creative_id'
    | 'channel_connection_id'
    | 'agent_id'
    | 'agency_id'
  >
  connection: { id: string; health: string; provider_account_id: string | null } | null
  platform: string | null
  capabilities: Record<string, unknown>
  creative: Record<string, unknown> | null
  variants: Array<{
    id: string
    label: string | null
    copy: Record<string, unknown>
    renditions: Array<{
      id: string
      channel_key: string | null
      asset_url: string | null
      dimensions: unknown
      status: string | null
    }>
  }>
  channels: Array<{
    channel_key: string
    platform: string | null
    caption: string | null
    media_urls: string[]
    variant_id: string | null
    label: string | null
    renditions?: Array<{
      id: string
      channel_key: string | null
      asset_url: string | null
    }>
  }>
  side_effects: boolean
}

export interface PublishingBulkActionResponse {
  results: Array<{
    id: string
    ok: boolean
    code?: string
    message?: string
    status?: string
    execution?: CalendarExecution
  }>
}

/** AGN-CRD-004 — standing credit allocation rules for an agency wallet. */
export type AgencyCreditAllocationMode =
  | 'manual'
  | 'percentage'
  | 'cap_per_agent'
  | 'hybrid'

export interface AgencyCreditAllocationOverride {
  agent_user_id: string
  agent_name: string
  percentage: number | null
  cap_usd: number | null
}

export interface AgencyCreditAllocationRules {
  agency_id: string
  mode: AgencyCreditAllocationMode
  overrides: AgencyCreditAllocationOverride[]
  percentage_total: number
  shared_pool_percentage: number
  updated_by: string | null
  updated_at: string | null
  created_at: string | null
  is_default: boolean
}

export interface AgencyCreditAllocationAgent {
  user_id: string
  name: string
  role: string
}

/** Issue 192a — Personal Access Token row shape (no `hashed_secret`; server never returns it). */
export interface ApiTokenRecord {
  id: string
  name: string
  scopes: string[]
  last_used_at: string | null
  expires_at: string | null
  revoked_at: string | null
  created_at: string
  agency_id: string | null
}

/** Server response on POST — the raw token is here exactly once. */
export interface ApiTokenCreateResponse {
  token: string
  record: ApiTokenRecord
}

export interface CommandItem {
  message_id: string
  conversation_id: string
  category: string
  sentiment: 'positive' | 'neutral' | 'negative' | null
  priority: 'low' | 'normal' | 'high' | 'urgent'
  content: string
  author_name: string | null
  contact_id: string | null
  listing_id: string | null
  listing_title: string | null
  platform: string
  suggested_reply: string | null
  created_at: string
}

export interface AgencyLeadFunnelStage {
  stage: string
  count: number
}

export interface AgencyLeadFunnelSourceRow {
  source: string
  inquiries: number
  viewings: number
  opportunities: number
  won: number
  won_value: number
}

export interface AgencyLeadFunnelAgentRow {
  agent_id: string
  agent_name: string
  inquiries: number
  viewings: number
  opportunities: number
  won: number
}

export interface AgencyLeadFunnelResponse {
  generated_at: string
  scope: {
    agency_id: string
    start_date: string | null
    end_date: string | null
    filters: { source: string | null; agent_id: string | null; area: string | null }
  }
  funnel: {
    inquiries: number
    viewings: number
    opportunities: number
    closed_won: number
    closed_lost: number
  }
  conversion_rates: {
    inquiry_to_viewing: number | null
    viewing_to_opportunity: number | null
    opportunity_to_won: number | null
    inquiry_to_won: number | null
  }
  by_stage: AgencyLeadFunnelStage[]
  by_source: AgencyLeadFunnelSourceRow[]
  by_agent: AgencyLeadFunnelAgentRow[]
  sankey: {
    nodes: { id: string; label: string; group: 'source' | 'agent' | 'outcome' }[]
    links: { source: string; target: string; value: number }[]
  }
  filter_options: {
    sources: string[]
    agents: { id: string; name: string }[]
    areas: string[]
  }
}

export interface AgencyRevenueAttributionRow {
  channel: string
  label: string
  revenue: number
  count: number
}

export interface AgencyRevenueAttributionAgentRow {
  agent_id: string
  agent_name: string
  revenue: number
  count: number
}

export interface AgencyRevenueAttributionCampaignRow {
  campaign_id: string
  campaign_name: string
  revenue: number
  count: number
}

export interface AgencyRevenueAttributionTransaction {
  id: string
  closed_at: string
  agent_id: string
  agent_name: string
  attribution_source: string
  channel_label: string
  final_sold_price: number
  currency: string
  transaction_type: string
  listing_id: string
  campaign_id: string | null
  campaign_name: string | null
}

export interface AgencyRevenueAttributionResponse {
  generated_at: string
  scope: {
    agency_id: string
    start_date: string | null
    end_date: string | null
    filters: { channel: string | null; agent_id: string | null; campaign_id: string | null }
  }
  summary: {
    total_revenue: number
    transaction_count: number
    average_deal_value: number
    currency: string
  }
  by_channel: AgencyRevenueAttributionRow[]
  by_agent: AgencyRevenueAttributionAgentRow[]
  by_campaign: AgencyRevenueAttributionCampaignRow[]
  waterfall: { label: string; value: number }[]
  sankey: {
    nodes: { id: string; label: string; group: 'channel' | 'agent' }[]
    links: { source: string; target: string; value: number }[]
  }
  transactions: AgencyRevenueAttributionTransaction[]
  filter_options: {
    channels: string[]
    agents: { id: string; name: string }[]
    campaigns: { id: string; name: string }[]
  }
}

export interface PerformanceMetricBlock {
  impressions: number
  reach: number
  likes: number
  comments: number
  shares: number
  saves: number
  clicks: number
  engagements: number
  messages: number
  inquiries: number
  viewings_scheduled: number
  closes: number
  contacts: number
  avg_views_per_post: number
  published_posts: number
}

export type SavedSearchAlertChannel = 'email' | 'whatsapp' | 'inapp'
export type SavedSearchAlertFrequency = 'instant' | 'daily' | 'weekly'

export interface SavedSearch {
  id: string
  user_id: string | null
  agent_id: string | null
  contact_id: string | null
  name: string
  filters: Record<string, unknown>
  filter_summary?: string
  alert_enabled: boolean
  alert_channel: SavedSearchAlertChannel
  alert_frequency: SavedSearchAlertFrequency
  last_alert_run_at: string | null
  last_match_count: number
  created_at: string
  updated_at: string
}

export interface SavedSearchInput {
  name: string
  filters?: Record<string, unknown>
  alert_enabled?: boolean
  alert_channel?: SavedSearchAlertChannel
  alert_frequency?: SavedSearchAlertFrequency
}

export type BuyerOfferStatus = 'received' | 'countered' | 'accepted' | 'rejected' | 'withdrawn'
export type BuyerOfferFinancing = 'cash' | 'mortgage' | 'mixed'

export interface BuyerOffer {
  id: string
  property_id: string
  agent_id: string
  contact_id: string | null
  offeror_name: string
  amount: number
  currency: string
  offer_date: string
  terms: string | null
  financing_type: BuyerOfferFinancing | null
  expiry_date: string | null
  conditions: string | null
  status: BuyerOfferStatus
  notes: string | null
  created_at: string
  updated_at: string
}

export type SellerReportLayout = 'standard' | 'compact' | 'executive'
export type SellerReportStatus = 'live' | 'frozen' | 'revoked'

/**
 * Vendor report composition (migration 812). `layout_template` above is
 * deprecated and never rendered; the composition is what decides which sections
 * the vendor sees and in what order.
 */
export type VendorReportContentClass = 'report_content' | 'promotional'
export type VendorReportPurposeClass = 'transactional' | 'marketing'

/** A section the backend registry offers, as returned in `available_sections`. */
export interface VendorReportSectionDefinition {
  id: string
  label: string
  content_class: VendorReportContentClass
  /** Cannot be hidden, by the template or per listing. */
  required: boolean
  default_visible: boolean
}

/** One entry of an agency template's ordered section list. */
export interface VendorReportTemplateSection {
  id: string
  visible: boolean
  options: Record<string, unknown>
}

export interface VendorReportComposition {
  id: string
  agency_id: string
  name: string
  is_default: boolean
  sections: VendorReportTemplateSection[]
  purpose_class: VendorReportPurposeClass
  created_at: string
  updated_at: string
}

/**
 * The branding subset the backend hands an unauthenticated reader. Deliberately
 * narrower than `AgencyBranding`: no updated_by (a user id), no internal brand
 * state, no description. See backend publicAgencyBranding.
 */
export interface PublicAgencyBranding {
  agency_id: string
  name: string
  logo_url: string | null
  primary_color: string | null
  accent_color: string | null
  font_family: string
}

/** What a given vendor actually sees: ordered, visible, overrides applied. */
export interface ResolvedVendorReportComposition {
  composition_id: string | null
  composition_name: string | null
  hidden_sections: string[]
  sections: Array<{
    id: string
    label: string
    content_class: VendorReportContentClass
    options: Record<string, unknown>
  }>
  /** Derived from the template, so hiding a section never relaxes it. */
  purpose_class: VendorReportPurposeClass
}

export interface SellerReportConfig {
  id: string
  property_id: string
  agent_id: string
  agency_id: string | null
  state_of_play: string | null
  agent_summary: string | null
  show_offer_amounts: boolean
  show_full_address: boolean
  /** @deprecated migration 812 — never rendered. Use `composition_id`. */
  layout_template: SellerReportLayout
  composition_id: string | null
  /** Hide-only per-listing override; it cannot add a section. */
  hidden_sections: string[]
  status: SellerReportStatus
  frozen_snapshot: Record<string, unknown> | null
  created_at: string
  updated_at: string
}

export interface SellerReportShareToken {
  id: string
  report_id: string
  token: string
  recipient_email: string | null
  revoked_at: string | null
  created_at: string
}

export interface SellerReportPayload {
  generated_at: string
  /**
   * Absent on a report frozen before migration 812, so every consumer needs a
   * fallback order rather than assuming this is present.
   */
  composition?: ResolvedVendorReportComposition
  /** Null for a personal-workspace listing; absent on an older frozen snapshot. */
  branding?: PublicAgencyBranding | null
  report: {
    id: string | null
    status: SellerReportStatus
    layout_template: SellerReportLayout
    state_of_play: string
    agent_summary: string | null
    show_offer_amounts: boolean
    show_full_address: boolean
  }
  property: {
    id: string
    reference: string
    title: string
    listing_type?: string | null
    property_type?: string | null
    status?: string | null
    price: number
    price_unit: string
    bedrooms?: number | null
    bathrooms?: number | null
    area?: number | null
    area_unit?: string | null
    location_label: string
    campaign_started_at?: string | null
    days_on_market: number
    hero_image?: string | null
  }
  agent: {
    id: string
    name: string
    photo_url: string | null
    agency_name: string | null
  } | null
  scores: {
    quality: number | null
    cleanliness: number | null
    location: number | null
    overall: number | null
  }
  summary: {
    list_price: number
    days_on_market: number
    posts_count: number
    inquiries_received: number
    qualified_inquiries: number
    shielded_inquiries: number
    untriaged_inquiries: number
    viewings_count: number
    offers_count: number
    momentum: number
  }
  marketing: {
    websites: Array<Record<string, unknown>>
    social_posts: Array<Record<string, unknown>>
    channels: Array<{
      id: string
      platform: string
      status: string
      published_at: string | null
      post_url: string | null
      views: number
      clicks: number
      leads: number
    }>
  }
  performance: {
    all_channels: PerformanceMetricBlock
    per_channel: Array<PerformanceMetricBlock & { platform: string }>
    funnel: Array<{
      platform: string
      views: number
      engagements: number
      clicks: number
      inquiries: number
      viewings_scheduled: number
      closes: number
    }>
    time_series: {
      days: number
      channels: Record<string, Array<{
        date: string
        impressions: number
        engagements: number
        clicks: number
        comments: number
        likes: number
        shares: number
        saves: number
      }>>
    }
  }
  inquiries: {
    total: number
    qualified: number
    shielded: number
    untriaged: number
  }
  feedback: Array<{
    id: string
    scheduled_at?: string | null
    outcome: string
  }>
  offers: Array<{
    id: string
    status: string
    summary?: string
    amount?: number
    currency?: string
    amount_hidden?: boolean
    offer_date?: string
    financing_type?: string | null
  }>
  is_empty: boolean
}

export interface SellerReportResponse {
  report: SellerReportConfig
  payload: SellerReportPayload
  share_tokens: SellerReportShareToken[]
  /** The backend registry, so the agent UI never hard-codes a section list. */
  available_sections?: VendorReportSectionDefinition[]
  /** The listing agency's templates, so an ordinary agent can choose one. */
  available_compositions?: VendorReportComposition[]
}

export interface BuyerOfferInput {
  contact_id?: string | null
  offeror_name: string
  amount: number
  currency?: string
  offer_date?: string
  terms?: string | null
  financing_type?: BuyerOfferFinancing | null
  expiry_date?: string | null
  conditions?: string | null
  status?: BuyerOfferStatus
  notes?: string | null
}

// PA-NDL-001 — Notifications dead-letter queue.
export type NotificationDeadLetterStatus = 'dead_letter' | 'failed' | 'ignored' | 'pending'

export interface NotificationDeadLetterItem {
  id: string
  notification_id: string | null
  channel: string | null
  status: NotificationDeadLetterStatus
  attempts: number
  last_error: string | null
  next_retry_at: string | null
  created_at: string | null
  event_type: string | null
  title: string | null
}

export interface NotificationDeadLetterResponse {
  items: NotificationDeadLetterItem[]
  total: number
}

// AGT-TSK-002 — Task detail / edit.
export type TaskType = 'call' | 'email' | 'follow_up' | 'viewing' | 'meeting'
export type TaskStatus = 'pending' | 'completed' | 'cancelled' | 'snoozed'
export type TaskPriority = 'low' | 'normal' | 'high' | 'urgent'

export interface TaskRecord {
  id: string
  title: string
  notes: string
  type: TaskType
  status: TaskStatus
  priority: TaskPriority
  due_at: string
  completed_at?: string | null
  assigned_to?: string | null
  contact_id: string | null
  inquiry_id: string | null
  opportunity_id: string | null
  viewing_id?: string | null
  conversation_id: string | null
  created_at?: string
  updated_at?: string
}

// PA-INS-002 — Inspection submit form.
export type InspectorAssignmentStatus = 'pending' | 'in_progress' | 'completed' | 'skipped'

export interface InspectorAssignment {
  id: string
  agent_id: string
  area_id: string
  assigned_by?: string | null
  assigned_at: string
  due_at?: string | null
  completed_at?: string | null
  notes?: string | null
  status: InspectorAssignmentStatus
}

export interface InspectorArea {
  id: string
  name: string
  slug?: string
}

export interface InspectorDimension {
  id: string
  name: string
  slug: string
}

export interface InspectorAssignmentDetail {
  assignment: InspectorAssignment
  area: InspectorArea | null
  dimensions: InspectorDimension[]
}

export interface InspectionSubmissionInput {
  assignment_id: string
  area_id: string
  gps_latitude: number
  gps_longitude: number
  dimension_scores: Record<string, number>
  photo_urls?: string[]
  notes?: string | null
  signature?: string | null
}

export interface InspectionSubmission {
  id: string
  assignment_id: string
  agent_id: string
  area_id: string
  gps_latitude: number | null
  gps_longitude: number | null
  photo_urls: string[]
  dimension_scores: Record<string, number>
  notes: string | null
  signature: string | null
  status: 'pending_review' | 'approved' | 'rejected'
  reviewed_by: string | null
  reviewed_at: string | null
  review_notes: string | null
  submitted_at: string
}

// AGT-CTC-005 — Contact export.
export type ContactExportFormat = 'csv' | 'vcard'

export interface ContactExportField {
  key: string
  label: string
}

/** Fields a Pro agent may include in a CSV export (canonical column order). */
export const CONTACT_EXPORT_FIELDS: ContactExportField[] = [
  { key: 'name', label: 'Name' },
  { key: 'email', label: 'Email' },
  { key: 'phone', label: 'Phone' },
  { key: 'status', label: 'Status' },
  { key: 'source', label: 'Source' },
  { key: 'tags', label: 'Tags' },
  { key: 'first_touch_channel', label: 'First touch channel' },
  { key: 'first_touch_at', label: 'First touch at' },
  { key: 'last_activity_at', label: 'Last activity at' },
  { key: 'created_at', label: 'Created at' },
]

// PA-GOO-001 — Google Maps usage dashboard.
export interface GoogleUsageBreakdownOp {
  operation: string
  requests: number
  cost: number
}

export interface GoogleUsageTopConsumer {
  area_id: string
  requests: number
  cost: number
}

export interface GoogleUsageDailyPoint {
  date: string
  cost: number
  requests: number
}

export interface GoogleUsageSummary {
  mtd_spend_usd: number
  mtd_requests: number
  budget_usd_monthly: number
  alert_threshold_pct: number
  projected_month_end_usd: number
  headroom_usd: number
  pct_consumed: number
  over_budget: boolean
  near_threshold: boolean
  by_operation: GoogleUsageBreakdownOp[]
  top_consumers: GoogleUsageTopConsumer[]
  daily: GoogleUsageDailyPoint[]
  updated_at: string | null
}

export interface GoogleBudgetConfig {
  budget_usd_monthly: number
  alert_threshold_pct: number
  updated_by: string | null
  updated_at: string | null
}

export interface GoogleBudgetResponse {
  config: GoogleBudgetConfig
  constraints: {
    max_budget_usd_monthly: number
    min_alert_threshold_pct: number
    max_alert_threshold_pct: number
  }
}

export interface GoogleBudgetInput {
  budget_usd_monthly: number
  alert_threshold_pct: number
}

// AGT-APR-006 — My submitted reports. Summary row for the agent's own
// comparable-report submissions (GET /api/pricing/my-comparable-reports).
export interface ComparableReportSummary {
  id: string
  status?: string
  reason?: string
  comparable_id?: string
  comparable_type?: string
  notes?: string | null
  decision_reason_code?: string | null
  decision_notes?: string | null
  reviewed_at?: string | null
  created_at?: string
}

export interface ClosedTransaction {
  id: string
  listing_id: string
  agent_id: string
  agency_id: string | null
  contact_id: string | null
  opportunity_id: string | null
  transaction_type: 'sale' | 'rent' | 'lease'

  original_listed_price: number | null
  final_sold_price: number | null
  currency: string
  price_reductions_count: number | null

  listed_at: string | null
  closed_at: string
  days_on_market: number | null
  days_to_first_offer: number | null

  offers_received_count: number | null
  viewings_conducted: number | null
  rejected_offer_max: number | null
  rejected_offer_min: number | null

  buyer_type: string
  buyer_nationality: string | null
  payment_method: string
  down_payment_percent: number | null
  mortgage_provider: string | null

  close_reason: string
  agent_notes: string
  attribution_source: string

  origin: string
  is_backfilled: boolean
  source_note: string | null

  created_at: string
  updated_at: string
}

export interface SocialCardTemplate {
  id: string
  schema_version?: number
  name: string
  description?: string
  owner_type: 'platform' | 'store' | 'agency' | 'agent'
  owner_id: string | null
  engine?: 'builtin' | 'bannerbear'
  category?: string
  tags?: string[]
  base_canvas: { width: number; height: number }
  background?: Record<string, unknown>
  layers: Array<Record<string, unknown>>
  platform_overrides?: Record<string, Record<string, unknown>>
  created_at?: string
  updated_at?: string
  __preview?: {
    layer_count: number
    aspect: string | null
    has_photo_layer: boolean
    engine: string
    category: string | null
    tags: string[]
  }
}

export interface SocialCardAsset {
  id: string
  listing_id: string
  template_id: string
  template_name: string
  template_engine?: string
  platform: string
  platform_label: string
  dimensions: { width: number; height: number; aspect?: string }
  filename: string
  url: string
  size_bytes: number
  created_at: string
}

export interface CreativeRendition {
  id: string
  creative_variant_id: string
  channel_key: string
  width: number
  height: number
  provider: string
  asset_url: string | null
  status: string
}

export interface CreativeVariant {
  id: string
  creative_id: string
  label: string
  copy: Record<string, string>
  experiment_id?: string | null
  sort_order?: number
  renditions?: CreativeRendition[]
}

export interface CreativeRecord {
  id: string
  subject_type: string
  subject_id: string
  source: 'manual' | 'ai'
  approval_state: 'not_required' | 'pending' | 'approved' | 'rejected'
  status: string
  channel_keys: string[]
}

export interface CreativeBundle {
  creative: CreativeRecord
  variants: CreativeVariant[]
  approval_request?: {
    id: string
    state: string
    subject_type: string
    subject_id: string
  } | null
}

/**
 * Conversation list/detail shapes with dual-read channel + source
 * (BE-BLOCKER-04 migration window — prefer channel/source, fall back via readChannel/readSource).
 */
export interface InboxConversation {
  id: string
  contact_id?: string | null
  contact_name: string | null
  contact_email?: string | null
  contact_phone?: string | null
  contact_masked?: boolean
  contact_avatar_url?: string | null
  /** Transport — prefer this; else derive from source_channel. */
  channel?: string | null
  /** Origin — prefer this; else derive from source_channel. */
  source?: string | null
  /** Legacy packed field — dual-read fallback only. */
  source_channel?: string | null
  status: 'open' | 'closed' | string
  priority?: string
  priority_score?: number | null
  priority_reason?: string | null
  subject?: string | null
  last_message_at: string | null
  last_message_preview: string
  unread_count: number
  is_unread_by_agent?: boolean
  assigned_agent_id?: string | null
  assigned_agent_name?: string | null
  linked_listing_id?: string | null
  linked_listing_label?: string | null
  created_at?: string
  updated_at?: string
  archived_at?: string | null
}

export interface InboxConversationMessage {
  id: string
  conversation_id?: string
  direction: 'inbound' | 'outbound' | 'system'
  channel?: string | null
  source?: string | null
  source_channel?: string | null
  provider?: string | null
  content?: string
  body?: string
  content_type?: string
  status?: string
  delivery_status?: string
  created_at?: string
  sent_at?: string
  created_by_agent_id?: string | null
  failed_reason?: string | null
  is_first_inbound?: boolean
  system_event_type?: string | null
  image_url?: string | null
  audio_url?: string | null
  attachments?: Array<{
    id?: string
    url: string
    mime?: string | null
    filename?: string | null
    size_bytes?: number | null
    kind?: string | null
  }>
  metadata?: { attachments?: InboxConversationMessage['attachments'] } | null
  suggested_reply?: string | null
}

export interface InboxConversationDetail extends InboxConversation {
  messages?: InboxConversationMessage[]
  contact?: {
    id?: string
    name?: string
    email?: string
    phone?: string
    status?: string
    tags?: string[]
    source?: string
    assigned_agent_id?: string | null
  } | null
}

export interface CommandOpportunity {
  id: string
  contact_id: string
  property_id: string | null
  agent_id: string | null
  stage: string
  deal_value: number | null
  currency: string
  probability: number
  source: string
  sub_pipeline?: string
  origin?: string
  notes: string
  created_at: string
  updated_at: string
}

function resolveApiBase() {
  const configured = String(import.meta.env.VITE_API_URL || '').trim()
  if (configured) return configured.replace(/\/$/, '')

  // Local developer default (works with Vite proxy in vite.config.ts)
  return '/api'
}

export const API_BASE = resolveApiBase()

const TOKEN_KEY = 'fi_token'

function getToken() {
  const token = localStorage.getItem(TOKEN_KEY) || localStorage.getItem('sa_token')
  if (token && !localStorage.getItem(TOKEN_KEY) && localStorage.getItem('sa_token')) {
    localStorage.setItem(TOKEN_KEY, token)
    localStorage.removeItem('sa_token')
  }
  return token
}

/** Exported for WebSocket auth and other non-fetch clients. */
export function getAuthToken() {
  return getToken()
}

export function clearAuthToken() {
  localStorage.removeItem(TOKEN_KEY)
  localStorage.removeItem('sa_token')
}

export function setAuthToken(token: string) {
  localStorage.setItem(TOKEN_KEY, token)
  localStorage.removeItem('sa_token')
}

/**
 * Step-up elevation (Phase 7f).
 *
 * Deliberately a SECOND token rather than a replacement session: the normal
 * Bearer token is never touched, so obtaining elevation cannot disturb the
 * session and a second open tab is never left holding a stale one.
 *
 * Held in sessionStorage, not localStorage — an elevation is worth 15 minutes
 * and should not outlive the browser tab that earned it.
 */
const ELEVATED_TOKEN_KEY = 'fi_elevated_token'

export function getElevatedToken(): string | null {
  try {
    return sessionStorage.getItem(ELEVATED_TOKEN_KEY)
  } catch {
    return null
  }
}

export function setElevatedToken(token: string) {
  try {
    sessionStorage.setItem(ELEVATED_TOKEN_KEY, token)
  } catch {
    /* private-mode browsers: elevation simply won't persist across requests */
  }
}

export function clearElevatedToken() {
  try {
    sessionStorage.removeItem(ELEVATED_TOKEN_KEY)
  } catch {
    /* ignore */
  }
}

/** Mirrors PA session env (PA-NAV-001). Same key as hooks/useEnv ENV_STORAGE_KEY.
 * Inline read avoids a circular import (useEnv imports this module). */
function readWingcasterEnvHeader(): 'live' | 'test' {
  try {
    const raw = sessionStorage.getItem('wingcaster.env') ?? localStorage.getItem('wingcaster.env')
    if (raw === 'test' || raw === 'TEST') return 'test'
  } catch {
    /* private mode */
  }
  return 'live'
}

function headers() {
  const h: Record<string, string> = { 'Content-Type': 'application/json' }
  const token = getToken()
  if (token) h['Authorization'] = `Bearer ${token}`
  // Sent on every request so a gated endpoint sees it without each call site
  // having to know it is gated. Endpoints that don't require elevation ignore
  // the header entirely.
  const elevated = getElevatedToken()
  if (elevated) h['X-Elevated-Token'] = elevated
  // PA LIVE/TEST binding — server verifies against session GUC (Wave 0).
  h['X-Wingcaster-Env'] = readWingcasterEnvHeader()
  return h
}

async function fetchJson(path: string, options?: RequestInit) {
  const url = `${API_BASE}${path}`
  const res = await fetch(url, {
    ...options,
    headers: { ...headers(), ...(options?.headers || {}) },
  })
  const bodyText = await res.text()
  const contentType = res.headers.get('content-type') || ''
  const isLikelyHtml = /^\s*</.test(bodyText)

  const parseJson = () => {
    if (!bodyText) return null
    try {
      return JSON.parse(bodyText)
    } catch {
      return null
    }
  }

  if (!res.ok) {
    const err = parseJson() || {
      error: isLikelyHtml
        ? `API responded with HTML instead of JSON (${res.status}) at ${url}. Check VITE_API_URL for this environment.`
        : `Request failed (${res.status})`,
      status: res.status,
      url,
    }
    const error = new Error((err as any).error || `HTTP ${res.status}`) as Error & Record<string, unknown>
    Object.assign(error, err as Record<string, unknown>, { status: res.status, url })
    throw error
  }

  if (!bodyText) return null

  const parsed = parseJson()
  if (parsed !== null) return parsed

  if (isLikelyHtml || !contentType.includes('application/json')) {
    throw new Error(`API responded with non-JSON payload at ${url}. Check VITE_API_URL and backend routing.`)
  }

  throw new Error(`Failed to parse JSON response from ${url}`)
}

/** Strategy E-CFG platform row (F4) — GET/PUT /api/admin/strategy/config. */
export type PlatformConfigJsonMap = Record<string, unknown>

export interface PlatformConfig {
  id: string
  chaining_rules: { default_chained?: boolean } & PlatformConfigJsonMap
  default_intervals: PlatformConfigJsonMap
  lead_times: PlatformConfigJsonMap
  approval_targets: PlatformConfigJsonMap
  deadline_policies: { hold_cap_days?: number } & PlatformConfigJsonMap
  dismiss_interval: { seconds?: number } & PlatformConfigJsonMap
  declared_maxima: { dismiss_interval_seconds?: number } & PlatformConfigJsonMap
  set_by: string | null
  set_at: string | null
  agency_id?: string | null
  agent_id?: string | null
  plan_id?: string | null
  level?: string
  created_at?: string
  updated_at?: string
  data?: PlatformConfigJsonMap
}

export interface EffectiveConfigValue {
  value: unknown
  origin: string
}

/** Resolved three-level config with labelled origins (platform / tenant / plan). */
export interface EffectiveConfig {
  values: Record<string, EffectiveConfigValue>
  declared_maxima: EffectiveConfigValue
}

export type StrategyConfigWriteBody = {
  chaining_rules?: PlatformConfigJsonMap
  default_intervals?: PlatformConfigJsonMap
  lead_times?: PlatformConfigJsonMap
  approval_targets?: PlatformConfigJsonMap
  deadline_policies?: PlatformConfigJsonMap
  dismiss_interval?: PlatformConfigJsonMap
  declared_maxima?: PlatformConfigJsonMap
}

export interface AdminMarket {
  code: string
  label: string
  enabled: boolean
  phone_number_id?: string | null
  display_name?: string | null
}

export interface AdminMarketPatch {
  enabled?: boolean
  phone_number_id?: string | null
  display_name?: string | null
}

export const api = {
  // Auth
  login: (email: string, password: string) =>
    fetchJson('/auth/login', { method: 'POST', body: JSON.stringify({ email, password }) }),
  register: (data: Record<string, unknown>) =>
    fetchJson('/auth/register', { method: 'POST', body: JSON.stringify(data) }),
  me: () => fetchJson('/auth/me'),
  updateProfile: (data: Record<string, unknown>) =>
    fetchJson('/auth/me', { method: 'PUT', body: JSON.stringify(data) }),
  patchMe: (data: Record<string, unknown>) =>
    fetchJson('/users/me', { method: 'PATCH', body: JSON.stringify(data) }),
  getSettingsIndex: (): Promise<SettingsIndexResponse> => fetchJson('/settings/index'),
  getAuthSessions: (): Promise<{ sessions: AuthSessionRow[] }> => fetchJson('/auth/sessions'),
  deleteAuthSession: (sessionId: string) =>
    fetchJson(`/auth/sessions/${encodeURIComponent(sessionId)}`, { method: 'DELETE' }),
  deleteAuthSessionsExceptCurrent: (): Promise<{ revoked?: number } | null> =>
    fetchJson('/auth/sessions/all-except-current', { method: 'DELETE' }),
  getPushTokens: (): Promise<{ tokens: PushTokenRow[] }> => fetchJson('/auth/push-tokens'),
  deletePushToken: (id: string) =>
    fetchJson(`/auth/push-token/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  createBillingPortalSession: (section?: string): Promise<{ url: string }> =>
    fetchJson('/billing/portal-session', {
      method: 'POST',
      body: JSON.stringify(section ? { section } : {}),
    }),
  testBillingNotification: (channel: string) =>
    fetchJson(`/billing/notifications/test?channel=${encodeURIComponent(channel)}`, { method: 'POST' }),
  initiateDeleteAccount: (data: Record<string, unknown>) =>
    fetchJson('/auth/delete-account/initiate', { method: 'POST', body: JSON.stringify(data) }),
  regenerateDeleteAccountWord: (): Promise<{ word?: string }> =>
    fetchJson('/auth/delete-account/regenerate-word', { method: 'POST', body: '{}' }),
  resendDeleteAccountEmail: () =>
    fetchJson('/auth/delete-account/resend-email', { method: 'POST', body: '{}' }),
  confirmDeleteAccount: (data: Record<string, unknown>) =>
    fetchJson('/auth/delete-account/confirm', { method: 'POST', body: JSON.stringify(data) }),
  cancelDeleteAccount: () =>
    fetchJson('/auth/delete-account/cancel', { method: 'POST', body: '{}' }),
  getOnboarding: () => fetchJson('/auth/onboarding'),
  updateOnboarding: (data: Record<string, unknown>) =>
    fetchJson('/auth/onboarding', { method: 'PATCH', body: JSON.stringify(data) }),
  forgotPassword: (email: string) =>
    fetchJson('/auth/password/forgot', { method: 'POST', body: JSON.stringify({ email }) }),
  resetPassword: (token: string, password: string) =>
    fetchJson('/auth/password/reset', { method: 'POST', body: JSON.stringify({ token, password }) }),
  changePassword: (current_password: string, new_password: string) =>
    fetchJson('/auth/password/change', { method: 'POST', body: JSON.stringify({ current_password, new_password }) }),
  requestAccountRecovery: (data: { email: string; reason: string; preferred_channel?: 'email' | 'whatsapp'; contact?: string }) =>
    fetchJson('/auth/recovery/request', { method: 'POST', body: JSON.stringify(data) }),
  completeAccountRecovery: (data: { case_id: string; token: string; password: string }) =>
    fetchJson('/auth/recovery/complete', { method: 'POST', body: JSON.stringify(data) }),
  getAdminAccountRecoveryCases: () => fetchJson('/admin/account-recovery'),
  getAdminAccountRecoveryCase: (caseId: string) =>
    fetchJson(`/admin/account-recovery/${encodeURIComponent(caseId)}`),
  // Market on/off registry (migration 804)
  getEnabledMarkets: (): Promise<{ codes: string[] }> => fetchJson('/markets/enabled'),
  getAdminMarkets: (): Promise<{ markets: AdminMarket[] }> =>
    fetchJson('/admin/markets'),
  setAdminMarket: (code: string, body: AdminMarketPatch): Promise<AdminMarket> =>
    fetchJson(`/admin/markets/${encodeURIComponent(code)}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),
  // Strategy E-CFG platform defaults (F4) — privileged PA write path
  getAdminStrategyConfig: (): Promise<{
    platform: PlatformConfig | null
    effective: EffectiveConfig
  }> => fetchJson('/admin/strategy/config'),
  putAdminStrategyConfig: (
    body: StrategyConfigWriteBody,
  ): Promise<{ platform: PlatformConfig }> =>
    fetchJson('/admin/strategy/config', {
      method: 'PUT',
      body: JSON.stringify(body),
    }),
  /**
   * BE-BLOCKER-22 — only cast-vote. Legacy /approve and /reject return 410 Gone.
   * Do NOT reintroduce approve/reject call sites.
   */
  castAccountRecoveryVote: (
    caseId: string,
    body: { vote: 'approve' | 'reject'; notes?: string },
  ) =>
    fetchJson(`/admin/account-recovery/${encodeURIComponent(caseId)}/cast-vote`, {
      method: 'POST',
      body: JSON.stringify({ vote: body.vote, notes: body.notes ?? '' }),
    }),
  requestAccountRecoveryInfo: (
    caseId: string,
    body: { reason_code: string; notes?: string; requested_evidence: string[] },
  ) =>
    fetchJson(`/admin/account-recovery/${encodeURIComponent(caseId)}/request-info`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  cancelAccountRecoveryInfoRequest: (caseId: string) =>
    fetchJson(`/admin/account-recovery/${encodeURIComponent(caseId)}/cancel-info-request`, {
      method: 'POST',
      body: '{}',
    }),
  undoAccountRecoveryApprove: (caseId: string) =>
    fetchJson(`/admin/account-recovery/${encodeURIComponent(caseId)}/undo-approve`, {
      method: 'POST',
      body: '{}',
    }),
  withdrawAccountRecoveryVote: (caseId: string) =>
    fetchJson(`/admin/account-recovery/${encodeURIComponent(caseId)}/withdraw-vote`, {
      method: 'POST',
      body: '{}',
    }),
  revealAccountRecoveryAudit: (caseId: string, field: string) =>
    fetchJson(`/admin/account-recovery/${encodeURIComponent(caseId)}/reveal-audit`, {
      method: 'POST',
      body: JSON.stringify({ field }),
    }),
  /** Authenticated evidence proxy path (never a public/unsigned URL). */
  accountRecoveryEvidencePath: (caseId: string, evidenceId: string, download = false) =>
    `/admin/account-recovery/${encodeURIComponent(caseId)}/evidence/${encodeURIComponent(evidenceId)}${
      download ? '?download=1' : ''
    }`,
  /** Fetch evidence bytes via the PA auth proxy (Authorization + X-Wingcaster-Env). */
  fetchAccountRecoveryEvidenceBlob: async (caseId: string, evidenceId: string, download = false) => {
    const path = `/admin/account-recovery/${encodeURIComponent(caseId)}/evidence/${encodeURIComponent(evidenceId)}${
      download ? '?download=1' : ''
    }`
    const url = `${API_BASE}${path}`
    const res = await fetch(url, { headers: { ...headers() } })
    if (!res.ok) {
      const err = new Error(`Evidence fetch failed (${res.status})`) as Error & { status?: number }
      err.status = res.status
      throw err
    }
    return res.blob()
  },

  // Portal moderation detail (PA-MOD-002) — env-scoped via X-Wingcaster-Env
  getPortalModerationSubmission: (submissionId: string) =>
    fetchJson(`/admin/moderation/portals/${encodeURIComponent(submissionId)}`),
  getPortalModerationSibling: (
    submissionId: string,
    direction: 'next' | 'prev',
    query: Record<string, string | undefined> = {},
  ) => {
    const params = new URLSearchParams({ direction })
    for (const [k, v] of Object.entries(query)) {
      if (v != null && v !== '') params.set(k, v)
    }
    return fetchJson(
      `/admin/moderation/portals/${encodeURIComponent(submissionId)}/sibling?${params.toString()}`,
    )
  },
  getPortalModerationHistory: (submissionId: string) =>
    fetchJson(`/admin/moderation/portals/${encodeURIComponent(submissionId)}/history`),
  getPortalModerationAudit: (submissionId: string) =>
    fetchJson(`/admin/moderation/portals/${encodeURIComponent(submissionId)}/audit`),
  approvePortalModerationSubmission: (submissionId: string) =>
    fetchJson(`/admin/moderation/portals/${encodeURIComponent(submissionId)}/approve`, {
      method: 'POST',
      body: '{}',
    }),
  rejectPortalModerationSubmission: (
    submissionId: string,
    body: { reason_code: string; notes?: string },
  ) =>
    fetchJson(`/admin/moderation/portals/${encodeURIComponent(submissionId)}/reject`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  requestInfoPortalModerationSubmission: (
    submissionId: string,
    body: { reason_code: string; notes: string },
  ) =>
    fetchJson(`/admin/moderation/portals/${encodeURIComponent(submissionId)}/request-info`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  undoPortalModerationApprove: (submissionId: string) =>
    fetchJson(`/admin/moderation/portals/${encodeURIComponent(submissionId)}/undo-approve`, {
      method: 'POST',
      body: '{}',
    }),
  undoPortalModerationReject: (submissionId: string) =>
    fetchJson(`/admin/moderation/portals/${encodeURIComponent(submissionId)}/undo-reject`, {
      method: 'POST',
      body: '{}',
    }),
  revealPortalModerationContact: (submissionId: string, field: 'phone' | 'email') =>
    fetchJson(`/admin/moderation/portals/${encodeURIComponent(submissionId)}/reveal-contact`, {
      method: 'POST',
      body: JSON.stringify({ field }),
    }),

  // Two-factor / step-up (Phase 7f)
  twoFactorStatus: (): Promise<TwoFactorStatus> => fetchJson('/auth/2fa/status'),
  totpSetup: (current_password: string): Promise<TotpSetup> =>
    fetchJson('/auth/2fa/totp/setup', { method: 'POST', body: JSON.stringify({ current_password }) }),
  totpVerify: (secret: string, code: string): Promise<TotpEnrolmentResult> =>
    fetchJson('/auth/2fa/totp/verify', { method: 'POST', body: JSON.stringify({ secret, code }) }),
  totpDisable: (code: string): Promise<{ totp_enabled: false; token: string | null }> =>
    fetchJson('/auth/2fa/totp/disable', { method: 'POST', body: JSON.stringify({ code }) }),
  /** Invalidates existing backup codes and returns a fresh set once. Requires elevation. */
  regenerateBackupCodes: (): Promise<{ backup_codes: string[]; backup_codes_remaining: number }> =>
    fetchJson('/auth/2fa/backup-codes/regenerate', { method: 'POST', body: '{}' }),
  /** Redeems a sign-in challenge. Unauthenticated — there is no session yet. */
  twoFactorChallenge: (challenge_id: string, code: string) =>
    fetchJson('/auth/2fa/challenge', { method: 'POST', body: JSON.stringify({ challenge_id, code }) }),
  stepUp: (): Promise<StepUpChallenge> => fetchJson('/auth/step-up', { method: 'POST', body: '{}' }),
  stepUpVerify: (challenge_id: string, code: string): Promise<StepUpResult> =>
    fetchJson('/auth/step-up/verify', { method: 'POST', body: JSON.stringify({ challenge_id, code }) }),

  // OTP
  sendOtp: (channel: string, contact: string) =>
    fetchJson('/auth/send-otp', { method: 'POST', body: JSON.stringify({ channel, contact }) }),
  verifyOtp: (contact: string, code: string) =>
    fetchJson('/auth/verify-otp', { method: 'POST', body: JSON.stringify({ contact, code }) }),

  // Agencies
  searchAgencies: (q: string) => fetchJson(`/agencies/search?q=${encodeURIComponent(q)}`),
  /**
   * Legacy body-only apply — retired on the server (410). Prefer
   * {@link api.applyToAgencyBySlug}. Kept for AgentRegisterPage until Wave 1
   * signup lands.
   */
  applyToAgency: (agencyId: string, data: Record<string, string>) =>
    fetchJson('/agencies/apply', { method: 'POST', body: JSON.stringify({ agency_id: agencyId, ...data }) }),
  /** AGT-REC-004 — applicant-scoped outcome payload (404 if not caller’s). */
  getMyAgencyApplicationOutcome: (applicationId: string) =>
    fetchJson(`/users/me/agency-applications/${encodeURIComponent(applicationId)}`),
  acceptMyAgencyApplication: (applicationId: string) =>
    fetchJson(`/users/me/agency-applications/${encodeURIComponent(applicationId)}/accept`, {
      method: 'POST',
      body: '{}',
    }),
  declineMyAgencyApplication: (applicationId: string) =>
    fetchJson(`/users/me/agency-applications/${encodeURIComponent(applicationId)}/decline`, {
      method: 'POST',
      body: '{}',
    }),
  withdrawMyAgencyApplication: (applicationId: string) =>
    fetchJson(`/users/me/agency-applications/${encodeURIComponent(applicationId)}/withdraw`, {
      method: 'POST',
      body: '{}',
    }),
  /** AGN-MEM-005 — public card for apply / invite landing (id or slug). */
  getAgencyPublic: (slugOrId: string) =>
    fetchJson(`/agencies/${encodeURIComponent(slugOrId)}/public`),
  /** AGN-MEM-005 — POST /api/agencies/:slug/applications (auth or guest_signup). */
  applyToAgencyBySlug: (slug: string, body: Record<string, unknown>, init?: RequestInit) =>
    fetchJson(`/agencies/${encodeURIComponent(slug)}/applications`, {
      method: 'POST',
      body: JSON.stringify(body),
      ...init,
    }),
  /** AGN-MEM-005 — GET /api/invitations/:code (public resolve). */
  resolveInvitation: (code: string) =>
    fetchJson(`/invitations/${encodeURIComponent(code)}`),
  /** AGN-MEM-005 — POST /api/invitations/:code/accept (auth or guest_signup). */
  acceptInvitation: (code: string, body: Record<string, unknown>, init?: RequestInit) =>
    fetchJson(`/invitations/${encodeURIComponent(code)}/accept`, {
      method: 'POST',
      body: JSON.stringify(body),
      ...init,
    }),
  createAgency: (data: Record<string, unknown>) =>
    fetchJson('/agencies', { method: 'POST', body: JSON.stringify(data) }),
  getMyAgency: () => fetchJson('/agencies/my'),
  /** AGN-WLB-003 — load agency white-label copy config. */
  getAgencyWhiteLabelCopy: (): Promise<AgencyWhiteLabelCopyResponse> =>
    fetchJson('/agency/white-label/copy'),
  /** AGN-WLB-003 — update agency white-label copy fields. */
  updateAgencyWhiteLabelCopy: (data: { copy_fields: Partial<AgencySiteCopyFields> }): Promise<{ config: AgencySiteConfig }> =>
    fetchJson('/agency/white-label/copy', { method: 'PUT', body: JSON.stringify(data) }),
  /** AGN-WLB-003 — publish agency white-label copy. */
  publishAgencyWhiteLabelCopy: (): Promise<{ config: AgencySiteConfig }> =>
    fetchJson('/agency/white-label/copy/publish', { method: 'POST', body: '{}' }),
  /** AGN-ROU-001/002 — list agency lead routing rules. */
  listAgencyRoutingRules: (): Promise<AgencyRoutingRulesListResponse> =>
    fetchJson('/agency/routing/rules'),
  /** AGN-ROU-002 — fetch one routing rule by id. */
  getAgencyRoutingRule: (ruleId: string): Promise<AgencyRoutingRule> =>
    fetchJson(`/agency/routing/rules/${encodeURIComponent(ruleId)}`),
  /** AGN-ROU-002 — create a routing rule. */
  createAgencyRoutingRule: (data: Partial<AgencyRoutingRule>): Promise<AgencyRoutingRule> =>
    fetchJson('/agency/routing/rules', { method: 'POST', body: JSON.stringify(data) }),
  /** AGN-ROU-002 — update a routing rule. */
  updateAgencyRoutingRule: (ruleId: string, data: Partial<AgencyRoutingRule>): Promise<AgencyRoutingRule> =>
    fetchJson(`/agency/routing/rules/${encodeURIComponent(ruleId)}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  /** AGN-ROU-002 — delete a routing rule. */
  deleteAgencyRoutingRule: (ruleId: string): Promise<{ success: boolean }> =>
    fetchJson(`/agency/routing/rules/${encodeURIComponent(ruleId)}`, { method: 'DELETE' }),
  /** AGN-TPL-001 — list agency-authored message templates. */
  listAgencyMessageTemplates: (params?: {
    channel?: string
    category?: string
    status?: string
  }): Promise<AgencyMessageTemplatesListResponse> => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : ''
    return fetchJson(`/agency/templates${qs}`)
  },
  /** AGN-TPL-001 — fetch one agency template. */
  getAgencyMessageTemplate: (templateId: string): Promise<AgencyMessageTemplate> =>
    fetchJson(`/agency/templates/${encodeURIComponent(templateId)}`),
  /** AGN-TPL-001 — create an agency template draft. */
  createAgencyMessageTemplate: (data: Partial<AgencyMessageTemplate>): Promise<AgencyMessageTemplate> =>
    fetchJson('/agency/templates', { method: 'POST', body: JSON.stringify(data) }),
  /** AGN-TPL-001 — publish template to agents. */
  publishAgencyMessageTemplate: (templateId: string): Promise<AgencyMessageTemplate> =>
    fetchJson(`/agency/templates/${encodeURIComponent(templateId)}/publish`, { method: 'POST', body: '{}' }),
  /** AGN-TPL-002 — update an agency template. */
  updateAgencyMessageTemplate: (templateId: string, data: Partial<AgencyMessageTemplate>): Promise<AgencyMessageTemplate> =>
    fetchJson(`/agency/templates/${encodeURIComponent(templateId)}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  /** AGN-TPL-002 — delete an agency template. */
  deleteAgencyMessageTemplate: (templateId: string): Promise<{ success: boolean }> =>
    fetchJson(`/agency/templates/${encodeURIComponent(templateId)}`, { method: 'DELETE' }),
  /** AGN-TPL-002 — render a test preview. */
  renderAgencyMessageTemplate: (
    templateId: string,
    variables: Record<string, string>,
  ): Promise<AgencyMessageTemplateRenderResult> =>
    fetchJson(`/agency/templates/${encodeURIComponent(templateId)}/render`, {
      method: 'POST',
      body: JSON.stringify({ variables }),
    }),
  /** AGN-ROL-001 — capability-pack list with member counts + agency owner meta. */
  listAgencyCapabilityPacks: () => fetchJson('/agency/capability-packs'),
  /** AGN-ROL-002 — one pack's full capability matrix (domains) + members preview. */
  getAgencyCapabilityPack: (packId: string) =>
    fetchJson(`/agency/capability-packs/${encodeURIComponent(packId)}`),
  /**
   * AGN-ROL-001 — replace a member's capability-pack assignment. 202 when a
   * Finance grant needs a second-owner approval; 409 SECOND_OWNER_REQUIRED.
   */
  assignMemberCapabilityPacks: (
    userId: string,
    data: { packs: string[]; audit_reason?: string | null },
  ) =>
    fetchJson(`/agency/members/${encodeURIComponent(userId)}/capability-packs`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  /** AGN-DSH-002 — agency first-run onboarding checklist state. */
  getAgencyOnboardingState: (agencyId: string) =>
    fetchJson(`/agency/${encodeURIComponent(agencyId)}/onboarding-state?derive=1`),
  /** AGN-DSH-002 — patch onboarding state (checklist_delta / dismissed_forever). */
  patchAgencyOnboardingState: (
    agencyId: string,
    data: {
      step?: string
      path?: string | null
      checklist_delta?: Record<string, unknown>
      dismissed_forever?: boolean
    },
  ) =>
    fetchJson(`/agency/${encodeURIComponent(agencyId)}/onboarding-state`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  // H5 — audit log search.
  searchAuditLog: (
    filters: AuditLogSearchFilters = {},
  ): Promise<AuditLogSearchResponse> => {
    const q = new URLSearchParams()
    for (const [k, v] of Object.entries(filters)) {
      if (v === undefined || v === null || v === '') continue
      q.set(k, String(v))
    }
    const qs = q.toString()
    return fetchJson(`/audit/log${qs ? `?${qs}` : ''}`)
  },
  auditLogCsvPath: (filters: AuditLogSearchFilters = {}): string => {
    const q = new URLSearchParams()
    for (const [k, v] of Object.entries(filters)) {
      if (v === undefined || v === null || v === '') continue
      q.set(k, String(v))
    }
    const qs = q.toString()
    return `${API_BASE}/audit/log.csv${qs ? `?${qs}` : ''}`
  },

  // Issue 192b — self-serve data export (GDPR Art. 20 / UAE / KSA PDPL).
  requestDataExport: (): Promise<{ export: DataExportRecord }> =>
    fetchJson('/settings/data-export', { method: 'POST', body: '{}' }),
  listDataExports: (): Promise<{ exports: DataExportRecord[] }> =>
    fetchJson('/settings/data-export'),
  getDataExportStatus: (id: string): Promise<{ export: DataExportRecord }> =>
    fetchJson(`/settings/data-export/${encodeURIComponent(id)}/status`),
  /** Returns a browser-navigable URL (Authorization header must be sent — use `<a>` download or a fetch → blob flow). */
  dataExportDownloadPath: (id: string): string =>
    `${API_BASE}/settings/data-export/${encodeURIComponent(id)}/download`,

  // Issue 189 — WebAuthn / passkey factor.
  webauthnRegisterBegin: (): Promise<{ options: unknown }> =>
    fetchJson('/auth/webauthn/register/begin', { method: 'POST', body: '{}' }),
  webauthnRegisterComplete: (payload: {
    name: string
    response: unknown
  }): Promise<{ credential: PasskeyCredential }> =>
    fetchJson('/auth/webauthn/register/complete', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  webauthnAuthenticateBegin: (identifier?: string): Promise<{ options: unknown; session_key: string | null }> =>
    fetchJson('/auth/webauthn/authenticate/begin', {
      method: 'POST',
      body: JSON.stringify(identifier ? { identifier } : {}),
    }),
  webauthnAuthenticateComplete: (payload: {
    response: unknown
    challenge?: string
  }): Promise<{ token: string; agent?: unknown; factor_used: 'passkey' }> =>
    fetchJson('/auth/webauthn/authenticate/complete', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  listPasskeys: (): Promise<{ credentials: PasskeyCredential[] }> =>
    fetchJson('/auth/webauthn/credentials'),
  revokePasskey: (id: string) =>
    fetchJson(`/auth/webauthn/credentials/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  renamePasskey: (id: string, name: string) =>
    fetchJson(`/auth/webauthn/credentials/${encodeURIComponent(id)}/rename`, {
      method: 'POST',
      body: JSON.stringify({ name }),
    }),
  // Issue 192a — Personal Access Tokens.
  listApiTokens: (): Promise<{ tokens: ApiTokenRecord[] }> =>
    fetchJson('/settings/api-tokens'),
  createApiToken: (payload: {
    name: string
    scopes?: string[]
    expires_at?: string | null
  }): Promise<ApiTokenCreateResponse> =>
    fetchJson('/settings/api-tokens', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  revokeApiToken: (id: string) =>
    fetchJson(`/settings/api-tokens/${encodeURIComponent(id)}`, { method: 'DELETE' }),

  getAgency: (id: string) => fetchJson(`/agencies/${id}`),
  updateAgency: (id: string, data: Record<string, unknown>) =>
    fetchJson(`/agencies/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  getAgencyBranding: (id: string): Promise<AgencyBrandingResponse> =>
    fetchJson(`/agencies/${encodeURIComponent(id)}/branding`),
  updateAgencyBranding: (id: string, data: AgencyBrandingUpdate): Promise<AgencyBrandingResponse> =>
    fetchJson(`/agencies/${encodeURIComponent(id)}/branding`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  resetAgencyBranding: (id: string): Promise<AgencyBrandingResponse> =>
    fetchJson(`/agencies/${encodeURIComponent(id)}/branding/reset`, {
      method: 'POST',
      body: '{}',
    }),
  getAgencyPublicProfileSettings: (id: string): Promise<AgencyPublicProfileSettingsResponse> =>
    fetchJson(`/agencies/${encodeURIComponent(id)}/public-profile-settings`),
  updateAgencyPublicProfileSettings: (
    id: string,
    data: AgencyPublicProfileUpdate,
  ): Promise<AgencyPublicProfileSettingsResponse> =>
    fetchJson(`/agencies/${encodeURIComponent(id)}/public-profile-settings`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  // Issue 190 — admin-enforced 2FA policy.
  getAgencyMfaPolicy: (
    agencyId: string,
  ): Promise<{ policy: AgencyMfaPolicy }> =>
    fetchJson(`/agencies/${agencyId}/security/mfa-policy`),
  updateAgencyMfaPolicy: (
    agencyId: string,
    patch: Partial<
      Pick<
        AgencyMfaPolicy,
        | 'required'
        | 'grace_days'
        | 'allowed_factors'
        | 'scoped_roles'
        | 'bypass_user_ids'
        | 'conditional_rules'
        | 'enforce_on_next_login'
      >
    >,
  ): Promise<{ policy: AgencyMfaPolicy }> =>
    fetchJson(`/agencies/${agencyId}/security/mfa-policy`, {
      method: 'PUT',
      body: JSON.stringify(patch),
    }),
  /** AGN-MEM-002 — list agency applications (raw array today; client normalizes). */
  listAgencyApplications: (agencyId: string) =>
    fetchJson(`/agencies/${agencyId}/applications`),
  /** AGN-MEM-002 / 002b — approve creates membership; requires role + affiliation_mode. */
  approveAgencyApplication: (
    agencyId: string,
    applicationId: string,
    data: { role: string; affiliation_mode: string },
  ) =>
    fetchJson(`/agencies/${agencyId}/applications/${applicationId}/approve`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  /** AGN-MEM-002 / 002b — reject; reason forwarded for Agent 4 outcome deep-link. */
  rejectAgencyApplication: (
    agencyId: string,
    applicationId: string,
    data: { reason: string },
  ) =>
    fetchJson(`/agencies/${agencyId}/applications/${applicationId}/reject`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  /**
   * AGN-MEM-002b — audited contact reveal. Must succeed (2xx) before the UI unmasks.
   * Writes public.audit_log server-side.
   */
  revealAgencyApplicationContact: (
    agencyId: string,
    applicationId: string,
    data?: { field?: 'contact' | 'email' | 'phone' },
  ) =>
    fetchJson(`/agencies/${agencyId}/applications/${applicationId}/reveal-contact`, {
      method: 'POST',
      body: JSON.stringify(data || { field: 'contact' }),
    }),
  /**
   * AGN-MEM-002 — server CSV export (same filters as the queue: status, within, q).
   * Authenticated fetch + blob download — never builds CSV client-side.
   */
  exportAgencyApplicationsCsv: async (
    agencyId: string,
    params: { status?: string; within?: string; q?: string } = {},
  ) => {
    const qs = new URLSearchParams()
    if (params.status) qs.set('status', params.status)
    if (params.within) qs.set('within', params.within)
    if (params.q) qs.set('q', params.q)
    const path = `/agencies/${agencyId}/applications.csv${qs.toString() ? `?${qs}` : ''}`
    const url = `${API_BASE}${path}`
    const downloadHeaders = headers()
    delete downloadHeaders['Content-Type']
    const res = await fetch(url, { headers: downloadHeaders })
    if (!res.ok) {
      const bodyText = await res.text()
      let message = `Request failed (${res.status})`
      try {
        const parsed = JSON.parse(bodyText) as { error?: string }
        if (parsed?.error) message = parsed.error
      } catch {
        /* non-JSON error body */
      }
      const error = new Error(message) as Error & { status?: number }
      error.status = res.status
      throw error
    }
    const blob = await res.blob()
    const disposition = res.headers.get('Content-Disposition') || ''
    const match = /filename="([^"]+)"/i.exec(disposition)
    const filename =
      match?.[1]
      || `agency-applications-${params.status || 'all'}-${params.within || 'all'}.csv`
    const objectUrl = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = objectUrl
    a.download = filename
    a.click()
    URL.revokeObjectURL(objectUrl)
    return { ok: true as const, filename }
  },
  inviteMember: (agencyId: string, data: Record<string, unknown>) =>
    fetchJson(`/agencies/${agencyId}/members`, { method: 'POST', body: JSON.stringify(data) }),
  updateMember: (agencyId: string, memberId: string, data: Record<string, unknown>) =>
    fetchJson(`/agencies/${agencyId}/members/${memberId}`, { method: 'PUT', body: JSON.stringify(data) }),
  removeMember: (agencyId: string, memberId: string) =>
    fetchJson(`/agencies/${agencyId}/members/${memberId}`, { method: 'DELETE' }),
  endAgencyMembership: (agencyId: string, memberId: string, data?: Record<string, unknown>) =>
    fetchJson(`/agencies/${agencyId}/members/${memberId}/end`, { method: 'POST', body: JSON.stringify(data || {}) }),
  // AGN-MEM-008 — pause / resume a member (temporary suspension).
  pauseMember: (agencyId: string, memberId: string, reason: string) =>
    fetchJson(`/agencies/${agencyId}/members/${memberId}/pause`, { method: 'POST', body: JSON.stringify({ reason }) }),
  resumeMember: (agencyId: string, memberId: string) =>
    fetchJson(`/agencies/${agencyId}/members/${memberId}/resume`, { method: 'POST', body: JSON.stringify({}) }),
  getTiedListings: (agencyId: string, memberId: string) =>
    fetchJson(`/agencies/${agencyId}/members/${memberId}/tied-listings`),
  reassignAgencyListing: (agencyId: string, propertyId: string, data: Record<string, unknown>) =>
    fetchJson(`/agencies/${agencyId}/listings/${propertyId}/reassign`, { method: 'POST', body: JSON.stringify(data) }),

  // WF-31 ownership transfer (BE-BLOCKER-31) — routes under /agencies/:id/ownership-transfer.
  /** Current transfer + eligibility for the caller's agency (owner + involved parties). */
  getOwnershipTransferState: (agencyId: string): Promise<OwnershipTransferStateResponse> =>
    fetchJson(`/agencies/${encodeURIComponent(agencyId)}/ownership-transfer/state`),
  /** Sends a 6-digit OTP to the caller's account email. Rate-limited server-side. */
  sendOwnershipTransferOtp: (agencyId: string): Promise<OwnershipTransferOtpResult> =>
    fetchJson(`/agencies/${encodeURIComponent(agencyId)}/ownership-transfer/otp/send`, {
      method: 'POST',
      body: '{}',
    }),
  /** Owner initiates a transfer (requires elevation + OTP + typed name). */
  initiateOwnershipTransfer: (
    agencyId: string,
    body: { target_user_id: string; rationale: string; otp_code: string; typed_agency_name: string },
  ): Promise<OwnershipTransferMutationResult> =>
    fetchJson(`/agencies/${encodeURIComponent(agencyId)}/ownership-transfer/initiate`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  /** Target accepts a pending transfer (requires elevation + OTP + typed name). */
  acceptOwnershipTransfer: (
    agencyId: string,
    transferId: string,
    body: { otp_code: string; typed_agency_name: string },
  ): Promise<OwnershipTransferMutationResult> =>
    fetchJson(
      `/agencies/${encodeURIComponent(agencyId)}/ownership-transfer/${encodeURIComponent(transferId)}/accept`,
      { method: 'POST', body: JSON.stringify(body) },
    ),
  /** Target declines a pending transfer with a required reason (no challenge). */
  declineOwnershipTransfer: (
    agencyId: string,
    transferId: string,
    body: { reason: string },
  ): Promise<OwnershipTransferMutationResult> =>
    fetchJson(
      `/agencies/${encodeURIComponent(agencyId)}/ownership-transfer/${encodeURIComponent(transferId)}/decline`,
      { method: 'POST', body: JSON.stringify(body) },
    ),
  /** Initiator cancels a pending transfer. */
  cancelOwnershipTransfer: (
    agencyId: string,
    transferId: string,
  ): Promise<OwnershipTransferMutationResult> =>
    fetchJson(
      `/agencies/${encodeURIComponent(agencyId)}/ownership-transfer/${encodeURIComponent(transferId)}/cancel`,
      { method: 'POST', body: '{}' },
    ),
  /** Party acknowledges a resolved transfer so the screen resets on next load. */
  acknowledgeOwnershipTransfer: (
    agencyId: string,
    transferId: string,
  ): Promise<OwnershipTransferMutationResult> =>
    fetchJson(
      `/agencies/${encodeURIComponent(agencyId)}/ownership-transfer/${encodeURIComponent(transferId)}/acknowledge`,
      { method: 'POST', body: '{}' },
    ),
  /** Former owner reverses an executed transfer within the 30-day window. */
  reverseOwnershipTransfer: (
    agencyId: string,
    transferId: string,
    body: { otp_code: string; typed_agency_name: string },
  ): Promise<OwnershipTransferMutationResult> =>
    fetchJson(
      `/agencies/${encodeURIComponent(agencyId)}/ownership-transfer/${encodeURIComponent(transferId)}/reverse`,
      { method: 'POST', body: JSON.stringify(body) },
    ),

  getAgencyDeletionState: (agencyId: string): Promise<AgencyDeletionStateResponse> =>
    fetchJson(`/agencies/${encodeURIComponent(agencyId)}/deletion/state`),
  regenerateAgencyDeletionWord: (agencyId: string): Promise<{ word: string; expires_at: string }> =>
    fetchJson(`/agencies/${encodeURIComponent(agencyId)}/deletion/regenerate-word`, {
      method: 'POST',
      body: '{}',
    }),
  initiateAgencyDeletion: (
    agencyId: string,
    body: { word: string; typed_agency_name: string; reason: string; notes?: string },
  ): Promise<AgencyDeletionScheduleResponse> =>
    fetchJson(`/agencies/${encodeURIComponent(agencyId)}/deletion/initiate`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  cancelAgencyDeletion: (agencyId: string): Promise<{ ok: boolean; cancelled_at: string }> =>
    fetchJson(`/agencies/${encodeURIComponent(agencyId)}/deletion/cancel`, {
      method: 'POST',
      body: '{}',
    }),

  // Property taxonomy registry (GET /api/taxonomy). Methods on `api`, not named
  // exports — hand-listed vi.mock('@/api/client') factories break when a new
  // named export appears.
  getTaxonomy: () => fetchJson('/taxonomy'),
  suggestTaxonomy: (body: {
    kind: 'type' | 'attribute'
    suggested_label: string
    context_code?: string | null
    listing_id?: string | null
  }) =>
    fetchJson('/taxonomy/suggestions', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  // Properties
  getProperties: (params?: Record<string, string>) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : ''
    return fetchJson(`/properties${qs}`)
  },
  getProperty: (id: string) => fetchJson(`/properties/${id}`),
  /**
   * Piece 2 — the union of permit requirements for a situs + chosen display
   * markets (situs rule ∪ each display market's rule). Drives the composer's
   * "distribute in other countries" required-field expansion. `enabled_markets`
   * populates the picker.
   */
  getListingRequirements: (params: {
    situs?: string
    markets?: string
  }): Promise<{
    required: { key: string; label: string; scope: 'situs' | 'display'; market?: string }[]
    enabled_markets: string[]
  }> => {
    const qs = '?' + new URLSearchParams(params as Record<string, string>).toString()
    return fetchJson(`/listings/requirements${qs}`)
  },
  getCanonicalPropertyView: (
    id: string,
  ): Promise<{ canonical_view: CanonicalPropertyView | null }> =>
    fetchJson(`/properties/${id}/canonical`),
  createCanonicalPrimaryDispute: (
    id: string,
    data: CanonicalPrimaryDisputeInput,
  ): Promise<{ dispute: CanonicalPrimaryDispute }> =>
    fetchJson(`/properties/${id}/canonical/disputes`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  createProperty: (data: Record<string, unknown>) =>
    fetchJson('/properties', { method: 'POST', body: JSON.stringify(data) }),
  /** AGT-PUB-003 / BE-BLOCKER-11 — portal submission ledger for a listing or job. */
  getPublishingTracker: (params?: Record<string, string>) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : ''
    return fetchJson(`/publishing/tracker${qs}`)
  },
  /** AGT-PUB-006 KPI aggregate — same filter keys as the list endpoint. */
  getPublishingTrackerSummary: (params?: Record<string, string>) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : ''
    return fetchJson(`/publishing/tracker/summary${qs}`)
  },
  getPublishingJob: (jobId: string): Promise<PublishingJobPayload> =>
    fetchJson(`/publishing/jobs/${encodeURIComponent(jobId)}`),
  retryPublishingDestination: (
    jobId: string,
    destinationId: string,
  ): Promise<PublishingDestinationRetryResult> =>
    fetchJson(
      `/publishing/jobs/${encodeURIComponent(jobId)}/destinations/${encodeURIComponent(destinationId)}/retry`,
      { method: 'POST', body: '{}' },
    ),
  retryAllPublishingDestinations: (
    jobId: string,
    errorClassesToRetry: string[] = ['PORTAL_DOWN', 'UNKNOWN_ERROR'],
  ): Promise<PublishingJobRetryAllResult> =>
    fetchJson(`/publishing/jobs/${encodeURIComponent(jobId)}/retry-all`, {
      method: 'POST',
      body: JSON.stringify({ error_classes_to_retry: errorClassesToRetry }),
    }),
  uploadMedia: async (files: File[]) => {
    const form = new FormData()
    files.forEach((f) => form.append('files', f))
    const token = localStorage.getItem('fi_token') || localStorage.getItem('sa_token')
    const uploadHeaders: Record<string, string> = {
      'X-Wingcaster-Env': readWingcasterEnvHeader(),
    }
    if (token) uploadHeaders.Authorization = `Bearer ${token}`
    const res = await fetch(`${API_BASE}/uploads`, {
      method: 'POST',
      headers: uploadHeaders,
      body: form,
    })
    const bodyText = await res.text()
    const parsed = (() => {
      try {
        return bodyText ? JSON.parse(bodyText) : null
      } catch {
        return null
      }
    })()
    if (!res.ok) {
      throw new Error(parsed?.error || `Upload failed (${res.status})`)
    }
    if (!parsed) {
      throw new Error('Upload endpoint returned a non-JSON response')
    }
    return parsed as { items: Array<{ url: string; media_type: 'image' | 'video'; filename: string }> }
  },
  updateProperty: (id: string, data: Record<string, unknown>) =>
    fetchJson(`/properties/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteProperty: (id: string) =>
    fetchJson(`/properties/${id}`, { method: 'DELETE' }),

  // AGT-LST-013 — two-party property disposition case
  // Affiliation audit F-9: opening a case. Either party may, while the listing is
  // still held on the agency's behalf; the body carries the opener's own proposal.
  createPropertyDispositionCase: (
    propertyId: string,
    input: { disposition: PropertyDisposition; notes?: string | null },
  ): Promise<PropertyDispositionResponse> =>
    fetchJson(`/properties/${propertyId}/disposition-case`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  getPropertyDispositionCase: (propertyId: string): Promise<PropertyDispositionResponse> =>
    fetchJson(`/properties/${propertyId}/disposition-case`),
  updatePropertyDispositionDecision: (
    propertyId: string,
    input: { disposition: PropertyDisposition; notes?: string | null },
  ): Promise<PropertyDispositionResponse> =>
    fetchJson(`/properties/${propertyId}/disposition-case/decision`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    }),
  resolvePropertyDispositionCase: (propertyId: string): Promise<PropertyDispositionResponse> =>
    fetchJson(`/properties/${propertyId}/disposition-case/resolve`, {
      method: 'POST',
      body: '{}',
    }),

  // AGT-LST-010 — buyer offers on a listing
  listBuyerOffers: (propertyId: string) =>
    fetchJson(`/properties/${propertyId}/buyer-offers`) as Promise<{ offers: BuyerOffer[] }>,
  createBuyerOffer: (propertyId: string, data: BuyerOfferInput) =>
    fetchJson(`/properties/${propertyId}/buyer-offers`, {
      method: 'POST',
      body: JSON.stringify(data),
    }) as Promise<BuyerOffer>,
  updateBuyerOffer: (offerId: string, data: Partial<BuyerOfferInput>) =>
    fetchJson(`/buyer-offers/${offerId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }) as Promise<BuyerOffer>,
  deleteBuyerOffer: (offerId: string) =>
    fetchJson(`/buyer-offers/${offerId}`, { method: 'DELETE' }) as Promise<{ success: boolean }>,

  // AGT-LST-015 — seller performance report
  getSellerReport: (propertyId: string) =>
    fetchJson(`/properties/${propertyId}/seller-report`) as Promise<SellerReportResponse>,
  updateSellerReport: (propertyId: string, data: {
    state_of_play?: string | null
    agent_summary?: string | null
    show_offer_amounts?: boolean
    show_full_address?: boolean
    /** @deprecated migration 812 — never rendered. */
    layout_template?: SellerReportLayout
    composition_id?: string | null
    /** Hide-only: naming a required section is rejected with 400. */
    hidden_sections?: string[]
  }) =>
    fetchJson(`/properties/${propertyId}/seller-report`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }) as Promise<{
      report: SellerReportConfig
      payload: SellerReportPayload
      available_sections?: VendorReportSectionDefinition[]
      available_compositions?: VendorReportComposition[]
    }>,
  createSellerReportShareToken: (propertyId: string, data?: { recipient_email?: string | null }) =>
    fetchJson(`/properties/${propertyId}/seller-report/share-tokens`, {
      method: 'POST',
      body: JSON.stringify(data || {}),
    }) as Promise<SellerReportShareToken>,
  revokeSellerReportShareToken: (propertyId: string, tokenId: string) =>
    fetchJson(`/properties/${propertyId}/seller-report/share-tokens/${tokenId}`, {
      method: 'DELETE',
    }) as Promise<SellerReportShareToken>,
  getPublicSellerReport: (shareToken: string) =>
    fetchJson(`/public/seller-reports/${shareToken}`) as Promise<{
      status: SellerReportStatus
      payload: SellerReportPayload
    }>,

  // Migration 812 — agency-wide vendor report templates (owner/admin only).
  // An ordinary agent reads the list off getSellerReport instead.
  listVendorReportCompositions: () =>
    fetchJson('/agency/vendor-report-compositions') as Promise<{
      compositions: VendorReportComposition[]
      available_sections: VendorReportSectionDefinition[]
    }>,
  createVendorReportComposition: (data: {
    name: string
    is_default?: boolean
    sections?: Array<{ id: string; visible?: boolean; options?: Record<string, unknown> }>
  }) =>
    fetchJson('/agency/vendor-report-compositions', {
      method: 'POST',
      body: JSON.stringify(data),
    }) as Promise<VendorReportComposition>,
  updateVendorReportComposition: (id: string, data: {
    name?: string
    is_default?: boolean
    sections?: Array<{ id: string; visible?: boolean; options?: Record<string, unknown> }>
  }) =>
    fetchJson(`/agency/vendor-report-compositions/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }) as Promise<VendorReportComposition>,
  deleteVendorReportComposition: (id: string) =>
    fetchJson(`/agency/vendor-report-compositions/${id}`, {
      method: 'DELETE',
    }) as Promise<{ deleted: boolean; id: string }>,

  // Agents
  getAgents: () => fetchJson('/agents'),
  getAgent: (id: string) => fetchJson(`/agents/${id}`),
  getAgentTransactions: (id: string) => fetchJson(`/agents/${id}/transactions`),
  getAgentReviews: (id: string) => fetchJson(`/agents/${id}/reviews`),
  createReview: (agentId: string, data: Record<string, unknown>) =>
    fetchJson(`/agents/${agentId}/reviews`, { method: 'POST', body: JSON.stringify(data) }),
  getMyAgentReviews: (): Promise<ManagedAgentReviewsResponse> =>
    fetchJson('/agent/reviews'),
  respondToAgentReview: (reviewId: string, body: string): Promise<ManagedAgentReview> =>
    fetchJson(`/agent/reviews/${reviewId}/response`, {
      method: 'PATCH',
      body: JSON.stringify({ body }),
    }),
  flagAgentReview: (
    reviewId: string,
    input: { reason: ReviewFlagReason; details?: string | null },
  ): Promise<ManagedAgentReview> =>
    fetchJson(`/agent/reviews/${reviewId}/flag`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  // Zillow-style features
  getPriceHistory: (id: string) => fetchJson(`/properties/${id}/price-history`),
  getComps: (id: string) => fetchJson(`/properties/${id}/comps`),
  getZestimate: (id: string) => fetchJson(`/properties/${id}/zestimate`),
  getNeighborhoodStats: (name: string) => fetchJson(`/neighborhoods/${name}/stats`),
  getNeighborhoods: () => fetchJson('/neighborhoods'),

  // Inquiries
  createInquiry: (data: Record<string, string>) =>
    fetchJson('/inquiries', { method: 'POST', body: JSON.stringify(data) }),
  getInquiries: (params?: Record<string, string>) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : ''
    return fetchJson(`/inquiries${qs}`)
  },
  updateInquiry: (id: string, data: Record<string, unknown>) =>
    fetchJson(`/inquiries/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  getInquiryTimeline: (id: string) => fetchJson(`/inquiries/${id}/timeline`),
  getViewings: () => fetchJson('/viewings'),
  createViewing: (data: Record<string, unknown>) =>
    fetchJson('/viewings', { method: 'POST', body: JSON.stringify(data) }),
  updateViewing: (id: string, data: Record<string, unknown>) =>
    fetchJson(`/viewings/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),

  // Calendar & Diary (PR1 wave 1) — types in @/api/calendar
  getCalendarEvents: (params: CalendarEventListParams) => {
    const qs = new URLSearchParams({ from: params.from, to: params.to })
    if (params.kinds?.length) qs.set('kinds', params.kinds.join(','))
    return fetchJson(`/calendar/events?${qs.toString()}`) as Promise<{
      events: CalendarEvent[]
    }>
  },
  createCalendarEvent: (data: CalendarEventInput) =>
    fetchJson('/calendar/events', { method: 'POST', body: JSON.stringify(data) }) as Promise<{
      event: CalendarEvent
    }>,
  updateCalendarEvent: (id: string, data: CalendarEventPatch) =>
    fetchJson(`/calendar/events/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }) as Promise<{ event: CalendarEvent }>,
  getCalendarEvent: (id: string) =>
    fetchJson(`/calendar/events/${encodeURIComponent(id)}`) as Promise<{
      event: CalendarEvent
    }>,
  reassignCalendarEvent: (id: string, userId: string) =>
    fetchJson(`/calendar/events/${encodeURIComponent(id)}/reassign`, {
      method: 'POST',
      body: JSON.stringify({ user_id: userId }),
    }) as Promise<{ event: CalendarEvent }>,
  delegateCalendarEvent: (id: string, userId: string) =>
    fetchJson(`/calendar/events/${encodeURIComponent(id)}/delegate`, {
      method: 'POST',
      body: JSON.stringify({ user_id: userId }),
    }) as Promise<{ event: CalendarEvent }>,
  /**
   * Wave 6b — cancel ONE occurrence of a series. `id` is the series id (the
   * derived occurrence id 404s). 200 is always success (idempotent re-cancel
   * is a no-op); the body shape is not part of the contract we consume.
   */
  cancelCalendarOccurrence: (id: string, occurrenceStart: string) =>
    fetchJson(`/calendar/events/${encodeURIComponent(id)}/cancel-occurrence`, {
      method: 'POST',
      body: JSON.stringify({ occurrence_start: occurrenceStart }),
    }) as Promise<unknown>,
  getCalendarConflicts: (params: {
    from: string
    to: string
    excludeEventId?: string
  }) => {
    const qs = new URLSearchParams({ from: params.from, to: params.to })
    if (params.excludeEventId) qs.set('exclude_event_id', params.excludeEventId)
    return fetchJson(`/calendar/conflicts?${qs.toString()}`) as Promise<{
      ok: boolean
      code?: string
      conflicting?: { id: string; title: string; starts_at: string }
    }>
  },

  // Calendar & Diary (PR1 wave 5) — team calendar; types in @/api/calendar
  getCalendarTeamEvents: (params: { from: string; to: string }) => {
    const qs = new URLSearchParams({ from: params.from, to: params.to })
    return fetchJson(`/calendar/team?${qs.toString()}`) as Promise<CalendarTeamEventsResponse>
  },

  // Calendar & Diary (PR1 wave 4) — settings + autoresponse templates; types in @/api/calendar
  getCalendarSettings: () =>
    fetchJson('/calendar/settings') as Promise<{ settings: CalendarSettings }>,
  putCalendarSettings: (data: CalendarSettingsInput) =>
    fetchJson('/calendar/settings', { method: 'PUT', body: JSON.stringify(data) }) as Promise<{
      settings: CalendarSettings
    }>,
  getAutoresponseTemplates: () =>
    fetchJson('/calendar/autoresponse-templates') as Promise<{
      templates: AutoresponseTemplate[]
    }>,
  createAutoresponseTemplate: (data: AutoresponseTemplateInput) =>
    fetchJson('/calendar/autoresponse-templates', {
      method: 'POST',
      body: JSON.stringify(data),
    }) as Promise<{ template: AutoresponseTemplate }>,
  updateAutoresponseTemplate: (id: string, data: AutoresponseTemplatePatch) =>
    fetchJson(`/calendar/autoresponse-templates/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }) as Promise<{ template: AutoresponseTemplate }>,
  deleteAutoresponseTemplate: (id: string) =>
    fetchJson(`/calendar/autoresponse-templates/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    }) as Promise<{ deleted: boolean }>,

  // Dashboard
  getDashboardStats: () => fetchJson('/dashboard/stats'),
  getDashboardAnalytics: () => fetchJson('/dashboard/analytics'),
  getDashboardOperations: () => fetchJson('/dashboard/operations'),

  // Wave-8 Pro prefs / layout (AGT-DSH-002 / AGT-LST-002 / AGT-SET-002)
  getDashboardLayout: (tenantId?: string) => {
    const qs = tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : ''
    return fetchJson(`/users/me/dashboard-layout${qs}`) as Promise<{
      layout: Array<{ i: string; x: number; y: number; w: number; h: number }>
      density: 'compact' | 'comfortable' | 'spacious'
      updated_at: string | null
      tenant_id: string
    }>
  },
  patchDashboardLayout: (body: {
    tenant_id?: string
    layout?: Array<{ i: string; x: number; y: number; w: number; h: number }>
    density?: 'compact' | 'comfortable' | 'spacious'
  }) =>
    fetchJson('/users/me/dashboard-layout', {
      method: 'PATCH',
      body: JSON.stringify(body),
    }) as Promise<{
      layout: Array<{ i: string; x: number; y: number; w: number; h: number }>
      density: 'compact' | 'comfortable' | 'spacious'
      updated_at: string | null
      tenant_id: string
    }>,
  getListPrefs: (tenantId?: string) => {
    const qs = tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : ''
    return fetchJson(`/users/me/list-prefs${qs}`) as Promise<{
      listings: Record<string, unknown>
      updated_at: string | null
      tenant_id: string
    }>
  },
  patchListPrefs: (listings: Record<string, unknown>, tenantId?: string) =>
    fetchJson('/users/me/list-prefs', {
      method: 'PATCH',
      body: JSON.stringify({ listings, tenant_id: tenantId }),
    }) as Promise<{
      listings: Record<string, unknown>
      updated_at: string | null
      tenant_id: string
    }>,
  getProNudge: () =>
    fetchJson('/users/me/pro-nudge') as Promise<{
      eligible: boolean
      listing_count: number
      days_since_signup: number
      pro_nudge_dismissed_at: string | null
      days_since_dismiss: number | null
    }>,
  dismissProNudge: () =>
    fetchJson('/users/me/pro-nudge/dismiss', { method: 'POST', body: '{}' }) as Promise<{
      pro_nudge_dismissed_at: string
    }>,
  getSavedViews: (tenantId: string, resource = 'listings') =>
    fetchJson(
      `/tenants/${encodeURIComponent(tenantId)}/saved-views?resource=${encodeURIComponent(resource)}`,
    ) as Promise<{ views: Array<Record<string, unknown>> }>,
  createSavedView: (tenantId: string, body: Record<string, unknown>) =>
    fetchJson(`/tenants/${encodeURIComponent(tenantId)}/saved-views`, {
      method: 'POST',
      body: JSON.stringify(body),
    }) as Promise<Record<string, unknown>>,
  updateSavedView: (tenantId: string, viewId: string, body: Record<string, unknown>) =>
    fetchJson(`/tenants/${encodeURIComponent(tenantId)}/saved-views/${encodeURIComponent(viewId)}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }) as Promise<Record<string, unknown>>,
  deleteSavedView: (tenantId: string, viewId: string) =>
    fetchJson(`/tenants/${encodeURIComponent(tenantId)}/saved-views/${encodeURIComponent(viewId)}`, {
      method: 'DELETE',
    }) as Promise<{ success: boolean }>,
  bulkArchiveProperties: (ids: string[]) =>
    fetchJson('/properties/bulk/archive', {
      method: 'POST',
      body: JSON.stringify({ ids }),
    }) as Promise<{ updated: string[]; missing: string[] }>,
  bulkPublishProperties: (ids: string[], channels: string[] = []) =>
    fetchJson('/properties/bulk/publish', {
      method: 'POST',
      body: JSON.stringify({ ids, channels }),
    }) as Promise<{ updated: string[]; missing: string[]; channels: string[] }>,
  bulkDeleteProperties: (ids: string[], confirmed_phrase: string) =>
    fetchJson('/properties/bulk', {
      method: 'DELETE',
      body: JSON.stringify({ ids, confirmed_phrase }),
    }) as Promise<{ deleted: string[]; missing: string[] }>,
  bulkExportProperties: (ids: string[] | null = null) =>
    fetchJson('/properties/bulk/export', {
      method: 'POST',
      body: JSON.stringify({ ids, format: 'csv' }),
    }) as Promise<{
      job_id: string
      status: string
      csv: string
      filename: string
      row_count: number
    }>,
  bulkPriceAdjustProperties: (ids: string[], mode: 'fixed' | 'percent', value: number) =>
    fetchJson('/properties/bulk/price-adjust', {
      method: 'POST',
      body: JSON.stringify({ ids, mode, value }),
    }) as Promise<{ updated: Array<{ id: string; price: number }>; missing: string[] }>,
  getPropertyAnalytics: (id: string) => fetchJson(`/properties/${id}/analytics`),
  trackPropertyEvent: (id: string, data: Record<string, unknown>) =>
    fetchJson(`/properties/${id}/events`, { method: 'POST', body: JSON.stringify(data) }),
  getListingNotes: (id: string) => fetchJson(`/properties/${id}/notes`),
  createListingNote: (id: string, data: { body: string; visibility?: string }) =>
    fetchJson(`/properties/${id}/notes`, { method: 'POST', body: JSON.stringify(data) }),
  deleteListingNote: (id: string, noteId: string) =>
    fetchJson(`/properties/${id}/notes/${noteId}`, { method: 'DELETE' }),
  getListingReport: (id: string) => fetchJson(`/properties/${id}/report`),

  // AGT-CMP-005 — saved searches (audience source)
  getSavedSearches: () => fetchJson('/saved-searches') as Promise<{ saved_searches: SavedSearch[] }>,
  createSavedSearch: (name: string, filters: Record<string, unknown>) =>
    fetchJson('/saved-searches', {
      method: 'POST',
      body: JSON.stringify({
        name,
        filters,
        alert_enabled: true,
        alert_channel: 'inapp',
        alert_frequency: 'daily',
      }),
    }),
  createSavedSearchWithAlerts: (data: SavedSearchInput) =>
    fetchJson('/saved-searches', { method: 'POST', body: JSON.stringify(data) }) as Promise<SavedSearch>,
  updateSavedSearch: (id: string, data: Partial<SavedSearchInput>) =>
    fetchJson(`/saved-searches/${id}`, { method: 'PATCH', body: JSON.stringify(data) }) as Promise<SavedSearch>,
  runSavedSearchAlerts: () => fetchJson('/saved-searches/run-alerts', { method: 'POST', body: '{}' }) as Promise<{
    ran_at: string
    searches_processed: number
    total_matches: number
    results: Array<Record<string, unknown>>
  }>,
  deleteSavedSearch: (id: string) =>
    fetchJson(`/saved-searches/${id}`, { method: 'DELETE' }),

  // Consumer automation / notifications
  getNotifications: (params?: Record<string, string>) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : ''
    return fetchJson(`/notifications${qs}`)
  },
  markNotificationRead: (id: string) => fetchJson(`/notifications/${id}/read`, { method: 'POST', body: '{}' }),
  getMyInboxNotifications: (): Promise<InboxNotificationsResponse> =>
    fetchJson('/auth/me/notifications'),
  markMyInboxNotificationRead: (id: string) =>
    fetchJson(`/auth/me/notifications/${id}/read`, { method: 'POST', body: '{}' }),
  markMyInboxNotificationsAllRead: () =>
    fetchJson('/auth/me/notifications/mark-all-read', { method: 'POST', body: '{}' }),
  getNotificationPrefs: () => fetchJson('/notification-preferences'),
  updateNotificationPrefs: (data: Record<string, unknown>) =>
    fetchJson('/notification-preferences', { method: 'PATCH', body: JSON.stringify(data) }),
  runConsumerAutomation: (options?: { scope?: 'self' | 'all'; force_alerts?: boolean }) =>
    fetchJson('/automation/consumer/run', {
      method: 'POST',
      body: JSON.stringify({
        scope: options?.scope,
        force_alerts: options?.force_alerts,
      }),
    }),
  getConsumerAutomationMetrics: () => fetchJson('/automation/consumer/metrics'),
  getViewing: (id: string) => fetchJson(`/viewings/${id}`),
  retryNotification: (id: string) => fetchJson(`/notifications/${id}/retry`, { method: 'POST', body: '{}' }),
  getAdminNotificationDeadLetter: (
    params?: { channel?: string; q?: string; include_ignored?: boolean; limit?: number },
  ): Promise<NotificationDeadLetterResponse> => {
    const qs = new URLSearchParams()
    if (params?.channel) qs.set('channel', params.channel)
    if (params?.q) qs.set('q', params.q)
    if (params?.include_ignored) qs.set('include_ignored', 'true')
    if (params?.limit) qs.set('limit', String(params.limit))
    const suffix = qs.toString() ? `?${qs.toString()}` : ''
    return fetchJson(`/admin/notifications/dead-letter${suffix}`)
  },
  retryAdminNotificationDeadLetter: (id: string): Promise<{ item: NotificationDeadLetterItem }> =>
    fetchJson(`/admin/notifications/dead-letter/${encodeURIComponent(id)}/retry`, {
      method: 'POST',
      body: '{}',
    }),
  ignoreAdminNotificationDeadLetter: (
    id: string,
    reason?: string,
  ): Promise<{ item: NotificationDeadLetterItem }> =>
    fetchJson(`/admin/notifications/dead-letter/${encodeURIComponent(id)}/ignore`, {
      method: 'POST',
      body: JSON.stringify(reason ? { reason } : {}),
    }),
  retryAdminPendingNotifications: (limit = 20): Promise<{ processed?: number } & Record<string, unknown>> =>
    fetchJson('/admin/notifications/retry-pending', { method: 'POST', body: JSON.stringify({ limit }) }),

  getActivityLog: () => fetchJson('/activity-log'),

  // Distribution Hub
  getPlatforms: () => fetchJson('/platforms'),
  getFiAccounts: () => fetchJson('/fi-accounts'),

  // Multi-tenant social channels
  getSocialChannelsConfig: (): Promise<{
    integration_models: Record<string, 'enterprise' | 'oauth'>
    connection_fields: Record<string, {
      model: 'enterprise' | 'oauth'
      supported_methods: Array<'oauth' | 'manual'>
      primary_method: 'oauth' | 'manual'
      oauth_configured: boolean
      target_fields: Array<{ key: string; label: string; required: boolean; secret: boolean }>
    }>
  }> => fetchJson('/social-channels/config'),
  getSocialChannels: (): Promise<Array<{
    id: string
    platform: string
    account_name: string
    status: string
    health: string
    connect_method: 'oauth' | 'manual' | null
    handle: string | null
    enterprise_targets: Record<string, string>
    pending_author_identities?: Array<{ urn: string; label: string; type: 'person' | 'organization' }> | null
    token_status: {
      connected: boolean
      method?: 'oauth' | 'manual' | null
      scope?: string | null
      expires_at?: string | null
      health?: string | null
    }
    oauth: {
      connected: boolean
      scope?: string | null
      expires_at?: string | null
      user_id?: string | null
    }
    updated_at: string | null
  }>> => fetchJson('/social-channels'),
  upsertSocialChannel: (
    platform: string,
    payload: {
      handle?: string
      account_name?: string
      enterprise_targets?: Record<string, string>
    },
  ) => fetchJson(`/social-channels/${platform}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  }),
  disconnectSocialChannel: (platform: string) =>
    fetchJson(`/social-channels/${platform}`, { method: 'DELETE' }),
  startSocialOAuth: (platform: string): Promise<{ auth_url: string; state: string; dev: boolean }> =>
    fetchJson(`/social-channels/oauth/${platform}/start`),
  completeMetaOAuthPageSelection: (payload: {
    selection_id: string
    page_id: string
    platform: string
  }): Promise<{ ok: boolean; platform: string; connection_id: string }> =>
    fetchJson('/social-channels/oauth/meta/select-page', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  completeWhatsAppOAuthSelection: (payload: {
    selection_id: string
    phone_number_id: string
  }): Promise<{ ok: boolean; platform: string; connection_id: string }> =>
    fetchJson('/social-channels/oauth/meta/select-whatsapp', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  // Wave 2A — Paid ads (Meta + Google Demand Gen)
  getPaidAdsChannels: (): Promise<{
    channels: Array<{
      platform: string
      kind: string
      capabilities: Record<string, unknown>
      approval: { platform: string; approved: boolean; state: string; message: string }
      connection: { id: string; health: string; provider_account_id?: string | null } | null
      health: string
      honest_state: string
      message: string
    }>
  }> => fetchJson('/paid-ads/channels'),
  connectPaidAdsChannel: (
    platform: string,
    payload: {
      credentials_ref: string
      provider_account_id?: string | null
      integration_model?: 'tenant_oauth' | 'enterprise_env'
      data?: Record<string, unknown>
    },
  ) =>
    fetchJson(`/paid-ads/channels/${platform}/connect`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  getPaidAdExecutions: (params?: Record<string, string>) => {
    const qs = params && Object.keys(params).length
      ? `?${new URLSearchParams(params).toString()}`
      : ''
    return fetchJson(`/paid-ads/executions${qs}`)
  },
  createPaidAdExecution: (payload: {
    channel_connection_id: string
    campaign_id?: string | null
    creative_id?: string | null
    audience_id?: string | null
    objective: 'awareness' | 'traffic' | 'engagement' | 'leads' | 'conversions'
    budget_micros: number
    currency?: string
    targeting?: Record<string, unknown>
    schedule?: Record<string, unknown> | null
    format?: string | null
    name?: string | null
    subject_type?: string | null
    subject_id?: string | null
    scheduled_at?: string | null
    data?: Record<string, unknown>
  }) =>
    fetchJson('/paid-ads/executions', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  getPaidAdExecution: (id: string) => fetchJson(`/paid-ads/executions/${id}`),
  launchPaidAdExecution: (id: string) =>
    fetchJson(`/paid-ads/executions/${id}/launch`, { method: 'POST', body: '{}' }),

  // Paid Ads Step 3/4 — campaigns domain + broadcast door (type home:
  // src/api/paidAdsCampaigns.ts). All creations are drafts; launch is step 6.
  getPaidAdsSpendSplits: (platform: 'meta_ads' | 'google_ads'): Promise<import('./paidAdsCampaigns').SpendSplitsResponse> =>
    fetchJson(`/paid-ads/spend-splits?platform=${platform}`),
  createPaidAdsBroadcastPlan: (
    payload: import('./paidAdsCampaigns').BroadcastPlanPayload,
  ): Promise<import('./paidAdsCampaigns').BroadcastPlanResult> =>
    fetchJson('/paid-ads/broadcast-plan', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  // Step 5 — the AI chooser: proposal-only, nothing is created here. The
  // agent confirms in the door (broadcast kind=ai), which is audited.
  createPaidAdsAiProposal: (
    payload: import('./paidAdsCampaigns').AiProposalRequest,
  ): Promise<import('./paidAdsCampaigns').AiProposalResult> =>
    fetchJson('/paid-ads/ai-proposal', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  // Campaign door — the Step-3 campaigns API. All creations are drafts.
  getPaidAdsCampaignTemplates: (
    platform: 'meta_ads' | 'google_ads',
  ): Promise<{ templates: import('./paidAdsCampaigns').CampaignTemplate[]; platform: string }> =>
    fetchJson(`/paid-ads/campaigns/templates?platform=${platform}`),
  createPaidAdsCampaign: (
    payload: import('./paidAdsCampaigns').CampaignCreatePayload,
  ): Promise<import('./paidAdsCampaigns').CampaignRow> =>
    fetchJson('/paid-ads/campaigns', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  // SEO (Wave 2F)
  getListingSeo: (propertyId: string) => fetchJson(`/seo/properties/${propertyId}`),
  updateListingSeo: (propertyId: string, payload: Record<string, unknown>) =>
    fetchJson(`/seo/properties/${propertyId}`, { method: 'PUT', body: JSON.stringify(payload) }),
  generateListingSeo: (propertyId: string) =>
    fetchJson(`/seo/properties/${propertyId}/generate`, { method: 'POST', body: '{}' }),
  getListingSeoExport: (propertyId: string) => fetchJson(`/seo/properties/${propertyId}/export`),
  getListingSeoTarget: (propertyId: string) => fetchJson(`/seo/properties/${propertyId}/target`),
  setListingSeoTarget: (propertyId: string, payload: { target_surface: string; external_site_url?: string | null }) =>
    fetchJson(`/seo/properties/${propertyId}/target`, { method: 'PUT', body: JSON.stringify(payload) }),

  publishListingToSocial: (
    propertyId: string,
    payload: {
      channels: Array<{ platform: string; format?: string; link_url?: string; caption?: string }>
      caption: string
      media_urls?: string[]
      recipient?: string
      contact_id?: string
      creative_id?: string
    },
  ): Promise<{
    results: Array<{
      platform: string
      status: 'published' | 'failed'
      external_id: string | null
      external_url: string | null
      provider: string | null
      simulated: boolean
      error: string | null
      error_code?: string | null
      excluded?: boolean
    }>
  }> =>
    fetchJson(`/listings/${propertyId}/publish-social`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  getMyConnections: () => fetchJson('/my-connections'),
  connectMyPlatform: (data: Record<string, unknown>) =>
    fetchJson('/my-connections', { method: 'POST', body: JSON.stringify(data) }),
  updateMyConnection: (id: string, data: Record<string, unknown>) =>
    fetchJson(`/my-connections/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  disconnectMyPlatform: (id: string) =>
    fetchJson(`/my-connections/${id}`, { method: 'DELETE' }),
  listPersonalChannelConnections: (): Promise<{ connections: PersonalChannelConnection[] }> =>
    fetchJson('/social-channels/my-connections'),
  createPersonalChannelConnection: (
    data: PersonalChannelConnectionInput,
  ): Promise<PersonalChannelConnection> =>
    fetchJson('/social-channels/my-connections', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  updatePersonalChannelConnection: (
    id: string,
    data: Partial<Pick<PersonalChannelConnectionInput, 'account_name' | 'handle' | 'is_primary'>>,
  ): Promise<PersonalChannelConnection> =>
    fetchJson(`/social-channels/my-connections/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  removePersonalChannelConnection: (
    id: string,
  ): Promise<{ success: true; new_primary_id: string | null }> =>
    fetchJson(`/social-channels/my-connections/${id}`, { method: 'DELETE' }),
  distributeOwn: (propertyId: string, platforms: string[], options?: {
    mode?: string
    formats?: Record<string, string[]>
    recipient?: string
    caption?: string
    captions?: Record<string, string>
    intent?: string
  }) =>
    fetchJson(`/properties/${propertyId}/distribute-own`, {
      method: 'POST',
      body: JSON.stringify({
        platforms,
        mode: options?.mode || 'publish',
        formats: options?.formats,
        recipient: options?.recipient,
        caption: options?.caption,
        captions: options?.captions,
        intent: options?.intent || 'distribute',
      }),
    }),
  getDistributions: (propertyId: string): Promise<Array<{
    id: string
    property_id: string
    platform: string
    status: string
    external_id: string | null
    published_at: string | null
    landing_page?: string | null
    post_url?: string | null
    insights?: {
      impressions: number | null
      reach: number | null
      likes: number | null
      comments: number | null
      shares: number | null
      saves: number | null
      clicks: number | null
      source: string
      simulated: boolean
      fetched_at: string
    } | null
    impressions?: number
    reach?: number
    likes?: number
    comments_count?: number
    shares?: number
    saves?: number
    clicks?: number
    insights_fetched_at?: string
  }>> => fetchJson(`/properties/${propertyId}/distributions`),
  refreshDistributionInsights: (distributionId: string) =>
    fetchJson(`/distributions/${distributionId}/refresh-insights`, { method: 'POST', body: '{}' }),

  /* -------------------- Social-card templates + renders -------------------- */
  getSocialCardPlatforms: (): Promise<{
    platforms: Array<{ key: string; width: number; height: number; aspect: string; label: string }>
  }> => fetchJson('/social-cards/platforms'),
  getSocialCardBindings: (): Promise<{
    paths: string[]; helpers: string[]; example_usage: Record<string, string>
  }> => fetchJson('/social-card-templates/bindings'),
  listSocialCardTemplates: (
    scope?: 'visible' | 'platform' | 'store' | 'agency' | 'agent' | 'mine',
  ): Promise<{
    templates: SocialCardTemplate[]
    agency_id: string | null
  }> => fetchJson(`/social-card-templates${scope ? `?scope=${scope}` : ''}`),
  getSocialCardTemplate: (id: string): Promise<{ template: SocialCardTemplate }> =>
    fetchJson(`/social-card-templates/${id}`),
  createSocialCardTemplate: (
    template: Partial<SocialCardTemplate>,
    ownerType?: 'agent' | 'agency',
  ): Promise<{ template: SocialCardTemplate }> =>
    fetchJson('/social-card-templates', {
      method: 'POST',
      body: JSON.stringify({ template, owner_type: ownerType || 'agent' }),
    }),
  duplicateSocialCardTemplate: (
    id: string,
    payload?: { name?: string; owner_type?: 'agent' | 'agency' },
  ): Promise<{ template: SocialCardTemplate }> =>
    fetchJson(`/social-card-templates/${id}/duplicate`, {
      method: 'POST',
      body: JSON.stringify(payload || {}),
    }),
  updateSocialCardTemplate: (
    id: string, template: Partial<SocialCardTemplate>,
  ): Promise<{ template: SocialCardTemplate }> =>
    fetchJson(`/social-card-templates/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ template }),
    }),
  deleteSocialCardTemplate: (id: string) =>
    fetchJson(`/social-card-templates/${id}`, { method: 'DELETE' }),

  listSocialCards: (listingId: string): Promise<{ cards: SocialCardAsset[] }> =>
    fetchJson(`/listings/${listingId}/social-cards`),
  renderSocialCards: (
    listingId: string,
    payload: { template_ids: string[]; platforms: string[]; brand?: Record<string, unknown> },
  ): Promise<{ cards: SocialCardAsset[]; errors: Array<{ template_id: string; platform: string; error: string }> }> =>
    fetchJson(`/listings/${listingId}/social-cards/render`, {
      method: 'POST', body: JSON.stringify(payload),
    }),
  deleteSocialCard: (id: string) =>
    fetchJson(`/social-cards/${id}`, { method: 'DELETE' }),
  getBannerbearStatus: (): Promise<{
    enabled: boolean; force_synchronous: boolean; webhook_url_configured: boolean
  }> => fetchJson('/social-cards/bannerbear/status'),
  syncBannerbearCatalog: (): Promise<{ synced: number }> =>
    fetchJson('/social-cards/bannerbear/sync', { method: 'POST', body: '{}' }),

  listListingCreatives: (listingId: string): Promise<{ creatives: CreativeRecord[] }> =>
    fetchJson(`/listings/${listingId}/creatives`),
  generateListingCreative: (
    listingId: string,
    payload: {
      description: string
      channel_keys: string[]
      template_id?: string
      provider?: 'local' | 'bannerbear'
    },
  ): Promise<CreativeBundle> =>
    fetchJson(`/listings/${listingId}/creatives/generate`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  getCreative: (id: string): Promise<CreativeBundle> =>
    fetchJson(`/creatives/${id}`),
  updateCreativeVariant: (
    id: string,
    payload: { copy: Record<string, string> },
  ): Promise<{ variant: CreativeVariant }> =>
    fetchJson(`/creative-variants/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    }),
  approveCreative: (
    id: string,
    payload: { decision?: 'approved' | 'rejected'; note?: string },
  ): Promise<CreativeBundle> =>
    fetchJson(`/creatives/${id}/approve`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  publishCreative: (
    id: string,
    payload: {
      selections: Array<{ variant_id: string; channel_key: string }>
      media_urls?: string[]
    },
  ): Promise<{
    creative_id: string
    results: Array<{
      variant_id: string
      channel_key: string
      platform: string
      status: string
      error?: string
      excluded?: boolean
      media_urls?: string[]
    }>
    excluded?: Array<{ platform: string; code: string; message: string }>
    media_order?: string[]
  }> =>
    fetchJson(`/creatives/${id}/publish`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  /* ------ Closed transactions (AVM training data capture) ------ */
  getClosedTransactionsConfig: (): Promise<{
    transaction_types: string[]
    buyer_types: string[]
    payment_methods: string[]
    attribution_sources: string[]
    close_reasons: string[]
  }> => fetchJson('/closed-transactions/config'),
  listClosedTransactions: (params?: {
    listing_id?: string; contact_id?: string; limit?: number
  }): Promise<{ transactions: ClosedTransaction[] }> => {
    const q = new URLSearchParams()
    if (params?.listing_id) q.set('listing_id', params.listing_id)
    if (params?.contact_id) q.set('contact_id', params.contact_id)
    if (params?.limit) q.set('limit', String(params.limit))
    const qs = q.toString() ? `?${q.toString()}` : ''
    return fetchJson(`/closed-transactions${qs}`)
  },
  recordClosedTransaction: (payload: Partial<ClosedTransaction>): Promise<ClosedTransaction> =>
    fetchJson('/closed-transactions', { method: 'POST', body: JSON.stringify(payload) }),
  deleteClosedTransaction: (id: string) =>
    fetchJson(`/closed-transactions/${id}`, { method: 'DELETE' }),
  importClosedTransactionsCsv: (
    csvText: string,
    options?: {
      column_map?: Record<string, string>
      filename?: string | null
      preview_only?: boolean
    },
  ): Promise<{
    imported?: number
    skipped?: number
    errors?: Array<{ row: number; error: string }>
    import_id?: string | null
    row_count?: number
    preview?: Array<{
      row: number
      listing_id: string | null
      external_reference: string | null
      transaction_type: string
      final_sold_price: string | null
      closed_at: string | null
      currency: string
      valid: boolean
    }>
    valid_count?: number
    headers?: string[]
  }> => fetchJson('/closed-transactions/import', {
    method: 'POST',
    body: JSON.stringify({
      csv_text: csvText,
      ...(options?.column_map ? { column_map: options.column_map } : {}),
      ...(options?.filename ? { filename: options.filename } : {}),
      ...(options?.preview_only ? { preview_only: true } : {}),
    }),
  }),

  /* -------- Contact 360 (Phase 4.8) -------- */
  getContact360Config: (): Promise<{ category_weights: Record<string, number> }> =>
    fetchJson('/contact-360/config'),
  getContactConversations360: (contactId: string): Promise<{
    contact: Record<string, unknown>
    channels: string[]
    listings: Array<{ id: string; title: string; city: string | null; price: number | null; price_unit: string | null }>
    messages: Array<{
      id: string
      conversation_id: string
      channel: string
      platform: string | null
      listing_id: string | null
      direction: 'inbound' | 'outbound'
      content: string
      created_at: string
      status: string
      author_name: string | null
      category: string | null
      sentiment: 'positive' | 'neutral' | 'negative' | null
      category_confidence: number | null
      suggested_reply: string | null
      needs_agent_attention: boolean
    }>
    message_count: number
  }> => fetchJson(`/contacts/${contactId}/conversations-360`),
  getContactLeadScore: (contactId: string): Promise<{
    score: number
    message_count: number
    category_counts: Record<string, number>
    weighted_sum: number
    reasoning: string
  }> => fetchJson(`/contacts/${contactId}/lead-score`),
  getContactLeadSummary: (contactId: string): Promise<{
    contact_id: string
    score: { score: number; message_count: number; category_counts: Record<string, number>; weighted_sum: number; reasoning: string }
    summary: {
      text: string
      next_steps: Array<{ action: string; reason: string; params: { listing_id: string | null; template_hint: string | null; priority: string } }>
      generated_at: string
      provider: string
      message_count_at_generation: number
      is_stale: boolean
    } | null
    has_cached: boolean
    is_stale: boolean
  }> => fetchJson(`/contacts/${contactId}/lead-summary`),
  regenerateContactLeadSummary: (contactId: string) =>
    fetchJson(`/contacts/${contactId}/regenerate-summary`, { method: 'POST', body: '{}' }),

  /* -------- Per-listing Performance dashboard (Phase 4.9) -------- */
  getListingPerformance: (listingId: string, days = 30): Promise<{
    listing_id: string
    generated_at: string
    all_channels: PerformanceMetricBlock
    per_channel: Array<PerformanceMetricBlock & { platform: string }>
    funnel: Array<{
      platform: string
      views: number; engagements: number; clicks: number
      inquiries: number; viewings_scheduled: number; closes: number
    }>
    time_series: {
      days: number
      channels: Record<string, Array<{
        date: string; impressions: number; engagements: number; clicks: number
        comments: number; likes: number; shares: number; saves: number
      }>>
    }
    counts: {
      published_posts: number; channels: number
      snapshot_days: number; snapshot_points: number
      contacts_reached: number
    }
  }> => fetchJson(`/listings/${listingId}/performance?days=${days}`),
  retryDistribution: (distributionId: string) =>
    fetchJson(`/distributions/${distributionId}/retry`, { method: 'POST', body: '{}' }),
  retryPendingDistributions: (limit = 20) =>
    fetchJson('/distributions/retry-pending', { method: 'POST', body: JSON.stringify({ limit }) }),
  runRetryWorker: (options?: { limit?: number; due_only?: boolean; scope?: 'self' | 'all' }) =>
    fetchJson('/distributions/retry-worker/run', {
      method: 'POST',
      body: JSON.stringify({
        limit: options?.limit,
        due_only: options?.due_only,
        scope: options?.scope,
      }),
    }),

  // Contacts & Conversation Orchestrator
  getContacts: (params?: { q?: string }) => {
    const qs = params?.q ? `?${new URLSearchParams({ q: params.q }).toString()}` : ''
    return fetchJson(`/contacts${qs}`)
  },
  getContact: (id: string) => fetchJson(`/contacts/${id}`),
  // Full CRM contact form create (POST). Accepts the full nested payload; the
  // backend promotes typed columns and stores the rest in the `data` JSONB.
  createContact: (data: Record<string, unknown>) =>
    fetchJson('/contacts', { method: 'POST', body: JSON.stringify(data) }),
  // Private, tenant-gated contact attachment (e.g. Pre-Approval Letter). Multipart
  // upload with the auth header; the backend returns metadata only (no URL/key).
  uploadContactAttachment: async (
    contactId: string,
    file: File,
    kind: 'pre_approval_letter' | 'voice_note' | 'other' = 'pre_approval_letter',
  ): Promise<{ attachment: { id: string; kind: string; filename: string | null; content_type: string | null; size_bytes: number | null } }> => {
    const form = new FormData()
    form.append('file', file)
    form.append('kind', kind)
    const token = getToken()
    const uploadHeaders: Record<string, string> = { 'X-Wingcaster-Env': readWingcasterEnvHeader() }
    if (token) uploadHeaders.Authorization = `Bearer ${token}`
    const res = await fetch(`${API_BASE}/contacts/${encodeURIComponent(contactId)}/attachments`, {
      method: 'POST',
      headers: uploadHeaders,
      body: form,
    })
    const bodyText = await res.text()
    const parsed = (() => {
      try {
        return bodyText ? JSON.parse(bodyText) : null
      } catch {
        return null
      }
    })()
    if (!res.ok) {
      const error = new Error(parsed?.error || `Upload failed (${res.status})`) as Error & Record<string, unknown>
      Object.assign(error, parsed || {}, { status: res.status })
      throw error
    }
    if (!parsed) throw new Error('Attachment upload returned non-JSON')
    return parsed
  },
  getContactAttachments: (
    contactId: string,
    kind?: 'pre_approval_letter' | 'voice_note' | 'other',
  ): Promise<{ attachments: Array<{ id: string; kind: string; filename: string | null; content_type: string | null; size_bytes: number | null; created_at?: string }> }> => {
    const qs = kind ? `?kind=${encodeURIComponent(kind)}` : ''
    return fetchJson(`/contacts/${encodeURIComponent(contactId)}/attachments${qs}`)
  },
  deleteContactAttachment: (contactId: string, attachmentId: string) =>
    fetchJson(
      `/contacts/${encodeURIComponent(contactId)}/attachments/${encodeURIComponent(attachmentId)}`,
      { method: 'DELETE' },
    ),
  contactAttachmentDownloadPath: (contactId: string, attachmentId: string): string =>
    `${API_BASE}/contacts/${encodeURIComponent(contactId)}/attachments/${encodeURIComponent(attachmentId)}`,
  // AGT-CTC-005 — bulk export download URL (fetched with the auth header, then
  // streamed to a Blob by the caller so the browser saves a real file).
  contactsExportPath: (params: { format?: ContactExportFormat; fields?: string[] } = {}): string => {
    const q = new URLSearchParams()
    if (params.format) q.set('format', params.format)
    if (params.fields && params.fields.length) q.set('fields', params.fields.join(','))
    const qs = q.toString()
    return `${API_BASE}/contacts/export${qs ? `?${qs}` : ''}`
  },
  revealContactPii: (id: string, field: string) =>
    fetchJson(`/contacts/${id}/reveal-pii`, {
      method: 'POST',
      body: JSON.stringify({ field }),
    }),
  updateContact: (id: string, data: Record<string, unknown>) =>
    fetchJson(`/contacts/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  mergeContacts: (
    sourceId: string,
    payload: {
      target_contact_id: string
      field_selections?: Partial<Record<'name' | 'email' | 'phone' | 'status' | 'source', 'source' | 'target'>>
    },
  ) =>
    fetchJson(`/contacts/${sourceId}/merge`, { method: 'POST', body: JSON.stringify(payload) }),
  getContactTimeline: (id: string) => fetchJson(`/contacts/${id}/timeline`),
  // Contact card: listings the contact is tied to (inquiries + viewings) with state.
  getContactInterestedListings: (
    id: string,
  ): Promise<{ listings: Array<{
    property_id: string; title: string | null; address_display: string | null
    price: number | null; currency: string | null; listing_status: string | null
    inquiry_status: string | null; viewing_status: string | null
    viewing_scheduled_at: string | null; last_activity_at: string | null
  }> }> => fetchJson(`/contacts/${encodeURIComponent(id)}/interested-listings`),
  // Contact card: last contact per channel + last deal.
  getContactEngagement: (
    id: string,
  ): Promise<{
    channels: string[]
    last_by_channel: Record<string, string>
    last_contact_at: string | null
    last_deal: { id: string; stage: string | null; property_id: string | null; deal_value: number | null; currency: string | null; expected_close_date: string | null; updated_at: string | null } | null
  }> => fetchJson(`/contacts/${encodeURIComponent(id)}/engagement`),
  getContactNotes: (id: string) => fetchJson(`/contacts/${id}/notes`),
  createContactNote: (id: string, content: string) =>
    fetchJson(`/contacts/${id}/notes`, { method: 'POST', body: JSON.stringify({ content }) }),

  // Tasks
  getTasks: (params?: Record<string, string>) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : ''
    return fetchJson(`/tasks${qs}`)
  },
  getTask: (id: string): Promise<TaskRecord> => fetchJson(`/tasks/${id}`),
  createTask: (data: Record<string, unknown>) => fetchJson('/tasks', { method: 'POST', body: JSON.stringify(data) }),
  updateTask: (id: string, data: Record<string, unknown>) =>
    fetchJson(`/tasks/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  completeTask: (id: string) => fetchJson(`/tasks/${id}/complete`, { method: 'POST', body: '{}' }),
  deleteTask: (id: string) => fetchJson(`/tasks/${id}`, { method: 'DELETE' }),

  // Opportunities
  getOpportunities: (params?: Record<string, string>) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : ''
    return fetchJson(`/opportunities${qs}`)
  },
  createOpportunity: (data: Record<string, unknown>) =>
    fetchJson('/opportunities', { method: 'POST', body: JSON.stringify(data) }),
  getOpportunity: (id: string) => fetchJson(`/opportunities/${id}`),
  updateOpportunity: (id: string, data: Record<string, unknown>) =>
    fetchJson(`/opportunities/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  getConversations: (): Promise<InboxConversation[]> => fetchJson('/conversations'),
  getConversation: (id: string): Promise<InboxConversationDetail> => fetchJson(`/conversations/${id}`),
  createConversation: (payload: {
    contact_id?: string
    new_contact?: { name?: string; phone?: string; email?: string }
    channel: string
    body?: string
    source?: string
    subject?: string
    template_id?: string
    attachments?: Array<{ url: string; mime?: string; filename?: string; size_bytes?: number }>
  }): Promise<InboxConversation & { created?: boolean }> =>
    fetchJson('/conversations', { method: 'POST', body: JSON.stringify(payload) }),
  sendConversationMessage: (
    id: string,
    content: string,
    options?: {
      content_type?: string
      image_url?: string
      audio_url?: string
      subject?: string
      attachments?: Array<{ url: string; mime?: string; filename?: string; size_bytes?: number }>
      template_id?: string
    },
  ) =>
    fetchJson(`/conversations/${id}/messages`, { method: 'POST', body: JSON.stringify({ content, ...(options || {}) }) }),
  retryConversationMessage: (id: string, messageId: string) =>
    fetchJson(`/conversations/${id}/messages/${messageId}/retry`, { method: 'POST', body: '{}' }),
  bulkConversations: (payload: {
    conversation_ids: string[]
    action: 'mark_read' | 'mark_unread' | 'assign' | 'archive'
    assign_to_agent_id?: string
    assignment_note?: string
  }) => fetchJson('/conversations/bulk', { method: 'POST', body: JSON.stringify(payload) }),
  getAgentPreferences: (): Promise<{ inbox_merge_mode: 'merged' | 'separate' }> =>
    fetchJson('/agent-preferences'),
  patchAgentPreferences: (payload: { inbox_merge_mode: 'merged' | 'separate' }) =>
    fetchJson('/agent-preferences', { method: 'PATCH', body: JSON.stringify(payload) }),
  getConversationAiSuggestions: (
    id: string,
  ): Promise<{
    enabled?: boolean
    suggestions: Array<string | { id?: string; body: string; language?: string }>
    model?: string
    latency_ms?: number
    degraded?: boolean
    source?: string | null
  }> => fetchJson(`/conversations/${id}/ai-suggestions`, { method: 'POST', body: '{}' }),
  /** Alias for getConversationAiSuggestions (Wave 8 dispatch name). */
  getAiSuggestions: (
    id: string,
  ): Promise<{
    enabled?: boolean
    suggestions: Array<string | { id?: string; body: string; language?: string }>
    model?: string
    latency_ms?: number
    degraded?: boolean
    source?: string | null
  }> => fetchJson(`/conversations/${id}/ai-suggestions`, { method: 'POST', body: '{}' }),

  getListingComments: (
    listingId: string,
    options?: { category?: string | string[] },
  ): Promise<{
    threads: Array<{
      conversation_id: string
      platform: string | null
      channel: string
      external_post_id: string | null
      distribution_url: string | null
      top_category: string | null
      contact: { id: string; name: string; avatar: string | null } | null
      needs_agent_attention: boolean
      messages: Array<{
        id: string
        direction: 'inbound' | 'outbound'
        content: string
        created_at: string
        author_name: string | null
        status: string
        category: string | null
        sentiment: 'positive' | 'neutral' | 'negative' | null
        category_confidence: number | null
        category_source: 'rules' | 'ai' | 'manual' | null
        suggested_reply: string | null
        suggested_reply_composed_at: string | null
        needs_agent_attention: boolean
        priority: 'low' | 'normal' | 'high' | 'urgent' | null
        is_hidden: boolean
        routings: Array<{
          id: string
          category: string
          route: string | null
          outcomes: Array<{ type: string; ref_id?: string; at: string; notes?: string; details?: any }>
          created_at: string
        }>
      }>
      last_activity_at: string | null
    }>
    published_posts: number
    summary: Record<string, number>
    category_meta: Record<string, { label: string; emoji: string; description: string; route: string }>
  }> => {
    const cat = options?.category
    const qs = cat
      ? '?category=' + encodeURIComponent(Array.isArray(cat) ? cat.join(',') : String(cat))
      : ''
    return fetchJson(`/listings/${listingId}/comments${qs}`)
  },
  getCommentClassifierConfig: (): Promise<{
    categories: string[]
    sentiments: string[]
    meta: Record<string, { label: string; emoji: string; description: string; route: string }>
    operational?: {
      batch_size?: number
      ai_enabled?: boolean
      ai_provider?: string | null
      rules_confidence_threshold?: number
    }
  }> => fetchJson('/comment-classifier/config'),

  /** PA-CLS-001 — manual classifier batch + run history. */
  runCommentClassifierBatch: (): Promise<Record<string, unknown>> =>
    fetchJson('/admin/comment-classifier/run', { method: 'POST', body: '{}' }),

  listCommentClassifierRuns: (params?: { limit?: string }): Promise<{
    runs: Array<{
      id: string
      triggered_by_agent_id?: string | null
      batched: number
      updated_count: number
      skipped_reason?: string | null
      error_message?: string | null
      created_at: string
    }>
    total: number
  }> => {
    const qs = params?.limit ? `?limit=${encodeURIComponent(params.limit)}` : ''
    return fetchJson(`/admin/comment-classifier/runs${qs}`)
  },
  reclassifyComment: (messageId: string, category: string, sentiment?: string) =>
    fetchJson(`/comments/${messageId}/reclassify`, {
      method: 'POST',
      body: JSON.stringify({ category, sentiment }),
    }),
  backfillCommentCategories: (listingId: string) =>
    fetchJson(`/listings/${listingId}/comments/backfill-categories`, { method: 'POST', body: '{}' }),

  getRoutingConfig: (): Promise<{
    config: Record<string, Record<string, unknown>>
    agency_id: string | null
    agent_id: string | null
  }> => fetchJson('/routing-config'),
  getRoutingConfigDefaults: (): Promise<{
    defaults: Record<string, Record<string, unknown>>
    categories: string[]
  }> => fetchJson('/routing-config/defaults'),
  updateRoutingConfig: (payload: {
    owner_type?: 'agent' | 'agency'
    routes: Record<string, Record<string, unknown>>
  }) => fetchJson('/routing-config', { method: 'PUT', body: JSON.stringify(payload) }),

  getCommandCenter: (): Promise<{
    escalations: {
      complaints: Array<CommandItem>
      objections: Array<CommandItem>
      hot_leads: Array<CommandItem>
      other: Array<CommandItem>
    }
    pipeline: {
      standard: Array<CommandOpportunity>
      investor: Array<CommandOpportunity>
      other: Array<CommandOpportunity>
    }
    inquiries: Array<{
      id: string; property_id: string | null; property_title: string | null;
      name: string; email: string; phone: string; message: string;
      channel?: string; source?: string; source_channel?: string;
      status: string; priority: string; created_at: string;
      origin_message_id?: string
    }>
    engagement: {
      reactions: number; referrals: number; mentions: number;
      by_platform: Record<string, { reactions: number; referrals: number; mentions: number }>
    }
    ai_watching: Array<{
      conversation_id: string; channel: string; source?: string; source_channel?: string; contact_name: string | null;
      last_message_preview: string; last_message_at: string | null;
      ai_watch_started_at: string | null
    }>
    testimonials: Array<{
      id: string; content: string; author_name: string; source_channel?: string; channel?: string; source?: string;
      source_post_url: string | null; consent_status: string;
      published_status: string; created_at: string
    }>
    routing_activity: Array<{
      id: string; message_id: string; category: string; route: string | null;
      outcomes: Array<{ type: string; ref_id?: string; at: string; notes?: string }>;
      created_at: string
    }>
    summary: {
      escalations_total: number; pipeline_total: number; inquiries_total: number;
      testimonials_total: number; ai_watching_total: number
    }
  }> => fetchJson('/command-center'),
  assignConversation: (id: string, agentId?: string, note?: string) =>
    fetchJson(`/conversations/${id}/assign`, {
      method: 'POST',
      body: JSON.stringify({ agent_id: agentId, note: note || undefined }),
    }),
  getAssignableAgents: (id: string): Promise<{ agents: AssignableAgent[] }> =>
    fetchJson(`/conversations/${id}/assignable-agents`),
  updateConversation: (id: string, data: Record<string, unknown>) =>
    fetchJson(`/conversations/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  closeConversation: (id: string, reason?: string) =>
    fetchJson(`/conversations/${id}/close`, { method: 'POST', body: JSON.stringify({ reason }) }),
  markConversationRead: (id: string) =>
    fetchJson(`/conversations/${id}/read`, { method: 'POST', body: '{}' }),

  // WhatsApp
  getWhatsAppStatus: () => fetchJson('/whatsapp/status'),
  sendWhatsAppListing: (propertyId: string, to?: string) =>
    fetchJson('/whatsapp/send-listing', { method: 'POST', body: JSON.stringify({ property_id: propertyId, to }) }),
  sendWhatsAppText: (to: string, message: string) =>
    fetchJson('/whatsapp/send-text', { method: 'POST', body: JSON.stringify({ to, message }) }),

  submitToFi: (propertyId: string, platforms: string[], message?: string) =>
    fetchJson(`/properties/${propertyId}/submit-to-fi`, { method: 'POST', body: JSON.stringify({ platforms, message }) }),
  getMySubmissions: () => fetchJson('/my-submissions'),
  getDistributionPerformance: () => fetchJson('/distribution/performance'),

  /** Dynamic portal_registry picker (AGT-PUB-005) — no hardcoded portal arrays. */
  getPortalRegistry: (): Promise<{
    portals: Array<{
      code: string
      display_name: string
      description: string | null
      logo_url: string | null
      country_codes: string[]
      primary_language: string | null
      is_active: boolean
      deprecated_at: string | null
      sla_hours: number | null
    }>
  }> => fetchJson('/portals'),

  /**
   * Create a publishing job + pending_moderation destinations.
   * Returns jobId compatible with GET /api/publishing/jobs/:jobId (AGT-PUB-003).
   */
  createPublishingJob: (
    propertyId: string,
    portals: Array<string | { code: string; country_code?: string }>,
    message?: string,
  ): Promise<{
    jobId: string
    job: { id: string; aggregate?: string; listing_id?: string }
    destinations: unknown[]
  }> =>
    fetchJson('/publishing/jobs', {
      method: 'POST',
      body: JSON.stringify({
        property_id: propertyId,
        portals,
        message: message || undefined,
      }),
    }),

  // AGT-PUB-007 — schedule publish (later)
  listScheduledPublications: (propertyId: string) =>
    fetchJson(`/properties/${propertyId}/scheduled-publications`) as Promise<{
      scheduled: ScheduledPublication[]
    }>,
  createScheduledPublication: (propertyId: string, input: ScheduledPublicationInput) =>
    fetchJson(`/properties/${propertyId}/scheduled-publications`, {
      method: 'POST',
      body: JSON.stringify(input),
    }) as Promise<ScheduledPublication>,
  cancelScheduledPublication: (schedId: string) =>
    fetchJson(`/scheduled-publications/${schedId}`, { method: 'DELETE' }) as Promise<{ success: boolean }>,

  // SHR-ERR-002 — permission-denied "Request access"
  requestAccess: (input: AccessRequestInput): Promise<AccessRequestResult> =>
    fetchJson('/access-requests', { method: 'POST', body: JSON.stringify(input) }),
  getMyAccessRequests: (): Promise<{ requests: AccessRequest[] }> => fetchJson('/access-requests/mine'),
  getAccessRequest: (id: string): Promise<{ request: AccessRequest }> =>
    fetchJson(`/access-requests/${encodeURIComponent(id)}`),
  // SHR-ERR-005 — platform maintenance / degraded status
  getPlatformStatus: (): Promise<PlatformStatusResponse> => fetchJson('/status'),
  listPlatformStatusNotices: (): Promise<{ notices: PlatformStatusNotice[] }> =>
    fetchJson('/admin/status-notices'),
  createPlatformStatusNotice: (input: PlatformStatusNoticeInput) =>
    fetchJson('/admin/status-notices', { method: 'POST', body: JSON.stringify(input) }) as Promise<PlatformStatusNotice>,
  updatePlatformStatusNotice: (id: string, patch: PlatformStatusNoticeInput) =>
    fetchJson(`/admin/status-notices/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }) as Promise<PlatformStatusNotice>,

  // Admin
  getAdminSubmissions: () => fetchJson('/admin/submissions'),
  approveSubmission: (id: string, notes?: string) =>
    fetchJson(`/admin/submissions/${id}/approve`, { method: 'POST', body: JSON.stringify({ notes }) }),
  rejectSubmission: (id: string, notes?: string) =>
    fetchJson(`/admin/submissions/${id}/reject`, { method: 'POST', body: JSON.stringify({ notes }) }),

  getSharePayload: (propertyId: string) => fetchJson(`/properties/${propertyId}/share`),
  getFeedUrl: () => `${API_BASE}/feed/properties.xml`,

  // Territories
  getTerritories: () => fetchJson('/territories'),
  getTerritoryDisclosureFields: (id: string) => fetchJson(`/territories/${id}/disclosure-fields`),
  createPropertyOffer: (propertyId: string, data: Record<string, unknown>) =>
    fetchJson(`/properties/${propertyId}/offers`, { method: 'POST', body: JSON.stringify(data) }),

  // Engagement
  getAgentEngagement: (id: string) => fetchJson(`/agents/${id}/engagement`),
  followAgent: (id: string) => fetchJson(`/agents/${id}/follow`, { method: 'POST', body: '{}' }),
  unfollowAgent: (id: string) => fetchJson(`/agents/${id}/follow`, { method: 'DELETE' }),
  getFollowingAgent: (id: string) => fetchJson(`/agents/${id}/following-me`),

  // White-label sites
  getTemplates: () => fetchJson('/white-label/templates'),
  createSite: (data: Record<string, unknown>) =>
    fetchJson('/white-label/sites', { method: 'POST', body: JSON.stringify(data) }),
  getSites: () => fetchJson('/white-label/sites'),
  getSite: (id: string) => fetchJson(`/white-label/sites/${id}`),
  updateSite: (id: string, data: Record<string, unknown>) =>
    fetchJson(`/white-label/sites/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteSite: (id: string) => fetchJson(`/white-label/sites/${id}`, { method: 'DELETE' }),

  // Domains
  createDomain: (data: Record<string, unknown>) =>
    fetchJson('/white-label/domains', { method: 'POST', body: JSON.stringify(data) }),
  getDomains: () => fetchJson('/white-label/domains'),

  // Routing rules
  createRoutingRule: (data: Record<string, unknown>) =>
    fetchJson('/white-label/routing-rules', { method: 'POST', body: JSON.stringify(data) }),
  getRoutingRules: () => fetchJson('/white-label/routing-rules'),
  updateRoutingRule: (id: string, data: Record<string, unknown>) =>
    fetchJson(`/white-label/routing-rules/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteRoutingRule: (id: string) => fetchJson(`/white-label/routing-rules/${id}`, { method: 'DELETE' }),

  // Sync connections
  createSyncConnection: (data: Record<string, unknown>) =>
    fetchJson('/white-label/sync-connections', { method: 'POST', body: JSON.stringify(data) }),
  getSyncConnections: () => fetchJson('/white-label/sync-connections'),
  deleteSyncConnection: (id: string) => fetchJson(`/white-label/sync-connections/${id}`, { method: 'DELETE' }),
  runSyncConnection: (id: string, body?: Record<string, unknown>) =>
    fetchJson(`/white-label/sync-connections/${id}/run`, { method: 'POST', body: JSON.stringify(body || {}) }),
  importListings: (listings: unknown[], source?: string) =>
    fetchJson('/white-label/import-listings', { method: 'POST', body: JSON.stringify({ listings, source }) }),

  // Widgets
  createWidget: (data: Record<string, unknown>) =>
    fetchJson('/white-label/widgets', { method: 'POST', body: JSON.stringify(data) }),
  getWidgets: () => fetchJson('/white-label/widgets'),
  deleteWidget: (id: string) => fetchJson(`/white-label/widgets/${id}`, { method: 'DELETE' }),

  // Message Templates
  getMessageTemplates: (params?: { channel?: string; category?: string }) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : ''
    return fetchJson(`/message-templates${qs}`)
  },
  getDefaultMessageTemplates: (params?: { channel?: string; category?: string }) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : ''
    return fetchJson(`/message-templates/defaults${qs}`)
  },
  getMessageTemplate: (id: string) => fetchJson(`/message-templates/${id}`),
  createMessageTemplate: (data: Record<string, unknown>) =>
    fetchJson('/message-templates', { method: 'POST', body: JSON.stringify(data) }),
  updateMessageTemplate: (id: string, data: Record<string, unknown>) =>
    fetchJson(`/message-templates/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteMessageTemplate: (id: string) => fetchJson(`/message-templates/${id}`, { method: 'DELETE' }),
  renderMessageTemplate: (id: string, variables: Record<string, string>) =>
    fetchJson(`/message-templates/${id}/render`, { method: 'POST', body: JSON.stringify({ variables }) }),
  testSendMessageTemplate: (id: string, to: string, variables: Record<string, string>) =>
    fetchJson(`/message-templates/${id}/test-send`, { method: 'POST', body: JSON.stringify({ to, variables }) }),

  // Analytics
  getCrmAnalytics: (params?: { start_date?: string; end_date?: string; scope?: 'all'; agency_id?: string }) =>
    fetchJson(`/analytics/crm${params ? '?' + new URLSearchParams(params).toString() : ''}`),
  getAgencyCampaignPerformance: (params?: {
    start_date?: string
    end_date?: string
    channel?: string
    agent_id?: string
  }) =>
    fetchJson(
      `/agency/analytics/campaign-performance${params ? '?' + new URLSearchParams(params).toString() : ''}`,
    ) as Promise<AgencyCampaignPerformanceResponse>,
  getAgencyAttributionPerformance: (params?: {
    model?: AttributionModel
    campaign_id?: string
    execution_id?: string
    agent_id?: string
  }) => {
    const qs = params
      ? '?' + new URLSearchParams(
          Object.entries(params)
            .filter(([, v]) => v != null && v !== '')
            .map(([k, v]) => [k, String(v)]),
        ).toString()
      : ''
    return fetchJson(`/agency/analytics/attribution-performance${qs}`) as Promise<AgencyAttributionPerformanceResponse>
  },
  getAgencyAttributionChain: (
    conversionId: string,
    params?: { model?: AttributionModel },
  ) => {
    const qs = params?.model ? `?model=${encodeURIComponent(params.model)}` : ''
    return fetchJson(
      `/agency/analytics/attribution-chain/${encodeURIComponent(conversionId)}${qs}`,
    ) as Promise<AgencyAttributionChainResponse>
  },
  materialiseAgencyAttribution: (body?: {
    contact_id?: string
    correlation_id?: string
    attribute?: boolean
    models?: AttributionModel[]
  }) =>
    fetchJson('/agency/attribution/materialise', {
      method: 'POST',
      body: JSON.stringify(body || {}),
    }),
  recomputeAgencyAttribution: (body: { model: AttributionModel; conversion_id?: string }) =>
    fetchJson('/agency/attribution/recompute', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  getPublishingCalendar: (params?: {
    from?: string
    to?: string
    status?: string
    kind?: string
    campaign_id?: string
    property_id?: string
    agent_id?: string
    channel_connection_id?: string
    office?: string
    limit?: number
  }) => {
    const qs = params
      ? '?' + new URLSearchParams(
          Object.entries(params)
            .filter(([, v]) => v != null && v !== '')
            .map(([k, v]) => [k, String(v)]),
        ).toString()
      : ''
    return fetchJson(`/agency/publishing/calendar${qs}`) as Promise<PublishingCalendarResponse>
  },
  reschedulePublishingExecution: (
    id: string,
    body: { scheduled_at: string; recurrence?: string | null },
  ) =>
    fetchJson(`/agency/publishing/executions/${encodeURIComponent(id)}/reschedule`, {
      method: 'POST',
      body: JSON.stringify(body),
    }) as Promise<{ execution: CalendarExecution }>,
  validatePublishingExecution: (id: string) =>
    fetchJson(`/agency/publishing/executions/${encodeURIComponent(id)}/validate`, {
      method: 'POST',
      body: JSON.stringify({}),
    }) as Promise<PublishingValidationResponse>,
  previewPublishingExecution: (id: string) =>
    fetchJson(
      `/agency/publishing/executions/${encodeURIComponent(id)}/preview`,
    ) as Promise<PublishingPreviewResponse>,
  bulkReschedulePublishingExecutions: (body: {
    execution_ids: string[]
    scheduled_at: string
    recurrence?: string | null
  }) =>
    fetchJson('/agency/publishing/executions/bulk-reschedule', {
      method: 'POST',
      body: JSON.stringify(body),
    }) as Promise<PublishingBulkActionResponse>,
  bulkCancelPublishingExecutions: (body: { execution_ids: string[] }) =>
    fetchJson('/agency/publishing/executions/bulk-cancel', {
      method: 'POST',
      body: JSON.stringify(body),
    }) as Promise<PublishingBulkActionResponse>,

  getAgencyAgentLeaderboard: (params?: {
    start_date?: string
    end_date?: string
    metric?: AgencyLeaderboardMetric
  }) =>
    fetchJson(
      `/agency/analytics/agent-leaderboard${params ? '?' + new URLSearchParams(params).toString() : ''}`,
    ) as Promise<AgencyAgentLeaderboardResponse>,
  getAgencyListingsPerformance: (params?: {
    start_date?: string
    end_date?: string
    agent_id?: string
    area?: string
    property_type?: string
  }) =>
    fetchJson(
      `/agency/analytics/listings-performance${params ? '?' + new URLSearchParams(params).toString() : ''}`,
    ) as Promise<AgencyListingsPerformanceResponse>,
  getAgencyReportsHome: () =>
    fetchJson('/agency/analytics/reports-home') as Promise<AgencyReportsHomeResponse>,
  getCommunicationsAnalytics: (params?: { start_date?: string; end_date?: string; scope?: 'all'; agency_id?: string }) =>
    fetchJson(`/analytics/communications${params ? '?' + new URLSearchParams(params).toString() : ''}`),
  getAgencyLeadFunnel: (params?: {
    start_date?: string
    end_date?: string
    source?: string
    agent_id?: string
    area?: string
  }) => {
    const qs = params ? '?' + new URLSearchParams(Object.entries(params).filter(([, v]) => v != null && v !== '').map(([k, v]) => [k, String(v)])).toString() : ''
    return fetchJson(`/agency/analytics/lead-funnel${qs}`) as Promise<AgencyLeadFunnelResponse>
  },
  getAgencyRevenueAttribution: (params?: {
    start_date?: string
    end_date?: string
    channel?: string
    agent_id?: string
    campaign_id?: string
  }) => {
    const qs = params
      ? '?' + new URLSearchParams(
        Object.entries(params).filter(([, v]) => v != null && v !== '').map(([k, v]) => [k, String(v)]),
      ).toString()
      : ''
    return fetchJson(`/agency/analytics/revenue-attribution${qs}`) as Promise<AgencyRevenueAttributionResponse>
  },
  getSourcePerformance: (params?: { start_date?: string; end_date?: string; scope?: 'all'; agency_id?: string }): Promise<SourcePerformanceResponse> =>
    fetchJson(`/analytics/source-performance${params ? '?' + new URLSearchParams(params).toString() : ''}`),
  trackEvent: (data: Record<string, unknown>) =>
    fetchJson('/white-label/analytics', { method: 'POST', body: JSON.stringify(data) }),
  getAnalytics: () => fetchJson('/white-label/analytics'),
  getAgencyWhiteLabelAnalytics: (params?: { start_date?: string; end_date?: string }) => {
    const query = params ? new URLSearchParams(
      Object.entries(params).filter(([, value]) => value != null && value !== '') as [string, string][],
    ).toString() : ''
    return fetchJson(`/agency/white-label/analytics${query ? `?${query}` : ''}`) as Promise<AgencyWhiteLabelAnalyticsResponse>
  },

  // Public pages
  getPublicAgency: (id: string) => fetchJson(`/public/agencies/${id}`),
  getPublicAgentPortfolio: (id: string) => fetchJson(`/public/agents/${id}/portfolio`),
  getPublicSiteBySubdomain: (subdomain: string) => fetchJson(`/public/sites/by-subdomain/${subdomain}`),
  getPublicSiteProperty: (subdomain: string, propertyId: string) =>
    fetchJson(`/public/sites/by-subdomain/${subdomain}/properties/${propertyId}`),
  trackPublicSiteEvent: (subdomain: string, data: Record<string, unknown>) =>
    fetchJson(`/public/sites/by-subdomain/${subdomain}/events`, { method: 'POST', body: JSON.stringify(data) }),

  // Audiences (Wave 1D)
  getAudiences: (params?: Record<string, string>) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : ''
    return fetchJson(`/audiences${qs}`)
  },
  getAudience: (id: string) => fetchJson(`/audiences/${id}`),
  createAudience: (data: Record<string, unknown>) =>
    fetchJson('/audiences', { method: 'POST', body: JSON.stringify(data) }),
  updateAudience: (id: string, data: Record<string, unknown>) =>
    fetchJson(`/audiences/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteAudience: (id: string) => fetchJson(`/audiences/${id}`, { method: 'DELETE' }),
  resolveAudience: (id: string, data?: Record<string, unknown>) =>
    fetchJson(`/audiences/${id}/resolve`, { method: 'POST', body: JSON.stringify(data ?? {}) }),

  // Experiments (Wave 2D)
  getExperiments: (params?: {
    campaign_id?: string
    status?: ExperimentStatus
    dimension?: ExperimentDimension
  }) => {
    const qs = params
      ? '?' + new URLSearchParams(
          Object.entries(params)
            .filter(([, v]) => v != null && v !== '')
            .map(([k, v]) => [k, String(v)]),
        ).toString()
      : ''
    return fetchJson(`/agency/experiments${qs}`) as Promise<{ experiments: Experiment[] }>
  },
  getExperiment: (id: string) =>
    fetchJson(`/agency/experiments/${encodeURIComponent(id)}`) as Promise<Experiment>,
  createExperiment: (data: {
    campaign_id?: string | null
    dimension: ExperimentDimension
    variants: ExperimentVariant[]
    allocation?: ExperimentAllocation
    holdout_pct?: number
    goal_event: string
    status?: ExperimentStatus
    data?: Record<string, unknown>
  }) =>
    fetchJson('/agency/experiments', {
      method: 'POST',
      body: JSON.stringify(data),
    }) as Promise<Experiment>,
  updateExperiment: (id: string, data: Record<string, unknown>) =>
    fetchJson(`/agency/experiments/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }) as Promise<Experiment>,
  startExperiment: (id: string) =>
    fetchJson(`/agency/experiments/${encodeURIComponent(id)}/start`, {
      method: 'POST',
      body: '{}',
    }) as Promise<Experiment>,
  assignExperimentContact: (id: string, contactId: string) =>
    fetchJson(`/agency/experiments/${encodeURIComponent(id)}/assign`, {
      method: 'POST',
      body: JSON.stringify({ contact_id: contactId }),
    }),
  getExperimentAssignments: (id: string) =>
    fetchJson(`/agency/experiments/${encodeURIComponent(id)}/assignments`) as Promise<{
      assignments: ExperimentAssignment[]
    }>,
  getExperimentResults: (id: string, params?: { persist?: boolean }) => {
    const qs = params?.persist ? '?persist=1' : ''
    return fetchJson(
      `/agency/experiments/${encodeURIComponent(id)}/results${qs}`,
    ) as Promise<ExperimentResults>
  },
  concludeExperiment: (
    id: string,
    body?: { winner_variant?: string | null; require_significance?: boolean },
  ) =>
    fetchJson(`/agency/experiments/${encodeURIComponent(id)}/conclude`, {
      method: 'POST',
      body: JSON.stringify(body || {}),
    }) as Promise<{ experiment: Experiment; results: ExperimentResults }>,

  // Journeys (Wave 1A canonical) + legacy Campaigns aliases
  getJourneys: (params?: Record<string, string>) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : ''
    return fetchJson(`/journeys${qs}`)
  },
  getJourney: (id: string) => fetchJson(`/journeys/${id}`),
  createJourney: (data: Record<string, unknown>) =>
    fetchJson('/journeys', { method: 'POST', body: JSON.stringify(data) }),
  updateJourney: (id: string, data: Record<string, unknown>) =>
    fetchJson(`/journeys/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  enrollContactInJourney: (id: string, contactId: string) =>
    fetchJson(`/journeys/${id}/enroll`, { method: 'POST', body: JSON.stringify({ contact_id: contactId }) }),
  getJourneyRuns: (id: string) => fetchJson(`/journeys/${id}/runs`),
  getJourneyRun: (runId: string) => fetchJson(`/journey-runs/${runId}`),
  advanceJourneyRun: (journeyId: string, runId: string) =>
    fetchJson(`/journeys/${journeyId}/runs/${runId}/advance`, { method: 'POST', body: '{}' }),

  // ContactPolicy (Wave 2E)
  getContactPolicies: (params?: Record<string, string>) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : ''
    return fetchJson(`/contact-policies${qs}`) as Promise<ContactPolicy[]>
  },
  getContactPolicy: (id: string) =>
    fetchJson(`/contact-policies/${id}`) as Promise<ContactPolicy>,
  upsertContactPolicy: (data: {
    id?: string
    scope?: 'agency' | 'agent'
    name?: string
    rules: ContactPolicyRules
    data?: Record<string, unknown>
  }) =>
    fetchJson('/contact-policies', { method: 'PUT', body: JSON.stringify(data) }) as Promise<ContactPolicy>,

  // Campaigns / Drip sequences (legacy — prefer journeys)
  getCampaigns: (params?: Record<string, string>) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : ''
    return fetchJson(`/campaigns${qs}`)
  },
  getCampaign: (id: string) => fetchJson(`/campaigns/${id}`),
  createCampaign: (data: Record<string, unknown>) =>
    fetchJson('/campaigns', { method: 'POST', body: JSON.stringify(data) }),
  updateCampaign: (id: string, data: Record<string, unknown>) =>
    fetchJson(`/campaigns/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteCampaign: (id: string) => fetchJson(`/campaigns/${id}`, { method: 'DELETE' }),
  enrollContactInCampaign: (id: string, contactId: string) =>
    fetchJson(`/campaigns/${id}/enroll`, { method: 'POST', body: JSON.stringify({ contact_id: contactId }) }),
  autoEnrollCampaign: (id: string, maxContacts?: number) =>
    fetchJson(`/campaigns/${id}/auto-enroll`, { method: 'POST', body: JSON.stringify({ max_contacts: maxContacts }) }),
  getCampaignEnrollments: (id: string) => fetchJson(`/campaigns/${id}/enrollments`),
  getEnrollments: (params?: Record<string, string>) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : ''
    return fetchJson(`/enrollments${qs}`)
  },
  getEnrollment: (id: string) => fetchJson(`/enrollments/${id}`),
  updateEnrollment: (id: string, data: Record<string, unknown>) =>
    fetchJson(`/enrollments/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  runCampaignScheduler: (limit?: number) =>
    fetchJson('/campaigns/run-scheduler', { method: 'POST', body: JSON.stringify({ limit }) }),

  // GDPR / data subject rights
  deleteContact: (id: string) => fetchJson(`/contacts/${id}`, { method: 'DELETE' }),
  exportContact: (id: string) =>
    fetchJson(`/contacts/${id}/export`, { headers: { Accept: 'application/json' } }),

  // Property CTAs
  getPropertyCtaConfig: (id: string) => fetchJson(`/properties/${id}/cta-config`),
  updateAgentCtaConfig: (config: Record<string, unknown>) =>
    fetchJson('/auth/me', { method: 'PUT', body: JSON.stringify({ cta_config: config }) }),
  updateAgencyCtaConfig: (id: string, config: Record<string, unknown>) =>
    fetchJson(`/agencies/${id}`, { method: 'PUT', body: JSON.stringify({ cta_config: config }) }),
  scheduleCall: (data: Record<string, unknown>) => fetchJson('/tasks', { method: 'POST', body: JSON.stringify(data) }),
  bookViewing: (data: Record<string, unknown>) => fetchJson('/viewings', { method: 'POST', body: JSON.stringify(data) }),

  // WhatsApp Listings Module
  getWhatsAppListingsHealth: () => fetchJson('/admin/whatsapp-listings/health'),
  getWhatsAppListingsDrafts: () => fetchJson('/agent/whatsapp-listings/drafts'),
  getWhatsAppListingsDraft: (id: string) => fetchJson(`/agent/whatsapp-listings/drafts/${id}`),
  approveWhatsAppListingsDraft: (id: string, publishSocial = false) =>
    fetchJson(`/agent/whatsapp-listings/drafts/${id}/approve`, { method: 'POST', body: JSON.stringify({ publish_social: publishSocial }) }),
  discardWhatsAppListingsDraft: (id: string) =>
    fetchJson(`/agent/whatsapp-listings/drafts/${id}/discard`, { method: 'POST', body: '{}' }),
  reprocessWhatsAppListingsDraft: (id: string) =>
    fetchJson(`/agent/whatsapp-listings/drafts/${id}/reprocess`, { method: 'POST', body: '{}' }),
  getWhatsAppListingsAgentSettings: () =>
    fetchJson('/agent/whatsapp-listings/settings') as Promise<WhatsAppIntakeAgentSettings>,
  updateWhatsAppListingsAgentSettings: (data: WhatsAppIntakeAgentSettingsPatch) =>
    fetchJson('/agent/whatsapp-listings/settings', {
      method: 'PATCH',
      body: JSON.stringify(data),
    }) as Promise<WhatsAppIntakeAgentSettings>,
  getWhatsAppListingsAgentAnalytics: (
    range: WhatsAppIntakeAnalyticsRange = '30d',
  ): Promise<WhatsAppIntakeAnalytics> =>
    fetchJson(`/agent/whatsapp-listings/analytics?range=${range}`),
  getWhatsAppListingsAgentCredits: () => fetchJson('/agent/credits/balance'),
  getWhatsAppListingsAgentTransactions: (limit = 100) =>
    fetchJson(`/agent/credits/transactions?limit=${limit}`),
  topUpWhatsAppListingsAgentCredits: (amountUsd: number, paymentIntentId?: string) =>
    fetchJson('/agent/credits/top-up', { method: 'POST', body: JSON.stringify({ amount_usd: amountUsd, stripe_payment_intent_id: paymentIntentId }) }),

  getAdminWhatsAppListingsUsage: () => fetchJson('/admin/whatsapp-listings/usage'),
  getAdminWhatsAppListingsAuditLog: (params?: Record<string, string>) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : ''
    return fetchJson(`/admin/whatsapp-listings/audit-log${qs}`)
  },
  adminWhatsAppListingsAuditLogCsvPath: (params?: Record<string, string | undefined>) => {
    const entries = Object.entries(params || {}).filter(([, v]) => v != null && v !== '') as [string, string][]
    const qs = entries.length ? `?${new URLSearchParams(entries)}` : ''
    return `${API_BASE}/admin/whatsapp-listings/audit-log.csv${qs}`
  },
  getAdminWhatsAppListingsEntitlements: (params?: Record<string, string>) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : ''
    return fetchJson(`/admin/entitlements${qs}`)
  },
  createAdminWhatsAppListingsEntitlement: (data: Record<string, unknown>) =>
    fetchJson('/admin/entitlements', { method: 'POST', body: JSON.stringify(data) }),
  deleteAdminWhatsAppListingsEntitlement: (id: string) =>
    fetchJson(`/admin/entitlements/${id}`, { method: 'DELETE' }),
  grantAdminWhatsAppListingsCredits: (body: {
    scope: 'agent' | 'agency'
    scope_id: string
    amount_usd: number
    reason: string
  }) =>
    fetchJson('/admin/whatsapp-listings/credits/grant', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  getAgencyWhatsAppListingsUsage: () => fetchJson('/agency/whatsapp-listings/usage'),
  getAgencyWhatsAppListingsEntitlements: () => fetchJson('/agency/entitlements'),
  createAgencyWhatsAppListingsEntitlement: (data: Record<string, unknown>) =>
    fetchJson('/agency/entitlements', { method: 'POST', body: JSON.stringify(data) }),
  updateAgencyWhatsAppListingsEntitlement: (id: string, data: Record<string, unknown>) =>
    fetchJson(`/agency/entitlements/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  getAgencyWhatsAppListingsCredits: () => fetchJson('/agency/credits/balance'),
  getAgencyWalletOverview: (): Promise<AgencyWalletOverview> =>
    fetchJson('/agency/credits/wallet-overview') as Promise<AgencyWalletOverview>,
  getAgencyWalletSettings: (): Promise<{ settings: AgencyWalletSettings }> =>
    fetchJson('/agency/credits/wallet-settings') as Promise<{ settings: AgencyWalletSettings }>,
  updateAgencyWalletSettings: (lowBalanceAlertThreshold: number): Promise<{ settings: AgencyWalletSettings }> =>
    fetchJson('/agency/credits/wallet-settings', {
      method: 'PUT',
      body: JSON.stringify({ low_balance_alert_threshold: lowBalanceAlertThreshold }),
    }) as Promise<{ settings: AgencyWalletSettings }>,
  getAgencyFeatureQuotas: (): Promise<AgencyFeatureQuotasResponse> =>
    fetchJson('/agency/credits/feature-quotas') as Promise<AgencyFeatureQuotasResponse>,
  getAgencyWhatsAppListingsTransactions: (limit = 100) =>
    fetchJson(`/agency/credits/transactions?limit=${limit}`),
  topUpAgencyWhatsAppListingsCredits: (amountUsd: number, paymentIntentId?: string) =>
    fetchJson('/agency/credits/top-up', { method: 'POST', body: JSON.stringify({ amount_usd: amountUsd, stripe_payment_intent_id: paymentIntentId }) }),
  allocateAgencyWhatsAppListingsCredits: (agentId: string, amountUsd: number) =>
    fetchJson('/agency/credits/allocate', { method: 'POST', body: JSON.stringify({ agent_id: agentId, amount_usd: amountUsd }) }),
  getAgencyCreditAllocationRules: (): Promise<{
    rules: AgencyCreditAllocationRules
    agents: AgencyCreditAllocationAgent[]
  }> => fetchJson('/agency/credits/allocation-rules'),
  updateAgencyCreditAllocationRules: (payload: {
    mode: AgencyCreditAllocationMode
    overrides: Array<{
      agent_user_id: string
      percentage?: number | null
      cap_usd?: number | null
    }>
  }): Promise<{ rules: AgencyCreditAllocationRules }> =>
    fetchJson('/agency/credits/allocation-rules', {
      method: 'PUT',
      body: JSON.stringify(payload),
    }),

  // Reminder policies (AGT-TSK-003)
  getReminderPolicies: (params?: Record<string, string>) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : ''
    return fetchJson(`/reminder-policies${qs}`) as Promise<ReminderPolicy[]>
  },
  createReminderPolicy: (data: ReminderPolicyInput) =>
    fetchJson('/reminder-policies', { method: 'POST', body: JSON.stringify(data) }) as Promise<ReminderPolicy>,
  updateReminderPolicy: (id: string, data: Partial<ReminderPolicyInput>) =>
    fetchJson(`/reminder-policies/${id}`, { method: 'PATCH', body: JSON.stringify(data) }) as Promise<ReminderPolicy>,
  deleteReminderPolicy: (id: string) => fetchJson(`/reminder-policies/${id}`, { method: 'DELETE' }) as Promise<{ success: boolean }>,

  // Admin audit log
  getAdminAuditLog: (params?: Record<string, string>) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : ''
    return fetchJson(`/admin/audit-log${qs}`)
  },
  runAuditLogRetention: () => fetchJson('/admin/audit-log/retention', { method: 'POST', body: '{}' }),
  getAuditRetentionPolicy: (): Promise<AuditRetentionPolicyResponse> =>
    fetchJson('/admin/audit-log/retention-policy'),
  updateAuditRetentionPolicy: (
    body: AuditRetentionPolicyInput,
  ): Promise<{ policy: AuditRetentionPolicy }> =>
    fetchJson('/admin/audit-log/retention-policy', { method: 'PUT', body: JSON.stringify(body) }),
  promoteUser: (id: string, role: 'platform_admin' | null) =>
    fetchJson(`/admin/users/${id}/promote`, { method: 'POST', body: JSON.stringify({ role }) }),

  // Area Intelligence (public)
  listAreas: (params?: Record<string, string>) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : ''
    return fetchJson(`/areas${qs}`)
  },
  getArea: (slug: string) => fetchJson(`/areas/${slug}`),
  getAreaScores: (slug: string) => fetchJson(`/areas/${slug}/scores`),
  getAreaGoogleScores: (slug: string) => fetchJson(`/areas/${slug}/google-scores`),
  getListingArea: (listingId: string): Promise<{
    area: { id: string; slug: string; name: string; name_ar: string | null } | null
  }> => fetchJson(`/listings/${listingId}/area`),
  getAreaProperties: (slug: string, params?: Record<string, string>) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : ''
    return fetchJson(`/areas/${slug}/properties${qs}`)
  },
  compareAreas: (slug: string, withSlug: string) =>
    fetchJson(`/areas/${slug}/comparison?with=${encodeURIComponent(withSlug)}`),

  // Area Intelligence (inspector)
  getInspectorAssignments: (params?: Record<string, string>) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : ''
    return fetchJson(`/inspector/assignments${qs}`)
  },
  getInspectorAssignment: (id: string): Promise<InspectorAssignmentDetail> =>
    fetchJson(`/inspector/assignments/${encodeURIComponent(id)}`),
  startInspectorAssignment: (id: string) =>
    fetchJson(`/inspector/assignments/${id}/start`, { method: 'POST', body: '{}' }),
  getInspectorSubmissions: (params?: Record<string, string>) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : ''
    return fetchJson(`/inspector/submissions${qs}`)
  },
  createInspectorSubmission: (data: InspectionSubmissionInput): Promise<InspectionSubmission> =>
    fetchJson('/inspector/submissions', { method: 'POST', body: JSON.stringify(data) }),

  // Area Intelligence (admin)
  listAdminAreas: (params?: Record<string, string>) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : ''
    return fetchJson(`/admin/areas${qs}`)
  },
  createAdminArea: (data: Record<string, unknown>) =>
    fetchJson('/admin/areas', { method: 'POST', body: JSON.stringify(data) }),
  getAdminArea: (id: string) => fetchJson(`/admin/areas/${id}`) as Promise<AdminArea>,
  getAdminAreaDetail: (id: string) => fetchJson(`/admin/areas/${id}/detail`) as Promise<AdminAreaDetailResponse>,
  updateAdminArea: (id: string, data: Record<string, unknown>) =>
    fetchJson(`/admin/areas/${id}`, { method: 'PUT', body: JSON.stringify(data) }) as Promise<AdminArea>,
  deleteAdminArea: (id: string) => fetchJson(`/admin/areas/${id}`, { method: 'DELETE' }),
  listAdminAreaSources: (areaId: string) =>
    fetchJson(`/admin/areas/${areaId}/sources`) as Promise<{ items: AdminAreaSource[] }>,
  createAdminAreaSource: (areaId: string, data: Record<string, unknown>) =>
    fetchJson(`/admin/areas/${areaId}/sources`, { method: 'POST', body: JSON.stringify(data) }) as Promise<AdminAreaSource>,
  updateAdminAreaSource: (areaId: string, sourceId: string, data: Record<string, unknown>) =>
    fetchJson(`/admin/areas/${areaId}/sources/${sourceId}`, { method: 'PUT', body: JSON.stringify(data) }) as Promise<AdminAreaSource>,
  deleteAdminAreaSource: (areaId: string, sourceId: string) =>
    fetchJson(`/admin/areas/${areaId}/sources/${sourceId}`, { method: 'DELETE' }),
  enableAreaScoring: (id: string) =>
    fetchJson(`/admin/areas/${id}/enable-scoring`, { method: 'POST', body: '{}' }),
  disableAreaScoring: (id: string) =>
    fetchJson(`/admin/areas/${id}/disable-scoring`, { method: 'POST', body: '{}' }),
  listAdminDimensions: (params?: Record<string, string>) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : ''
    return fetchJson(`/admin/scoring/dimensions${qs}`)
  },
  createAdminDimension: (data: Record<string, unknown>) =>
    fetchJson('/admin/scoring/dimensions', { method: 'POST', body: JSON.stringify(data) }),
  updateAdminDimension: (id: string, data: Record<string, unknown>) =>
    fetchJson(`/admin/scoring/dimensions/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteAdminDimension: (id: string) =>
    fetchJson(`/admin/scoring/dimensions/${id}`, { method: 'DELETE' }),
  listAdminSourceTypes: (params?: Record<string, string>) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : ''
    return fetchJson(`/admin/scoring/source-types${qs}`)
  },
  createAdminSourceType: (data: Record<string, unknown>) =>
    fetchJson('/admin/scoring/source-types', { method: 'POST', body: JSON.stringify(data) }),
  updateAdminSourceType: (id: string, data: Record<string, unknown>) =>
    fetchJson(`/admin/scoring/source-types/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteAdminSourceType: (id: string) =>
    fetchJson(`/admin/scoring/source-types/${id}`, { method: 'DELETE' }),
  listAdminAiConfigs: (params?: Record<string, string>): Promise<{
    items: import('@/pages/admin/scoring/aiConfigTypes').AiScoringConfig[]
  }> => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : ''
    return fetchJson(`/admin/scoring/ai-configs${qs}`)
  },
  createAdminAiConfig: (data: import('@/pages/admin/scoring/aiConfigTypes').AiScoringConfigInput): Promise<
    import('@/pages/admin/scoring/aiConfigTypes').AiScoringConfig
  > =>
    fetchJson('/admin/scoring/ai-configs', { method: 'POST', body: JSON.stringify(data) }),
  updateAdminAiConfig: (
    id: string,
    data: Partial<import('@/pages/admin/scoring/aiConfigTypes').AiScoringConfigInput>,
  ): Promise<import('@/pages/admin/scoring/aiConfigTypes').AiScoringConfig> =>
    fetchJson(`/admin/scoring/ai-configs/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  listAdminAiConfigVersions: (id: string): Promise<{
    items: import('@/pages/admin/scoring/aiConfigTypes').AiScoringConfigVersion[]
  }> => fetchJson(`/admin/scoring/ai-configs/${encodeURIComponent(id)}/versions`),
  previewAdminAiConfig: (
    id: string,
    data: { area_id: string; dimension_id: string },
  ): Promise<import('@/pages/admin/scoring/aiConfigTypes').AiConfigPreviewResult> =>
    fetchJson(`/admin/scoring/ai-configs/${encodeURIComponent(id)}/preview`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  deleteAdminAiConfig: (id: string) =>
    fetchJson(`/admin/scoring/ai-configs/${id}`, { method: 'DELETE' }),
  listAdminSignals: (params?: Record<string, string>) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : ''
    return fetchJson(`/admin/scoring/signals${qs}`)
  },
  verifyAdminSignal: (id: string, notes?: string) =>
    fetchJson(`/admin/scoring/signals/${id}/verify`, { method: 'POST', body: JSON.stringify({ notes }) }),
  rejectAdminSignal: (id: string, reason?: string) =>
    fetchJson(`/admin/scoring/signals/${id}/reject`, { method: 'POST', body: JSON.stringify({ reason }) }),
  calculateAdminScores: (areaId: string) =>
    fetchJson('/admin/scoring/calculate', { method: 'POST', body: JSON.stringify({ area_id: areaId }) }),
  recalculateAdminScores: (body: Record<string, unknown>) =>
    fetchJson('/admin/scoring/calculate', { method: 'POST', body: JSON.stringify(body) }),
  refreshAreaGoogleSignals: (areaId: string): Promise<{
    area_id: string; source_types: number; signals_before?: number; signals_after: number; signals_created?: number
  }> => fetchJson(`/admin/areas/${areaId}/refresh-google-signals`, { method: 'POST', body: '{}' }),
  overrideAdminScore: (data: Record<string, unknown>) =>
    fetchJson('/admin/scoring/override', { method: 'POST', body: JSON.stringify(data) }),
  getAdminAreaCurrentScores: (areaId: string) =>
    fetchJson(`/admin/scoring/areas/${encodeURIComponent(areaId)}/current-scores`),
  getAdminGoogleUsage: (params?: Record<string, string>) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : ''
    return fetchJson(`/admin/google-usage${qs}`)
  },
  getGoogleUsageSummary: (params?: { days?: number; top?: number }): Promise<{ summary: GoogleUsageSummary }> => {
    const qs = new URLSearchParams()
    if (params?.days) qs.set('days', String(params.days))
    if (params?.top) qs.set('top', String(params.top))
    const suffix = qs.toString() ? `?${qs.toString()}` : ''
    return fetchJson(`/admin/google-usage/summary${suffix}`)
  },
  getGoogleBudget: (): Promise<GoogleBudgetResponse> => fetchJson('/admin/google-usage/budget'),
  updateGoogleBudget: (body: GoogleBudgetInput): Promise<{ config: GoogleBudgetConfig }> =>
    fetchJson('/admin/google-usage/budget', { method: 'PUT', body: JSON.stringify(body) }),

  // Market Pricing (public)
  getPricingAnalysis: (propertyId: string, params?: Record<string, string>): Promise<PricingAnalysis> => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : ''
    return fetchJson(`/pricing/analysis/${propertyId}${qs}`)
  },
  getPricingComparables: (propertyId: string): Promise<Record<string, unknown>[]> => fetchJson(`/pricing/comparables/${propertyId}`),
  /**
   * Issue 551 — value an ad-hoc subject that is not a saved listing. The response
   * is a calculation, not a persisted record: distribution + comparables, or
   * `sufficient: false` with the engine's refusal reason.
   */
  analyseSubject: (data: SubjectAnalysisRequest): Promise<SubjectAnalysis> =>
    fetchJson('/pricing/analyse', { method: 'POST', body: JSON.stringify(data) }),
  getPricingTrends: (areaId: string, propertyType: string): Promise<PricingTrendSnapshot[]> =>
    fetchJson(`/pricing/trends/${areaId}?property_type=${encodeURIComponent(propertyType)}`),
  reportComparable: (data: Record<string, unknown>) =>
    fetchJson('/pricing/report-comparable', { method: 'POST', body: JSON.stringify(data) }),
  uploadPricingEvidence: async (
    file: File,
  ): Promise<{
    id: string
    url: string
    sha256: string
    content_type: string
    size_bytes: number
    scan_status?: string
    filename?: string
  }> => {
    const form = new FormData()
    form.append('file', file)
    const token = getToken()
    const uploadHeaders: Record<string, string> = {
      'X-Wingcaster-Env': readWingcasterEnvHeader(),
    }
    if (token) uploadHeaders.Authorization = `Bearer ${token}`
    const elevated = getElevatedToken()
    if (elevated) uploadHeaders['X-Elevated-Token'] = elevated
    const res = await fetch(`${API_BASE}/pricing/evidence-uploads`, {
      method: 'POST',
      headers: uploadHeaders,
      body: form,
    })
    const bodyText = await res.text()
    const parsed = (() => {
      try {
        return bodyText ? JSON.parse(bodyText) : null
      } catch {
        return null
      }
    })()
    if (!res.ok) {
      const error = new Error(parsed?.error || `Upload failed (${res.status})`) as Error &
        Record<string, unknown>
      Object.assign(error, parsed || {}, { status: res.status })
      throw error
    }
    if (!parsed) throw new Error('Evidence upload returned non-JSON')
    return parsed
  },
  deletePricingEvidence: (id: string) =>
    fetchJson(`/pricing/evidence-uploads/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  getMyComparableReports: (): Promise<ComparableReportSummary[]> => fetchJson('/pricing/my-comparable-reports'),
  getMyAgentPriceReports: (): Promise<AgentPriceReport[]> => fetchJson('/pricing/my-agent-price-reports'),
  /** AGT-REC-002 — preferred user-scoped by-id (falls back in hooks when absent). */
  getMyComparableReportOutcome: (reportId: string) =>
    fetchJson(`/users/me/comparable-reports/${encodeURIComponent(reportId)}`),
  withdrawComparableReport: (reportId: string) =>
    fetchJson(`/users/me/comparable-reports/${encodeURIComponent(reportId)}/withdraw`, {
      method: 'POST',
      body: '{}',
    }),
  /** AGT-REC-003 — preferred user-scoped by-id (falls back in hooks when absent). */
  getMyPriceReportOutcome: (reportId: string) =>
    fetchJson(`/users/me/price-reports/${encodeURIComponent(reportId)}`),
  withdrawPriceReport: (reportId: string) =>
    fetchJson(`/users/me/price-reports/${encodeURIComponent(reportId)}/withdraw`, {
      method: 'POST',
      body: '{}',
    }),
  getAgentPricingPortfolio: (): Promise<AgentPricingPortfolio> => fetchJson('/agent/pricing/portfolio'),
  getAgencyPricingPortfolio: (): Promise<AgencyPricingPortfolio> => fetchJson('/agency/pricing/portfolio'),
  getAgencyBulkPriceAdjustActive: (): Promise<{ active: BulkPriceActiveAdjustment | null }> =>
    fetchJson('/agency/pricing/bulk-adjust/active'),
  previewAgencyBulkPriceAdjust: (payload: {
    listing_ids: string[]
    strategy: BulkPriceStrategy
    strategy_value?: number | null
  }): Promise<{ preview: BulkPricePreview; missing: string[]; safety: { ok: boolean; code?: string; error?: string } }> =>
    fetchJson('/agency/pricing/bulk-adjust/preview', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  applyAgencyBulkPriceAdjust: (payload: {
    listing_ids: string[]
    strategy: BulkPriceStrategy
    strategy_value?: number | null
    reversal_hours?: number
    confirmation: string
  }): Promise<{ adjustment: { id: string; status: string; reversal_deadline_at: string; listing_count: number }; missing: string[] }> =>
    fetchJson('/agency/pricing/bulk-adjust', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  revertAgencyBulkPriceAdjust: (adjustmentId: string): Promise<{ id: string; status: string; reverted_at: string }> =>
    fetchJson(`/agency/pricing/bulk-adjust/${encodeURIComponent(adjustmentId)}/revert`, {
      method: 'POST',
      body: '{}',
    }),
  getAgencyPricingComparables: (filters: AgencyComparablesFilters = {}): Promise<AgencyComparablesResponse> => {
    const params = new URLSearchParams()
    for (const [key, value] of Object.entries(filters)) {
      if (value !== undefined && value !== '') params.set(key, String(value))
    }
    const query = params.toString()
    return fetchJson(`/agency/pricing/comparables${query ? `?${query}` : ''}`)
  },

  // AGN-SET-001 — Agency settings home overview.
  getAgencySettingsOverview: (): Promise<AgencySettingsOverview> => fetchJson('/agency/settings/overview'),
  keepAgentListingPrice: (propertyId: string, reason?: string): Promise<PricingDecision> =>
    fetchJson(`/agent/pricing/properties/${propertyId}/keep-price`, { method: 'POST', body: JSON.stringify({ reason }) }),
  adjustAgentListingPrice: (propertyId: string, newPrice: number, reason?: string) =>
    fetchJson(`/agent/pricing/properties/${propertyId}/adjust-price`, { method: 'POST', body: JSON.stringify({ new_price: newPrice, reason }) }),

  // Market Pricing (admin)
  getAdminPricingConfigs: () => fetchJson('/admin/pricing/configs'),
  createAdminPricingConfig: (data: Record<string, unknown>) =>
    fetchJson('/admin/pricing/configs', { method: 'POST', body: JSON.stringify(data) }),
  updateAdminPricingConfig: (id: string, data: Record<string, unknown>) =>
    fetchJson(`/admin/pricing/configs/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteAdminPricingConfig: (id: string) =>
    fetchJson(`/admin/pricing/configs/${id}`, { method: 'DELETE' }),

  getAdminPricingSources: () => fetchJson('/admin/pricing/sources'),
  createAdminPricingSource: (data: Record<string, unknown>) =>
    fetchJson('/admin/pricing/sources', { method: 'POST', body: JSON.stringify(data) }),
  updateAdminPricingSource: (source: string, data: Record<string, unknown>) =>
    fetchJson(`/admin/pricing/sources/${source}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteAdminPricingSource: (source: string) =>
    fetchJson(`/admin/pricing/sources/${source}`, { method: 'DELETE' }),

  getAdminPricingCurrencyRates: () => fetchJson('/admin/pricing/currency-rates'),
  createAdminPricingCurrencyRate: (data: Record<string, unknown>) =>
    fetchJson('/admin/pricing/currency-rates', { method: 'POST', body: JSON.stringify(data) }),
  updateAdminPricingCurrencyRate: (id: string, data: Record<string, unknown>) =>
    fetchJson(`/admin/pricing/currency-rates/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteAdminPricingCurrencyRate: (id: string) =>
    fetchJson(`/admin/pricing/currency-rates/${id}`, { method: 'DELETE' }),
  refreshAdminPricingCurrencyRates: () =>
    fetchJson('/admin/pricing/currency-rates/refresh', { method: 'POST', body: '{}' }),

  importAdminPricingCsv: (csvText: string, filename?: string) =>
    fetchJson('/admin/pricing/external-comparables/import-csv', {
      method: 'POST',
      body: JSON.stringify({ csv_text: csvText, filename }),
    }),
  getAdminPricingCsvImportLogs: () => fetchJson('/admin/pricing/csv-import-logs'),

  getAdminPricingNormalizationRules: () => fetchJson('/admin/pricing/normalization-rules'),
  createAdminPricingNormalizationRule: (data: Record<string, unknown>) =>
    fetchJson('/admin/pricing/normalization-rules', { method: 'POST', body: JSON.stringify(data) }),
  updateAdminPricingNormalizationRule: (id: string, data: Record<string, unknown>) =>
    fetchJson(`/admin/pricing/normalization-rules/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteAdminPricingNormalizationRule: (id: string) =>
    fetchJson(`/admin/pricing/normalization-rules/${id}`, { method: 'DELETE' }),

  recalculateAdminPricing: (data: Record<string, unknown>) =>
    fetchJson('/admin/pricing/recalculate', { method: 'POST', body: JSON.stringify(data) }),
  createAdminPricingRecalculationJob: (data: Record<string, unknown>): Promise<PricingRecalculationJob> =>
    fetchJson('/admin/pricing/recalculation-jobs', { method: 'POST', body: JSON.stringify(data) }),
  getAdminPricingRecalculationJobs: (params?: Record<string, string>): Promise<PricingRecalculationJob[]> => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : ''
    return fetchJson(`/admin/pricing/recalculation-jobs${qs}`)
  },
  getAdminPricingRecalculationJob: (id: string, includeItems = false) =>
    fetchJson(`/admin/pricing/recalculation-jobs/${id}?include_items=${includeItems}`),
  cancelAdminPricingRecalculationJob: (id: string): Promise<PricingRecalculationJob> =>
    fetchJson(`/admin/pricing/recalculation-jobs/${id}/cancel`, { method: 'POST', body: '{}' }),
  retryAdminPricingRecalculationJob: (id: string): Promise<PricingRecalculationJob> =>
    fetchJson(`/admin/pricing/recalculation-jobs/${id}/retry-failed`, { method: 'POST', body: '{}' }),

  getAdminPricingTrends: () => fetchJson('/admin/pricing/trends'),
  runAdminPricingTrends: () => fetchJson('/admin/pricing/trends/run', { method: 'POST', body: '{}' }),

  getAdminPricingReports: () => fetchJson('/admin/pricing/reports'),
  reviewAdminPricingReport: (id: string, data: Record<string, unknown>) =>
    fetchJson(`/admin/pricing/reports/${id}/review`, { method: 'POST', body: JSON.stringify(data) }),

  // PA-PVA-011 — canonical property resolution admin
  getAdminCanonicalResolutionQueue: () => fetchJson('/admin/pricing/canonical'),
  getAdminCanonicalResolution: (id: string) => fetchJson(`/admin/pricing/canonical/${encodeURIComponent(id)}`),
  postAdminCanonicalChangePrimary: (id: string, data: Record<string, unknown>) =>
    fetchJson(`/admin/pricing/canonical/${encodeURIComponent(id)}/change-primary`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  postAdminCanonicalSplit: (id: string, data: Record<string, unknown>) =>
    fetchJson(`/admin/pricing/canonical/${encodeURIComponent(id)}/split`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  postAdminCanonicalMerge: (id: string, data: Record<string, unknown>) =>
    fetchJson(`/admin/pricing/canonical/${encodeURIComponent(id)}/merge`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  postAdminCanonicalHold: (id: string, data: Record<string, unknown>) =>
    fetchJson(`/admin/pricing/canonical/${encodeURIComponent(id)}/hold`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  // WF-05 PA-PVA-008/008b — comparable-report queue + decisions (BE-28)
  listAdminComparableReports: (params?: Record<string, string>) => {
    const qs = params && Object.keys(params).length ? `?${new URLSearchParams(params)}` : ''
    return fetchJson(`/admin/pricing/reports${qs}`)
  },
  getAdminComparableReport: (reportId: string, params?: Record<string, string>) => {
    const qs = params && Object.keys(params).length ? `?${new URLSearchParams(params)}` : ''
    return fetchJson(`/admin/pricing/reports/${reportId}${qs}`)
  },
  getAdminComparableReportReporterHistory: (reportId: string, params?: Record<string, string>) => {
    const qs = params && Object.keys(params).length ? `?${new URLSearchParams(params)}` : ''
    return fetchJson(`/admin/pricing/reports/${reportId}/reporter-history${qs}`)
  },
  getAdminComparableReportAuditTrail: (reportId: string) =>
    fetchJson(`/admin/pricing/reports/${reportId}/audit-trail`),
  getAdminComparableReportAffectedValuations: (reportId: string, params?: Record<string, string>) => {
    const qs = params && Object.keys(params).length ? `?${new URLSearchParams(params)}` : ''
    return fetchJson(`/admin/pricing/reports/${reportId}/affected-valuations${qs}`)
  },
  confirmAdminComparableReportRemove: (reportId: string, data: Record<string, unknown>) =>
    fetchJson(`/admin/pricing/reports/${reportId}/confirm-remove`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  confirmAdminComparableReportQuarantine: (reportId: string, data: Record<string, unknown>) =>
    fetchJson(`/admin/pricing/reports/${reportId}/confirm-quarantine`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  rejectAdminComparableReportAsInvalid: (reportId: string, data: Record<string, unknown>) =>
    fetchJson(`/admin/pricing/reports/${reportId}/reject-as-invalid`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  requestAdminComparableReportInfo: (reportId: string, data: Record<string, unknown>) =>
    fetchJson(`/admin/pricing/reports/${reportId}/request-info`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  undoAdminComparableReportDecision: (
    reportId: string,
    data?: { undo_token_id?: string },
  ) =>
    fetchJson(`/admin/pricing/reports/${reportId}/undo-decision`, {
      method: 'POST',
      body: JSON.stringify(data || {}),
    }),
  recallAdminComparableReportProposal: (
    reportId: string,
    data: { reason: string },
  ) =>
    fetchJson(`/admin/pricing/reports/${reportId}/recall-proposal`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  getAdminComparableReportEvidenceUrl: (reportId: string, evidenceId: string) =>
    fetchJson(
      `/admin/pricing/reports/${encodeURIComponent(reportId)}/evidence/${encodeURIComponent(evidenceId)}/url`,
    ),
  castSecondApprovalVote: (data: {
    approval_request_id: string
    decision: 'approve' | 'decline'
    notes?: string
  }) =>
    fetchJson(
      `/admin/valuation/approval-requests/${encodeURIComponent(data.approval_request_id)}/vote`,
      {
        method: 'POST',
        body: JSON.stringify({
          decision: data.decision,
          notes: data.notes,
        }),
      },
    ),
  bulkRejectAdminComparableReportsAsInvalid: (data: Record<string, unknown>) =>
    fetchJson('/admin/pricing/reports/bulk-reject-as-invalid', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  bulkRequestAdminComparableReportsInfo: (data: Record<string, unknown>) =>
    fetchJson('/admin/pricing/reports/bulk-request-info', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  // WF-06 PA-PVA-009/009b — agent price-report queue + detail
  getAdminAgentPriceReports: (params?: Record<string, string | number | undefined>) => {
    const cleaned: Record<string, string> = {}
    if (params) {
      for (const [k, v] of Object.entries(params)) {
        if (v === undefined || v === null || v === '') continue
        cleaned[k] = String(v)
      }
    }
    const qs = Object.keys(cleaned).length ? `?${new URLSearchParams(cleaned)}` : ''
    return fetchJson(`/admin/pricing/agent-price-reports${qs}`)
  },
  getAdminAgentPriceReport: (id: string) =>
    fetchJson(`/admin/pricing/agent-price-reports/${id}`),
  reviewAdminAgentPriceReport: (
    id: string,
    data: {
      status: 'verified' | 'rejected' | 'request_info'
      incorporate?: boolean
      weight?: number
      reason_code?: string
      notes?: string
    },
  ) =>
    fetchJson(`/admin/pricing/agent-price-reports/${id}/review`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  bulkReviewAdminAgentPriceReports: (data: {
    ids: string[]
    status: 'verified' | 'rejected' | 'request_info'
    reason_code?: string
    notes?: string
    incorporate?: boolean
  }) =>
    fetchJson('/admin/pricing/agent-price-reports/bulk-review', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  undoAdminAgentPriceReportReview: (id: string, data?: { undo_token_id?: string }) =>
    fetchJson(`/admin/pricing/agent-price-reports/${id}/undo-review`, {
      method: 'POST',
      body: JSON.stringify(data || {}),
    }),
  revealAdminAgentPriceReportPii: (id: string, data: { field: string; kind?: string }) =>
    fetchJson(`/admin/pricing/agent-price-reports/${id}/reveal-audit`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  getAdminAgentPriceReportEvidenceUrl: (reportId: string, evidenceId: string) =>
    fetchJson(`/admin/pricing/agent-price-reports/${reportId}/evidence/${evidenceId}/url`),
  getAdminPricingBenchmarkSeries: (segmentId: string, window = '90d') =>
    fetchJson(
      `/admin/pricing/benchmarks/${encodeURIComponent(segmentId)}/series?window=${encodeURIComponent(window)}`,
    ),
  exportAdminAgentPriceReportsCsv: async (params?: Record<string, string | number | undefined>) => {
    const cleaned: Record<string, string> = {}
    if (params) {
      for (const [k, v] of Object.entries(params)) {
        if (v === undefined || v === null || v === '') continue
        cleaned[k] = String(v)
      }
    }
    const qs = Object.keys(cleaned).length ? `?${new URLSearchParams(cleaned)}` : ''
    const url = `${API_BASE}/admin/pricing/agent-price-reports.csv${qs}`
    const res = await fetch(url, { headers: headers() })
    if (!res.ok) throw new Error(`CSV export failed (${res.status})`)
    const blob = await res.blob()
    const href = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = href
    a.download = 'agent-price-reports.csv'
    a.click()
    URL.revokeObjectURL(href)
  },

  submitAgentPriceReport: (data: Record<string, unknown>) =>
    fetchJson('/pricing/agent-price-reports', { method: 'POST', body: JSON.stringify(data) }),

  // Listings AI — draft a listing from photos.
  describeListingFromPhotos: (payload: {
    photo_urls: string[]
    hints?: {
      city?: string
      neighborhood?: string
      type?: 'sale' | 'rent'
      property_type?: string
      price?: number
      currency?: string
      notes?: string
    }
    provider?: string
    intent?: 'create' | 'update'
    existing_listing?: Record<string, unknown>
  }): Promise<{
    property: {
      title: string | null
      description: string | null
      type: 'sale' | 'rent' | null
      property_type: string | null
      price: number | null
      price_unit: string | null
      bedrooms: number | null
      bathrooms: number | null
      area: number | null
      area_unit: string | null
      location: string | null
      city: string | null
      neighborhood: string | null
      address: string | null
      amenities: string[]
      furnished: boolean | null
      features: string[]
      confidence: number
    }
    provider: string
    change_summary: Record<string, unknown> | null
  }> => fetchJson('/listings-ai/describe', { method: 'POST', body: JSON.stringify(payload) }),

  // Listing-disclosure territories (public.territories). Used by
  // platform message templates for optional territory scoping.
  listTerritories: (): Promise<Territory[]> => fetchJson('/territories'),

  // ============================================================
  // Subscription notification preferences
  // ============================================================

  getMyNotificationPreferences: (): Promise<{ preferences: NotificationPreferenceRow[]; event_kinds: string[] }> =>
    fetchJson('/billing/notifications/preferences'),
  updateMyNotificationPreferences: (updates: Array<{ event_kind: string; channel: string; enabled: boolean }>): Promise<{ preferences: NotificationPreferenceRow[] }> =>
    fetchJson('/billing/notifications/preferences', { method: 'PUT', body: JSON.stringify({ updates }) }),
  getMyNotificationHistory: (limit = 100): Promise<{ events: NotificationEventRow[] }> =>
    fetchJson(`/billing/notifications/history?limit=${limit}`),

  // ============================================================
  // Platform message templates (admin)
  //
  // Distinct from the tenant message_templates surface (the agent-owned
  // outbound copy). These are the platform's transactional messages TO
  // tenants — signup OTP, welcome, WhatsApp guide. Every WRITE endpoint
  // is step-up-gated on the backend (requireElevated); the client sends
  // the elevated token automatically via headers() when it's present in
  // sessionStorage.
  // ============================================================

  listPlatformTemplates: (opts: import('@/types/platformTemplates').PlatformTemplateListFilters = {}): Promise<{
    templates: import('@/types/platformTemplates').PlatformMessageTemplate[]
  }> => {
    const parts: string[] = []
    if (opts.code) parts.push(`code=${encodeURIComponent(opts.code)}`)
    if (opts.channel) parts.push(`channel=${encodeURIComponent(opts.channel)}`)
    if (opts.category) parts.push(`category=${encodeURIComponent(opts.category)}`)
    if (opts.language) parts.push(`language=${encodeURIComponent(opts.language)}`)
    if (opts.territoryId) parts.push(`territoryId=${encodeURIComponent(opts.territoryId)}`)
    if (opts.includeInactive) parts.push('includeInactive=1')
    return fetchJson(`/admin/message-templates${parts.length ? '?' + parts.join('&') : ''}`)
  },

  getPlatformTemplate: (id: string): Promise<{ template: import('@/types/platformTemplates').PlatformMessageTemplate }> =>
    fetchJson(`/admin/message-templates/${id}`),

  getPlatformTemplateVersions: (id: string): Promise<{
    current_version: number
    versions: import('@/types/platformTemplates').PlatformMessageTemplateVersion[]
  }> => fetchJson(`/admin/message-templates/${id}/versions`),

  createPlatformTemplate: (input: import('@/types/platformTemplates').CreatePlatformTemplateInput): Promise<{
    template: import('@/types/platformTemplates').PlatformMessageTemplate
  }> => fetchJson('/admin/message-templates', { method: 'POST', body: JSON.stringify(input) }),

  updatePlatformTemplate: (id: string, patch: import('@/types/platformTemplates').UpdatePlatformTemplateInput): Promise<{
    template: import('@/types/platformTemplates').PlatformMessageTemplate
  }> => fetchJson(`/admin/message-templates/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),

  revertPlatformTemplate: (id: string, version: number): Promise<{
    template: import('@/types/platformTemplates').PlatformMessageTemplate
  }> => fetchJson(`/admin/message-templates/${id}/revert`, { method: 'POST', body: JSON.stringify({ version }) }),

  deletePlatformTemplate: (id: string): Promise<{ deleted: true }> =>
    fetchJson(`/admin/message-templates/${id}`, { method: 'DELETE' }),

  previewPlatformTemplate: (id: string, variables: Record<string, unknown> = {}): Promise<import('@/types/platformTemplates').PlatformTemplatePreview> =>
    fetchJson(`/admin/message-templates/${id}/preview`, { method: 'POST', body: JSON.stringify({ variables }) }),

  testSendPlatformTemplate: (id: string, to: string, variables: Record<string, unknown> = {}): Promise<{
    sent: true
    provider: string
    provider_message_id: string | null
  }> => fetchJson(`/admin/message-templates/${id}/test-send`, { method: 'POST', body: JSON.stringify({ to, variables }) }),

  resolvePlatformTemplate: (opts: { code: string; language?: string; territoryId?: string }): Promise<{
    template: import('@/types/platformTemplates').PlatformMessageTemplate | null
  }> => {
    const parts = [`code=${encodeURIComponent(opts.code)}`]
    if (opts.language) parts.push(`language=${encodeURIComponent(opts.language)}`)
    if (opts.territoryId) parts.push(`territoryId=${encodeURIComponent(opts.territoryId)}`)
    return fetchJson(`/admin/message-templates/resolve?${parts.join('&')}`)
  },

  getAdminVendor: (id: string): Promise<import('@/pages/admin/fin/vendorTypes').VendorDetail> =>
    fetchJson(`/admin/fin/vendors/${encodeURIComponent(id)}`),
  listAdminVendorRates: (id: string): Promise<import('@/pages/admin/fin/vendorTypes').VendorRatesResponse> =>
    fetchJson(`/admin/fin/vendors/${encodeURIComponent(id)}/rates`),
  listAdminVendorStatements: (id: string): Promise<import('@/pages/admin/fin/vendorTypes').VendorStatementsResponse> =>
    fetchJson(`/admin/fin/vendors/${encodeURIComponent(id)}/statements`),
  getAdminVendorMargin: (
    id: string,
    month: string,
  ): Promise<import('@/pages/admin/fin/vendorTypes').VendorMarginResponse> =>
    fetchJson(`/admin/fin/vendors/${encodeURIComponent(id)}/margin?month=${encodeURIComponent(month)}`),

  finGet: (path: string): Promise<Record<string, unknown>> =>
    fetchJson(`/admin/fin${path}`),

  finPost: (
    path: string,
    body: Record<string, unknown> = {},
    opts?: { ifMatch?: number | string },
  ): Promise<Record<string, unknown>> =>
    fetchJson(`/admin/fin${path}`, {
      method: 'POST',
      body: JSON.stringify({ reason_code: 'ADMIN_OPS', ...body }),
      headers: {
        'If-Match': `"${opts?.ifMatch ?? 1}"`,
        'Idempotency-Key': (globalThis.crypto?.randomUUID?.() || `ops-${Date.now()}`),
      },
    }),

  /** PA-INV-004 — issue a debit note against an issued invoice. */
  createAdminDebitNote: (
    invoiceId: string,
    body: { amount_minor: number; reason_code?: string },
  ): Promise<Record<string, unknown>> =>
    fetchJson(`/admin/fin/invoices/${encodeURIComponent(invoiceId)}/debit-note`, {
      method: 'POST',
      body: JSON.stringify({ reason_code: 'ADMIN_OPS', ...body }),
      headers: {
        'If-Match': '"1"',
        'Idempotency-Key': (globalThis.crypto?.randomUUID?.() || `ops-${Date.now()}`),
      },
    }),

  finPatch: (path: string, body: Record<string, unknown> = {}): Promise<Record<string, unknown>> =>
    fetchJson(`/admin/fin${path}`, {
      method: 'PATCH',
      body: JSON.stringify({ reason_code: 'ADMIN_OPS', ...body }),
      headers: {
        'If-Match': '"1"',
        'Idempotency-Key': (globalThis.crypto?.randomUUID?.() || `ops-${Date.now()}`),
      },
    }),

  finDelete: (path: string): Promise<Record<string, unknown>> =>
    fetchJson(`/admin/fin${path}`, {
      method: 'DELETE',
      headers: {
        'If-Match': '"1"',
        'Idempotency-Key': (globalThis.crypto?.randomUUID?.() || `ops-${Date.now()}`),
      },
    }),

  // ── WF-20 two-person approval execute cluster (PA-APR-003/005/006) ──────
  getApprovalExecutePreview: (id: string): Promise<import('@/components/approval/approvalTypes').ExecutePreview> =>
    fetchJson(`/admin/fin/approvals/${encodeURIComponent(id)}/execute-preview`),

  getApprovalAuditTrail: (id: string): Promise<import('@/components/approval/approvalTypes').ApprovalAuditTrail> =>
    fetchJson(`/admin/fin/approvals/${encodeURIComponent(id)}/audit-trail`),

  executeApproval: (
    id: string,
    data: {
      workflowCode: string
      confirmationPhrase?: string | null
      version: number
      idempotencyKey?: string
    },
  ): Promise<import('@/components/approval/approvalTypes').ExecuteResult> =>
    fetchJson(`/admin/fin/approvals/${encodeURIComponent(id)}/execute`, {
      method: 'POST',
      body: JSON.stringify({
        workflow_code: data.workflowCode,
        confirmation_phrase: data.confirmationPhrase ?? undefined,
      }),
      headers: {
        'If-Match': `"${data.version}"`,
        'Idempotency-Key': data.idempotencyKey || (globalThis.crypto?.randomUUID?.() || `exec-${Date.now()}`),
      },
    }),

  getEligibleEscalationTargets: (
    id: string,
    opts: { q?: string; requestType?: string } = {},
  ): Promise<import('@/components/approval/approvalTypes').EligibleEscalationTargets> => {
    const parts: string[] = []
    if (opts.q) parts.push(`q=${encodeURIComponent(opts.q)}`)
    if (opts.requestType) parts.push(`request_type=${encodeURIComponent(opts.requestType)}`)
    const qs = parts.length ? `?${parts.join('&')}` : ''
    return fetchJson(`/admin/fin/approvals/${encodeURIComponent(id)}/eligible-escalation-targets${qs}`)
  },

  escalateApproval: (
    id: string,
    data: {
      targetApproverId: string
      reasonVocab: string
      notes: string
      notifyChannels: string[]
      version: number
    },
  ): Promise<Record<string, unknown>> =>
    fetchJson(`/admin/fin/approvals/${encodeURIComponent(id)}/escalate`, {
      method: 'POST',
      body: JSON.stringify({
        target_approver_id: data.targetApproverId,
        reason_vocab: data.reasonVocab,
        notes: data.notes,
        notify_channels: data.notifyChannels,
      }),
      headers: { 'If-Match': `"${data.version}"` },
    }),

  withdrawFinApproval: (
    id: string,
    data: { reason: string; version: number },
  ): Promise<Record<string, unknown>> =>
    fetchJson(`/admin/fin/approvals/${encodeURIComponent(id)}/withdraw`, {
      method: 'POST',
      body: JSON.stringify({ reason: data.reason }),
      headers: { 'If-Match': `"${data.version}"` },
    }),

  getTenantCreditsBalance: (): Promise<TenantCreditsBalance> =>
    fetchJson('/tenant/credits/balance'),
  getTenantSubscription: (): Promise<{ subscription: TenantSubscription | null; tenant_id: string }> =>
    fetchJson('/tenant/subscription'),
  getTenantPlans: (): Promise<{ plans: TenantPlan[] }> =>
    fetchJson('/tenant/plans'),
  getTenantInvoices: (): Promise<{ invoices: TenantInvoice[] }> =>
    fetchJson('/tenant/invoices'),
  getTenantCreditNotes: (): Promise<{ credit_notes: TenantCreditNote[] }> =>
    fetchJson('/tenant/credit-notes'),
  requestTenantTopUp: (amountUsd: number, idempotencyKey?: string): Promise<{
    status: string
    message?: string
    amount_usd: number
    idempotency_key: string
  }> => fetchJson('/tenant/credits/top-up', {
    method: 'POST',
    body: JSON.stringify({ amount_usd: amountUsd, idempotency_key: idempotencyKey }),
  }),
  getCheckoutConfig: (
    body: { kind: 'subscription'; package_version_id: string } | { kind: 'topup'; amount_usd: number },
  ): Promise<CheckoutConfig> => fetchJson('/billing/checkout-config', {
    method: 'POST',
    body: JSON.stringify(body),
  }),
  previewTenantPlanChange: (subscriptionId: string, newPackageVersionId: string): Promise<TenantPlanPreview> =>
    fetchJson('/tenant/subscription/preview-change', {
      method: 'POST',
      body: JSON.stringify({
        subscription_id: subscriptionId,
        new_package_version_id: newPackageVersionId,
      }),
    }),
  changeTenantPlan: (subscriptionId: string, newPackageVersionId: string, prorate = true): Promise<{
    previous: TenantSubscription
    subscription: TenantSubscription
    fraction?: number
    net?: number
  }> => fetchJson('/tenant/subscription/change-plan', {
    method: 'POST',
    body: JSON.stringify({
      subscription_id: subscriptionId,
      new_package_version_id: newPackageVersionId,
      prorate,
    }),
  }),

  // Contact relationships (BE-BLOCKER-36) — used by RelationshipsEditorPage.
  getContactRelationshipsMine: (contactId: string) =>
    fetchJson(`/contacts/${encodeURIComponent(contactId)}/relationships/mine`),
  getContactRelationshipsOther: (contactId: string) =>
    fetchJson(`/contacts/${encodeURIComponent(contactId)}/relationships/other`),
  createContactRelationship: (contactId: string, data: Record<string, unknown>) =>
    fetchJson(`/contacts/${encodeURIComponent(contactId)}/relationships`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  updateContactRelationship: (
    contactId: string,
    relationshipId: string,
    data: Record<string, unknown>,
  ) =>
    fetchJson(
      `/contacts/${encodeURIComponent(contactId)}/relationships/${encodeURIComponent(relationshipId)}`,
      { method: 'PATCH', body: JSON.stringify(data) },
    ),
  deleteContactRelationship: (contactId: string, relationshipId: string) =>
    fetchJson(
      `/contacts/${encodeURIComponent(contactId)}/relationships/${encodeURIComponent(relationshipId)}`,
      { method: 'DELETE' },
    ),
  resendRelationshipConsentLink: (contactId: string, relationshipId: string) =>
    fetchJson(
      `/contacts/${encodeURIComponent(contactId)}/relationships/${encodeURIComponent(relationshipId)}/resend-consent-link`,
      { method: 'POST', body: '{}' },
    ),
  /** Public, token-authed — pass token only; never authorize from other query params. */
  getPublicRelationshipConsent: (token: string) =>
    fetchJson(`/public/relationships/consent?token=${encodeURIComponent(token)}`),
  acceptPublicRelationshipConsent: (token: string) =>
    fetchJson('/public/relationships/consent/accept', {
      method: 'POST',
      body: JSON.stringify({ token }),
    }),
  rejectPublicRelationshipConsent: (token: string) =>
    fetchJson('/public/relationships/consent/reject', {
      method: 'POST',
      body: JSON.stringify({ token }),
    }),
}

export type PublishingAggregate =
  | 'all_succeeded'
  | 'mixed'
  | 'all_failed'
  | 'in_review_only'
  | 'partial'

export type PublishingDestinationStatus = 'succeeded' | 'in_review' | 'failed'

export type PublishingErrorClass =
  | 'AUTH_EXPIRED'
  | 'PORTAL_RULES_VIOLATION'
  | 'PORTAL_DOWN'
  | 'QUOTA_EXCEEDED'
  | 'INVALID_CONTENT'
  | 'UNKNOWN_ERROR'

export interface PublishingJobSummary {
  id: string
  listing_id: string | null
  listing_short_ref: string | null
  aggregate: PublishingAggregate
  submitted_at: string | null
  completed_at: string | null
  counts: {
    succeeded: number
    in_review: number
    failed: number
    total: number
  }
  credits: {
    total_charged: number
    total_reserved: number
  }
}

export interface PublishingDestinationPortal {
  code: string | null
  display_name: string | null
  logo_url: string | null
  country_code: string | null
  all_country_codes: string[]
}

export interface PublishingDestinationTimelineEntry {
  id?: string
  status?: string
  error_class?: string | null
  error_message?: string | null
  attempted_at?: string | null
  key?: string
  label?: string
  timestamp?: string
  state?: 'complete' | 'current' | 'pending' | 'skipped'
}

export interface PublishingDestination {
  id: string
  portal: PublishingDestinationPortal
  channel_type?: string
  status: PublishingDestinationStatus
  error_class: PublishingErrorClass | null
  portal_message: string | null
  credit_charged: number
  credit_reserved: number
  credit_held?: number
  credit_released?: number
  event_at: string | null
  live_url: string | null
  retry_available: boolean
  fix_deep_link: string | null
  moderation_queue_deep_link: string | null
  correlation_id: string | null
  timeline: PublishingDestinationTimelineEntry[]
}

export interface PublishingJobPayload {
  job: PublishingJobSummary
  destinations: PublishingDestination[]
}

export interface PublishingDestinationRetryResult {
  destination: PublishingDestination
  job: PublishingJobSummary | null
  destinations: PublishingDestination[]
}

export interface PublishingJobRetryAllResult extends PublishingJobPayload {
  retried_destination_ids: string[]
  skipped: Array<{ id: string; reason: string; error_class?: string | null }>
}

export interface FeatureQuota {
  enabled: boolean
  registered: boolean
  feature_code: string
  display_name?: string
  quota_used_this_cycle: number
  quota_display: number
  typical_monthly: number
  usage_ratio: number
  soft_warning: boolean
  used_credits?: number
  typical_credits?: number
}

export interface AgencyFeatureQuotaAgentBreakdown {
  agent_user_id: string | null
  agent_name: string
  used_credits: number
  source: 'agent' | 'agency'
}

export interface AgencyFeatureQuota extends FeatureQuota {
  category?: string
  category_group: string
  at_cap: boolean
  near_cap: boolean
  agent_breakdown: AgencyFeatureQuotaAgentBreakdown[]
}

export interface AgencyFeatureQuotaGroup {
  key: string
  label: string
  quotas: AgencyFeatureQuota[]
}

export interface AgencyFeatureQuotasResponse {
  agency_id: string
  billing_cycle_start: string | null
  billing_cycle_end: string | null
  groups: AgencyFeatureQuotaGroup[]
  quotas: AgencyFeatureQuota[]
}

export interface TenantCreditsBalance {
  tenant_id: string
  public_tenant_id: string
  scope: string
  credits_remaining: number
  credits_reserved: number
  credits_remaining_units: number
  credits_reserved_units: number
  currency: string
  hard_block: boolean
  quotas: FeatureQuota[]
}

export interface TenantSubscription {
  id: string
  status: string
  package_code?: string
  display_name?: string
  tier?: string
  billing_cadence?: string
  version_number?: number
  properties_committed: number
  properties_covered?: number
  monthly_price_minor?: number
  billing_cycle_start: string
  billing_cycle_end: string
  auto_renew: boolean
  paddle_subscription_id?: string | null
  paddle_customer_id?: string | null
}

export interface CheckoutConfig {
  price_id: string
  quantity: number
  custom_data: Record<string, unknown>
  customer_email: string | null
  environment: 'sandbox' | 'production'
}

export interface TenantPlan {
  package_id: string
  code: string
  display_name: string
  tier: string
  target_audience: string
  billing_cadence: string
  currency: string
  version_id: string
  version_number: number
  properties_covered: number
  monthly_price_minor: number
  state: string
}

export interface TenantInvoice {
  id: string
  status: string
  currency: string
  total_minor: number
  issued_at: string | null
  due_at: string | null
  invoice_number: string | null
}

export interface TenantCreditNote {
  id: string
  invoice_id: string
  amount_minor: number
  currency: string
  reason_code: string | null
  status: string
  note_number: string | null
  issued_at: string | null
  created_at: string
}

export interface SettingsIndexBadge {
  kind?: 'status' | 'count'
  tone?: 'default' | 'warning' | 'danger'
  label?: string
  label_key?: string
  value?: number
}

export interface SettingsIndexItem {
  id: string
  label: string
  label_key?: string
  route: string
  icon?: string
  badge?: SettingsIndexBadge | null
}

export interface SettingsIndexGroup {
  id: string
  label: string
  label_key?: string
  items: SettingsIndexItem[]
}

export interface SettingsIndexActivity {
  kind?: string
  label: string
  at: string
}

export interface SettingsIndexSecurityCapabilities {
  two_factor_enrolled?: boolean
  active_session_count?: number
}

export interface SettingsIndexBillingCapabilities {
  plan?: string | null
  past_due?: boolean
  display_name?: string | null
  renews_at?: string | null
}

export interface SettingsIndexIdentityCapabilities {
  oauth_only?: boolean
  signin_method?: string
}

export interface SettingsIndexTeamCapabilities {
  role?: string
  member_count?: number | null
  pending_invite_count?: number
}

export interface SettingsIndexCapabilities {
  account?: boolean
  danger?: boolean
  password?: boolean
  env?: string
  identity?: SettingsIndexIdentityCapabilities
  security?: SettingsIndexSecurityCapabilities
  billing?: SettingsIndexBillingCapabilities | false
  team?: SettingsIndexTeamCapabilities | false
}

export interface SettingsIndexResponse {
  capabilities?: SettingsIndexCapabilities
  groups: SettingsIndexGroup[]
  recent_activity?: SettingsIndexActivity[]
}

export interface AuthSessionRow {
  id: string
  is_current?: boolean
  device_kind?: 'desktop' | 'mobile' | 'tablet' | 'unknown' | string
  device_summary?: string
  ip?: string | null
  ip_country_iso?: string | null
  ip_country?: string | null
  ip_city?: string | null
  created_at?: string
  last_active_at?: string
}

export interface PushTokenRow {
  id: string
  platform: 'ios' | 'android' | 'web' | string
  device_id?: string | null
  created_at?: string
  last_used_at?: string | null
}

export interface TenantPlanPreview {
  subscription_id: string
  new_package_version_id: string
  new_package_display_name?: string
  fraction: number
  old_remaining: number
  new_remaining: number
  net: number
  currency: string
  new_monthly_price_minor: number
}
